import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import "./CinemaAnalytics.css";
import "../components/AnalyticsReportShell.css";

import reverseArrowImage from "../assets/reversearrow.jpg";
import cinemaAnalyticsLogo from "../assets/cinemaAnalytics.png";
import amcHeroImage from "../assets/AMC.png";
import bbHeroImage from "../assets/BB.png";
import brendenHeroImage from "../assets/Brenden.png";
import clarkHeroImage from "../assets/Clark.png";
import cinemarkHeroImage from "../assets/Cinemark.png";
import cineplexHeroImage from "../assets/Cineplex.png";
import harkinsHeroImage from "../assets/Harkins.png";
import imdbHeroImage from "../assets/imdb.png";
import marcusHeroImage from "../assets/Marcus.png";
import regalHeroImage from "../assets/Regal.png";
import rottenTomatoesHeroImage from "../assets/rottentomatoes.png";
import aiOptimizationsOldImage from "../assets/aioptimizationsOld.gif";
import sherlocVoiceoImage from "../assets/sherloc-voiceo.gif";
// Shared cinema mark: the single fallback for any exhibitor without approved
// artwork. Imported once and reused by every card rather than per-card copies.
import cinemaFallbackMark from "../assets/PurpleFilmIcon.jpg";
import PoweredByLogo from "../components/PoweredByLogo";
import {
  AdvertiserIdentityTrigger,
  GovernedAdvertiserSelectorModal,
} from "../components/GovernedAdvertiserSelector";
import useLegacyPlatformPageAwareness from "../hooks/useLegacyPlatformPageAwareness";
import useGovernedAdvertiserCatalog from "../hooks/useGovernedAdvertiserCatalog";

import {
  GOVERNED_INTELLIGENCE_STATES,
  resolveGovernedIntelligenceState,
  governedInsightUnavailableCopy,
  governedEvidenceProvenance,
} from "../intelligence/governedIntelligenceState";
import { formatFlightRange, parseFlightDateSafe } from "../utils/flightDates";
import {
  buildAdvertiserFallbackPlan,
  selectApprovedPlanCampaign,
} from "../utils/optimusPacing";
import { isInternalReportingExcludedCampaign } from "../utils/canonicalCampaignIdentity";
import {
  CINEMA_REAPPROVAL_CTA_MODES,
  buildCinemaReapprovalCta,
  normalizeCinemaReapprovalDirective,
} from "../utils/cinemaReapprovalDirective";
import {
  isCinemaCsvFile,
  parseCinemaCloudWorksCsvPreview,
  getTopCircuitChartData,
  getTopMarketChartData,
  getReadinessChartData,
  getRecommendedNextStep,
  getCinemaFootprintHeadline,
  buildCinemaTargetingPrep,
  normalizeTargetingRadiusMiles,
} from "../utils/cinemaLocationCsv";
import {
  CinemaInsightChartCard,
  CinemaHorizontalBarChart,
  CinemaReadinessChart,
  CinemaExhibitorDonutChart,
  CinemaExhibitorLegend,
  CinemaGeographicTileGrid,
  buildCinemaExhibitorDistribution,
} from "../components/CinemaFootprintCharts";
import {
  buildCinemaFootprintAnalyticsModel,
  filterAndSortCinemaVenues,
  formatFootprintPercentage,
  US_STATE_TILE_GRID,
  US_STATE_TILE_GRID_COLS,
  US_STATE_TILE_GRID_ROWS,
} from "../utils/cinemaFootprintAnalytics";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "";
const safeBase = (base) => String(base || "").replace(/\/+$/, "");
const CINEMA_PLATFORM_KEY = "cinema";
const CINEMA_PLATFORM_LABEL = "Cinema";
const FLIGHT_DATE_PRESET = "flight";
const CINEMA_AI_PROVIDER_KEY = "clCinemaAiProvider";
const LOCATION_LIMIT_OPTIONS = [25, 50, 100, 200];
const CINEMA_REAPPROVAL_STATUS = "NEEDS_REAPPROVAL";
const CINEMA_RETRYABLE_SCOPE_IMPORT_CODES = new Set([
  "TITLE_SCOPED_CAMPAIGN_REQUIRED",
  "CAMPAIGN_NOT_TITLE_SCOPED",
  "CAMPAIGN_ADVERTISER_MISMATCH",
  "CINEMA_SCOPE_MISMATCH",
  "FOOTPRINT_VERSION_MISMATCH",
]);
const CINEMA_APPROVED_LIFECYCLE_STATUSES = new Set([
  "APPROVED",
  "COMPLETED",
  "EXECUTED",
  "COUNTERSIGNED",
]);
const EMPTY_LOCATION_DATA = { count: 0, items: [] };
const EMPTY_LOCATION_CLUSTERS = {
  totals: {
    totalRows: 0,
    activeRows: 0,
    inactiveRows: 0,
    filteredRows: 0,
    filteredActiveRows: 0,
    filteredInactiveRows: 0,
  },
  stateClusters: [],
  chainClusters: [],
  sourceClusters: [],
  availableStates: [],
  availableChains: [],
  availableSources: [],
};
// Presentation labels for the governed intelligence contract keys. Display
// only — the state machine in governedIntelligenceState.js stays authoritative
// and no key is added or removed here.
const CINEMA_OVERVIEW_STATE_LABELS = Object.freeze({
  [GOVERNED_INTELLIGENCE_STATES.UNINITIALIZED]: "Awaiting Scope",
  [GOVERNED_INTELLIGENCE_STATES.PROCESSING]: "Processing",
  [GOVERNED_INTELLIGENCE_STATES.ERROR]: "Review Required",
  [GOVERNED_INTELLIGENCE_STATES.NO_DATA]: "Awaiting Cinema Data",
  [GOVERNED_INTELLIGENCE_STATES.INSUFFICIENT_DATA]: "Partial Data",
  [GOVERNED_INTELLIGENCE_STATES.READY_FOR_ANALYSIS]: "Ready for Analysis",
});

const CINEMA_CHAIN_HERO_BUTTONS = [
  { key: "AMC", label: "AMC", chainKey: "AMC", matchKeys: ["AMC"], image: amcHeroImage },
  {
    key: "B_AND_B_THEATRES",
    label: "B&B",
    chainKey: "B_AND_B_THEATRES",
    matchKeys: [
      "BB",
      "B&B",
      "B & B",
      "B_AND_B",
      "B AND B",
      "BB_THEATRES",
      "BB THEATERS",
      "BB THEATRES",
      "B&B_THEATRES",
      "B&B THEATRES",
      "B&B_THEATERS",
      "B&B THEATERS",
      "B_AND_B_THEATRES",
      "B AND B THEATRES",
      "B_AND_B_THEATERS",
      "B AND B THEATERS",
      "US_B_AND_B_THEATRES",
      "US B AND B THEATRES",
      "US_B_AND_B_THEATERS",
      "US B AND B THEATERS",
    ],
    image: bbHeroImage,
  },
  {
    key: "BRENDEN_THEATRES",
    label: "BRENDEN",
    chainKey: "BRENDEN_THEATRES",
    matchKeys: [
      "BRENDEN",
      "BRENDEN_THEATRES",
      "BRENDEN THEATRES",
      "BRENDEN_THEATERS",
      "BRENDEN THEATERS",
      "US_BRENDEN",
      "US BRENDEN",
      "US_BRENDEN_THEATRES",
      "US BRENDEN THEATRES",
      "US_BRENDEN_THEATERS",
      "US BRENDEN THEATERS",
    ],
    image: brendenHeroImage,
  },
  {
    key: "CLARK_FILM_BUYING",
    label: "CLARK",
    chainKey: "CLARK_FILM_BUYING",
    matchKeys: [
      "CLARK",
      "CLARK_FILM_BUYING",
      "CLARK FILM BUYING",
      "US_CLARK",
      "US CLARK",
      "US_CLARK_FILM_BUYING",
      "US CLARK FILM BUYING",
    ],
    image: clarkHeroImage,
  },
  {
    key: "US_CINEMARK",
    label: "CINEMARK",
    chainKey: "US_CINEMARK",
    matchKeys: ["US_CINEMARK", "US CINEMARK", "CINEMARK"],
    image: cinemarkHeroImage,
  },
  {
    key: "CA_CINEPLEX",
    label: "CINEPLEX",
    chainKey: "CA_CINEPLEX",
    matchKeys: [
      "CA_CINEPLEX",
      "CA CINEPLEX",
      "CINEPLEX",
      "CINEPLEX_THEATRES",
      "CINEPLEX THEATRES",
      "CA_CINEPLEX_THEATRES",
      "CA CINEPLEX THEATRES",
      "CINEPLEX_THEATERS",
      "CINEPLEX THEATERS",
      "CA_CINEPLEX_THEATERS",
      "CA CINEPLEX THEATERS",
    ],
    image: cineplexHeroImage,
  },
  {
    key: "HARKINS_THEATRES",
    label: "HARKINS",
    chainKey: "HARKINS_THEATRES",
    matchKeys: [
      "HARKINS",
      "HARKINS_THEATRES",
      "HARKINS THEATRES",
      "HARKINS_THEATERS",
      "HARKINS THEATERS",
      "US_HARKINS",
      "US HARKINS",
      "US_HARKINS_THEATRES",
      "US HARKINS THEATRES",
      "US_HARKINS_THEATERS",
      "US HARKINS THEATERS",
      "HARKINS_CINEMAS",
      "HARKINS CINEMAS",
    ],
    image: harkinsHeroImage,
  },
  {
    key: "US_MARCUS",
    label: "MARCUS",
    chainKey: "US_MARCUS",
    matchKeys: [
      "US_MARCUS",
      "US MARCUS",
      "MARCUS",
      "MARCUS_THEATRES",
      "MARCUS THEATRES",
      "US_MARCUS_THEATRES",
      "US MARCUS THEATRES",
      "MARCUS_THEATERS",
      "MARCUS THEATERS",
      "US_MARCUS_THEATERS",
      "US MARCUS THEATERS",
    ],
    image: marcusHeroImage,
  },
  { key: "REGAL", label: "REGAL", chainKey: "REGAL", matchKeys: ["REGAL"], image: regalHeroImage },
];

function normalizeCinemaChainLookupKey(value) {
  return String(value || "")
    .trim()
    .normalize("NFKD")
    .replace(/[’'`]/g, "")
    .replace(/&/g, " AND ")
    .replace(/[^\w\s]/g, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();
}

function getCinemaChainHeroButton(chainKey) {
  if (!chainKey) return null;
  const normalizedKey = normalizeCinemaChainLookupKey(chainKey);
  return (
    CINEMA_CHAIN_HERO_BUTTONS.find((button) => {
      const matchKeys = [button.chainKey, ...(button.matchKeys || [])];
      return matchKeys.some((candidate) => normalizeCinemaChainLookupKey(candidate) === normalizedKey);
    }) || null
  );
}

function resolveCinemaChainHeroButton(...values) {
  for (const value of values) {
    const exactMatch = getCinemaChainHeroButton(value);
    if (exactMatch) return exactMatch;
  }

  const normalizedCandidates = values
    .map((value) => normalizeCinemaChainLookupKey(value))
    .filter(Boolean);

  if (!normalizedCandidates.length) return null;

  return (
    CINEMA_CHAIN_HERO_BUTTONS.find((button) => {
      const matchKeys = [button.chainKey, ...(button.matchKeys || [])].map((candidate) =>
        normalizeCinemaChainLookupKey(candidate)
      );
      return normalizedCandidates.some((candidate) =>
        matchKeys.some(
          (matchKey) =>
            matchKey === candidate ||
            matchKey.includes(candidate) ||
            candidate.includes(matchKey)
        )
      );
    }) || null
  );
}

/**
 * The ONE place an exhibitor is turned into artwork.
 *
 * Both readings of the exhibitor aggregation — the donut legend and the
 * horizontal share bars — call this, so a chain can never wear one logo in one
 * chart and another beside it. Precedence: the independent grouping is never
 * given a chain's artwork, an approved chain logo wins, and anything else falls
 * back to the shared cinema mark.
 *
 * `isFallback` travels with the src because the mark needs different rendering
 * from a real logo: it is neon line-work baked onto an opaque black JPEG, so it
 * has to be blended to keep its own background from showing as a black tile.
 * Callers must not re-derive this by comparing src values.
 */
function resolveCinemaExhibitorArtwork(exhibitor = {}) {
  const approved = exhibitor.isIndependent
    ? null
    : resolveCinemaChainHeroButton(exhibitor.key, exhibitor.label)?.image || null;
  return {
    src: approved || cinemaFallbackMark,
    isFallback: !approved,
  };
}

function getLiveCinemaLabelLines(label) {
  const normalized = String(label || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return ["Independent"];

  const prefixed = normalized.match(/^([A-Z]{2})\s+(.+)$/);
  if (prefixed) return [prefixed[1], prefixed[2]];

  const words = normalized.split(" ");
  if (words.length <= 1 || normalized.length <= 15) return [normalized];

  const midpoint = Math.ceil(words.length / 2);
  return [words.slice(0, midpoint).join(" "), words.slice(midpoint).join(" ")];
}

function resolveCanonicalCinemaChainIdentity(...values) {
  const heroButton = resolveCinemaChainHeroButton(...values);
  if (heroButton) {
    return {
      key: heroButton.chainKey,
      label: heroButton.label,
      image: heroButton.image || null,
    };
  }

  const fallbackValue = values.find((value) => String(value || "").trim()) || "INDEPENDENT";
  const normalizedKey = normalizeCinemaChainLookupKey(fallbackValue) || "INDEPENDENT";
  const label = titleCase(String(fallbackValue || normalizedKey).replace(/_/g, " ")) || "Independent";
  return { key: normalizedKey, label, image: null };
}

function buildLiveCinemaCards(chainClusters, totalRows) {
  const deduped = new Map();

  (chainClusters || []).forEach((cluster) => {
    const count = Number(cluster?.count || 0);
    if (count <= 0) return;

    const canonical = resolveCanonicalCinemaChainIdentity(
      cluster?.key,
      cluster?.label,
      cluster?.rawValue
    );
    const existing = deduped.get(canonical.key);

    if (existing) {
      existing.count += count;
      existing.activeCount += Number(cluster?.activeCount || 0);
      return;
    }

    deduped.set(canonical.key, {
      key: canonical.key,
      label: canonical.label,
      labelLines: getLiveCinemaLabelLines(canonical.label),
      image: canonical.image,
      count,
      share: 0,
      activeCount: Number(cluster?.activeCount || 0),
    });
  });

  return Array.from(deduped.values())
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 6)
    .map((item) => ({
      ...item,
      share: totalRows > 0 ? item.count / totalRows : 0,
    }));
}

function resolveCinemaAnalysisDatePreset() {
  return FLIGHT_DATE_PRESET;
}

function safeNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeCinemaTitleScope(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\(\d{4}\)\s*$/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatCinemaSourceLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "workbook_sitelist") return "Imported Workbook Footprint";
  if (normalized === "manual_assignment") return "Manual Assignment";
  if (normalized === "selected_locations") return "Selected Locations";
  if (normalized === "selected_segment") return "Selected Segment";
  if (normalized === "title_scope_auto") return "Title Scope Auto";
  if (!normalized) return "No Cinema Data Uploaded";
  return titleCase(normalized.replace(/[_-]+/g, " "));
}

function formatInteger(value) {
  const n = safeNum(value);
  if (n == null) return "--";
  return Math.round(n).toLocaleString("en-US");
}

function formatCurrency(value) {
  const n = safeNum(value);
  if (n == null) return "--";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatCurrencyPrecise(value) {
  const n = safeNum(value);
  if (n == null) return "--";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatPercent(value, digits = 1) {
  const n = safeNum(value);
  if (n == null) return "--";
  return `${n.toFixed(digits)}%`;
}

function formatPercentFromDecimal(value, digits = 1) {
  const n = safeNum(value);
  if (n == null) return "--";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatShareLabel(numerator, denominator, digits = 0) {
  const num = safeNum(numerator);
  const den = safeNum(denominator);
  if (num == null || den == null || den <= 0) return "--";
  return formatPercentFromDecimal(num / den, digits);
}

function formatMultiple(value) {
  const n = safeNum(value);
  if (n == null) return "--";
  return `${n.toFixed(2)}x`;
}

function formatDateOnly(value) {
  if (!value) return "--";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "--";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function toDateOnlyString(value) {
  const parsed = parseFlightDateSafe(value);
  if (!parsed) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function buildRangeObjectFromDates(startValue, endValue) {
  const startDate = toDateOnlyString(startValue);
  const endDate = toDateOnlyString(endValue);
  if (!startDate || !endDate || endDate < startDate) return null;
  return { startDate, endDate };
}

function coerceCampaignArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.campaigns)) return payload.campaigns;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.rows)) return payload.rows;
  return [];
}

function normalizePositiveInteger(value) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function getCinemaCampaignId(campaign) {
  return normalizePositiveInteger(
    campaign?.id ?? campaign?._id ?? campaign?.campaignId ?? campaign?.campaign_id
  );
}

function getCinemaCampaignStatus(campaign) {
  return String(campaign?.status || "").trim().toUpperCase();
}

function getCinemaCampaignName(campaign) {
  return String(
    campaign?.campaign_name ||
      campaign?.campaignName ||
      campaign?.name ||
      (getCinemaCampaignId(campaign) ? `Campaign ${getCinemaCampaignId(campaign)}` : "")
  ).trim();
}

function getCinemaCampaignTitle(campaign) {
  // Campaign identity and film-title identity are intentionally separate.
  // A campaign name is never promoted to a canonical title.
  return String(
    campaign?.film_title ||
      campaign?.filmTitle ||
      campaign?.canonical_title ||
      campaign?.canonicalTitle ||
      campaign?.title ||
      ""
  ).trim();
}

function getCinemaCampaignUpdatedAt(campaign) {
  const parsed = new Date(
    campaign?.updated_at || campaign?.updatedAt || campaign?.created_at || campaign?.createdAt || 0
  ).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function selectCinemaContextCampaign({ campaigns, approvedCampaign, preferredCampaignId = null }) {
  const eligible = (Array.isArray(campaigns) ? campaigns : [])
    .filter(
      (campaign) =>
        getCinemaCampaignId(campaign) && !isInternalReportingExcludedCampaign(campaign)
    )
    .sort(
      (left, right) =>
        getCinemaCampaignUpdatedAt(right) - getCinemaCampaignUpdatedAt(left) ||
        getCinemaCampaignId(right) - getCinemaCampaignId(left)
    );

  const preferredId = normalizePositiveInteger(preferredCampaignId);
  if (preferredId) {
    const exactPreferred = eligible.find(
      (campaign) => getCinemaCampaignId(campaign) === preferredId
    );
    if (exactPreferred) return exactPreferred;
  }

  const persistedContextCampaign = eligible.find((campaign) =>
    normalizePositiveInteger(
      campaign?.titleContextId ??
        campaign?.title_context_id ??
        campaign?.cinemaTitleContextId ??
        campaign?.cinema_title_context_id
    )
  );
  if (persistedContextCampaign) return persistedContextCampaign;

  // A targeting revision in governed review remains the relevant analytical
  // context even though it is explicitly not deployable.
  const reapprovalCampaign = eligible.find(
    (campaign) => getCinemaCampaignStatus(campaign) === CINEMA_REAPPROVAL_STATUS
  );
  if (reapprovalCampaign) return reapprovalCampaign;

  const approvedId = getCinemaCampaignId(approvedCampaign);
  if (approvedId) {
    const exactApproved = eligible.find(
      (campaign) => getCinemaCampaignId(campaign) === approvedId
    );
    if (exactApproved) return exactApproved;
  }

  return (
    eligible.find((campaign) =>
      CINEMA_APPROVED_LIFECYCLE_STATUSES.has(getCinemaCampaignStatus(campaign))
    ) || eligible[0] || null
  );
}

function readCinemaCanonicalScope(payload, fallback = {}) {
  const source =
    payload?.canonicalScope ||
    payload?.scope?.canonicalScope ||
    payload?.titleContext?.canonicalScope ||
    payload?.data?.canonicalScope ||
    {};
  const titleContext = payload?.titleContext || payload?.data?.titleContext || {};

  return {
    campaignId: normalizePositiveInteger(
      source.campaignId ??
        source.campaign_id ??
        titleContext.campaignId ??
        titleContext.campaign_id ??
        fallback.campaignId
    ),
    titleContextId: normalizePositiveInteger(
      source.titleContextId ??
        source.title_context_id ??
        titleContext.id ??
        payload?.titleContextId ??
        fallback.titleContextId
    ),
    footprintVersion: normalizePositiveInteger(
      source.footprintVersion ??
        source.footprint_version ??
        titleContext.footprintVersion ??
        titleContext.footprint_version ??
        payload?.footprintVersion ??
        fallback.footprintVersion
    ),
    title: String(
      source.title ??
        source.canonicalTitle ??
        titleContext.title ??
        titleContext.canonicalTitle ??
        fallback.title ??
        ""
    ).trim(),
  };
}

function appendCinemaCanonicalScopeParams(params, scope) {
  const campaignId = normalizePositiveInteger(scope?.campaignId);
  const titleContextId = normalizePositiveInteger(scope?.titleContextId);
  const footprintVersion = normalizePositiveInteger(scope?.footprintVersion);
  if (campaignId) params.set("campaignId", String(campaignId));
  if (titleContextId) params.set("titleContextId", String(titleContextId));
  if (footprintVersion) params.set("footprintVersion", String(footprintVersion));
  return params;
}

function buildCinemaCanonicalScopeBody(scope) {
  const params = new URLSearchParams();
  appendCinemaCanonicalScopeParams(params, scope);
  return Object.fromEntries(params.entries());
}

function getCampaignFlightRange(campaign) {
  if (!campaign) return null;
  return buildRangeObjectFromDates(
    campaign.flight_start_date ||
      campaign.flightStartDate ||
      campaign.flight_start ||
      campaign.flightStart ||
      null,
    campaign.flight_end_date ||
      campaign.flightEndDate ||
      campaign.flight_end ||
      campaign.flightEnd ||
      null
  );
}

function hasCinemaReapprovalSignal(flightLineage, campaignStatus) {
  const lineageStatuses = [
    flightLineage?.currentStatus,
    flightLineage?.current_status,
  ].map((value) => String(value || "").trim().toUpperCase());
  return (
    flightLineage?.requiresReapproval === true ||
    lineageStatuses.includes(CINEMA_REAPPROVAL_STATUS) ||
    campaignStatus === CINEMA_REAPPROVAL_STATUS
  );
}

export function resolveCurrentCinemaFlightLineage({
  footprintState,
  sourceState,
}) {
  return footprintState?.flightLineage || sourceState?.flightLineage || null;
}

export function resolveLastApprovedFlightRange({
  flightLineage,
  campaign,
  campaignStatus,
}) {
  const lineageFlight = flightLineage?.lastApprovedFlight || {};
  const governedLineageRange = buildRangeObjectFromDates(
    lineageFlight.startDate || lineageFlight.start || null,
    lineageFlight.endDate || lineageFlight.end || null
  );
  if (governedLineageRange) return governedLineageRange;

  if (hasCinemaReapprovalSignal(flightLineage, campaignStatus)) return null;

  // Mutable campaign dates are current approved evidence only while the
  // campaign itself remains in an approved lifecycle state. Once a revision
  // requires reapproval, the backend lineage snapshot is the sole authority
  // for any historical "last approved" window. Proposed/current dates must
  // never be promoted to that historical label.
  if (CINEMA_APPROVED_LIFECYCLE_STATUSES.has(campaignStatus)) {
    return getCampaignFlightRange(campaign);
  }
  return null;
}

export function resolveCinemaFlightRange({
  lastApprovedFlightRange,
  selectedCampaign,
  canonicalCampaignId,
  requiresReapproval,
  approvedFlightRange,
  advertiserFlightRange,
}) {
  if (lastApprovedFlightRange) return lastApprovedFlightRange;
  if (
    requiresReapproval ||
    normalizePositiveInteger(canonicalCampaignId) ||
    selectedCampaign
  ) {
    return null;
  }
  return approvedFlightRange || advertiserFlightRange || null;
}

export function doesCinemaEvidenceMatchCanonicalScope(evidence, canonicalScope) {
  const evidenceScope = readCinemaCanonicalScope(evidence, evidence || {});
  const campaignId = normalizePositiveInteger(canonicalScope?.campaignId);
  const titleContextId = normalizePositiveInteger(canonicalScope?.titleContextId);
  const footprintVersion = normalizePositiveInteger(canonicalScope?.footprintVersion);
  return Boolean(
    campaignId &&
      titleContextId &&
      footprintVersion &&
      evidenceScope.campaignId === campaignId &&
      evidenceScope.titleContextId === titleContextId &&
      evidenceScope.footprintVersion === footprintVersion
  );
}

export function mergeCinemaCanonicalScope(previous, payload) {
  const canonical = readCinemaCanonicalScope(payload, previous);
  const previousCampaignId = normalizePositiveInteger(previous?.campaignId);
  const canonicalCampaignId = normalizePositiveInteger(canonical.campaignId);
  if (
    previousCampaignId &&
    canonicalCampaignId &&
    canonicalCampaignId !== previousCampaignId
  ) {
    return previous;
  }

  const previousTitleContextId = normalizePositiveInteger(previous?.titleContextId);
  const canonicalTitleContextId = normalizePositiveInteger(canonical.titleContextId);
  if (
    previousTitleContextId &&
    canonicalTitleContextId &&
    canonicalTitleContextId !== previousTitleContextId
  ) {
    return previous;
  }
  const previousFootprintVersion = normalizePositiveInteger(previous?.footprintVersion);
  const canonicalFootprintVersion = normalizePositiveInteger(canonical.footprintVersion);
  if (
    previousCampaignId &&
    canonicalCampaignId === previousCampaignId &&
    previousTitleContextId &&
    canonicalTitleContextId === previousTitleContextId &&
    previousFootprintVersion &&
    canonicalFootprintVersion &&
    canonicalFootprintVersion < previousFootprintVersion
  ) {
    return previous;
  }

  const next = {
    campaignId: String(canonicalCampaignId || previousCampaignId || ""),
    titleContextId: String(canonicalTitleContextId || ""),
    footprintVersion: String(canonicalFootprintVersion || ""),
    title: canonical.title || previous?.title || "",
  };
  return Object.keys(next).every(
    (key) => String(next[key]) === String(previous?.[key] || "")
  )
    ? previous
    : next;
}

function scoreDescription(key, entry) {
  const label = String(entry?.label || "");
  if (key === "marketPosition") {
    if (label === "strong") return "Theatrical footprint is materially established and scaling with real presence.";
    if (label === "established") return "Footprint is meaningful, but market-share language should stay guarded.";
    return "Footprint signal is still emerging relative to broader market scale.";
  }
  if (key === "demandMomentum") {
    if (label === "accelerating") return "Recent ticket-sales velocity is strengthening across the observed windows.";
    if (label === "stable") return "Demand pacing looks broadly steady rather than sharply moving.";
    return "Demand pacing is softening or the history depth is still thin.";
  }
  if (key === "marketingEfficiency") {
    if (label === "efficient") return "Media input is translating into healthier theatrical return signals.";
    if (label === "mixed") return "Efficiency is present but still directional rather than fully attributed.";
    return "Efficiency signals are fragile and need cautious interpretation.";
  }
  if (key === "projectionConfidence") {
    if (label === "medium_high") return "Forecast confidence is stronger because the signal mix is deeper.";
    if (label === "medium") return "Forecast is usable, but still directional rather than precise.";
    return "Forecast should be treated as a low-confidence scenario read.";
  }
  return "Diagnostic score derived from the live framework contract.";
}

function severityLabel(value) {
  const s = String(value || "").toLowerCase();
  if (s === "opportunity") return "Opportunity";
  if (s === "watchout") return "Watchout";
  if (s === "gap") return "Gap";
  return titleCase(value || "Note");
}

function normalizeAiProviderForUi(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "auto";
  if (
    normalized === "openai" ||
    normalized === "gpt" ||
    normalized === "chatgpt" ||
    normalized.includes("openai") ||
    normalized.includes("chatgpt") ||
    normalized.includes("gpt-") ||
    normalized.startsWith("gpt")
  ) {
    return "chatgpt";
  }
  if (
    normalized === "anthropic" ||
    normalized === "claude" ||
    normalized.includes("anthropic") ||
    normalized.includes("claude")
  ) {
    return "claude";
  }
  return normalized === "auto" ? "auto" : "auto";
}

function getAiProviderPresentation(provider) {
  const normalized = normalizeAiProviderForUi(provider);
  if (normalized === "chatgpt") {
    return {
      normalized,
      label: "Sherloc",
      logoSrc: aiOptimizationsOldImage,
      logoAlt: "Sherloc hero",
    };
  }
  if (normalized === "claude") {
    return {
      normalized,
      label: "Eden",
      logoSrc: sherlocVoiceoImage,
      logoAlt: "Eden hero",
    };
  }
  return {
    normalized: "auto",
    label: "Auto",
    logoSrc: null,
    logoAlt: "",
  };
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    "";
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchJsonRequest(path, options = {}) {
  const response = await fetch(`${safeBase(API_BASE_URL)}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Preserve the structured error contract (code + payload) so callers can
    // distinguish campaign-scope, persistence, and geocoding failures instead
    // of collapsing everything into one generic import error.
    const error = new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
    error.code = payload?.code || null;
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function createAdvertiserScopeAbortError() {
  const error = new Error("Stale advertiser-scoped request");
  error.name = "AbortError";
  return error;
}

function isAbortError(error) {
  return error?.name === "AbortError" || error?.code === "ABORT_ERR";
}

function KpiCard({ label, value, hint = "", accent = "blue" }) {
  return (
    <div className={`cinema-analysis-kpi-card cinema-analysis-kpi-card-${accent}`}>
      <div className="cinema-analysis-kpi-label">{label}</div>
      <div className="cinema-analysis-kpi-value">{value}</div>
      {hint ? <div className="cinema-analysis-kpi-hint">{hint}</div> : null}
    </div>
  );
}

function MetricCard({ label, value, hint = "" }) {
  return (
    <div className="cinema-analysis-metric-card">
      <div className="cinema-analysis-metric-label">{label}</div>
      <div className="cinema-analysis-metric-value">{value}</div>
      {hint ? <div className="cinema-analysis-metric-hint">{hint}</div> : null}
    </div>
  );
}

function AnalysisSection({ title, kicker = "", aside = null, children }) {
  return (
    <section className="cinema-analysis-section">
      <div className="cinema-analysis-section-head">
        <div>
          {kicker ? <div className="cinema-analysis-section-kicker">{kicker}</div> : null}
          <h2 className="cinema-analysis-section-title">{title}</h2>
        </div>
        {aside}
      </div>
      <div className="cinema-analysis-section-body">{children}</div>
    </section>
  );
}

function Badge({ tone = "neutral", children }) {
  return <span className={`cinema-analysis-badge cinema-analysis-badge-${tone}`}>{children}</span>;
}

function getFirstValidUrl(candidateValues = []) {
  for (const candidate of candidateValues) {
    const value = String(candidate || "").trim();
    if (!value) continue;
    try {
      const parsed = new URL(value);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return value;
      }
    } catch (_err) {
      // Ignore invalid URLs and continue to the next likely field.
    }
  }
  return "";
}

function getCampaignStrategyPayload(campaign) {
  if (!campaign || typeof campaign !== "object") return {};
  const payload = campaign.strategy_payload || campaign.strategyPayload || {};
  return payload && typeof payload === "object" ? payload : {};
}

function getFirstDefinedValue(candidateValues = []) {
  for (const candidate of candidateValues) {
    if (candidate !== undefined && candidate !== null && String(candidate).trim() !== "") {
      return candidate;
    }
  }
  return null;
}

function getFirstNumericValue(candidateValues = []) {
  const value = getFirstDefinedValue(candidateValues);
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const match = String(value).match(/-?\d+(?:\.\d+)?/);
  const parsed = match ? Number(match[0]) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

function formatImdbRatingValue(value) {
  return value == null ? "Source Connected" : `${value.toFixed(1)}/10`;
}

function formatRottenTomatoesValue(criticScore, audienceScore) {
  if (criticScore != null && audienceScore != null) {
    return `${Math.round(criticScore)}% Critic • ${Math.round(audienceScore)}% Audience`;
  }
  if (criticScore != null) return `${Math.round(criticScore)}% Critic`;
  if (audienceScore != null) return `${Math.round(audienceScore)}% Audience`;
  return "Source Connected";
}

function extractCampaignFilmReferenceInputs(campaign, fallbackTitle = "") {
  if (!campaign || typeof campaign !== "object") {
    return {
      title: String(fallbackTitle || "this title").trim() || "this title",
      officialWebsiteUrl: "",
      imdbUrl: "",
      rottenTomatoesUrl: "",
      imdbRating: null,
      rottenTomatoesCriticScore: null,
      rottenTomatoesAudienceScore: null,
      hasAnySource: false,
      hasAnySignal: false,
    };
  }

  const strategyPayload = getCampaignStrategyPayload(campaign);
  const title =
    String(
      campaign?.film_title ||
        campaign?.filmTitle ||
        campaign?.campaign_name ||
        campaign?.campaignName ||
        campaign?.name ||
        campaign?.title ||
        fallbackTitle ||
        "this title"
    ).trim() || "this title";
  const officialWebsiteUrl = getFirstValidUrl([
    campaign?.destinationUrl,
    campaign?.destination_url,
    strategyPayload?.destinationUrl,
    strategyPayload?.destination_url,
    campaign?.landingPageUrl,
    campaign?.landing_page_url,
  ]);
  const imdbUrl = getFirstValidUrl([
    campaign?.imdbUrl,
    campaign?.imdb_url,
    strategyPayload?.imdbUrl,
    strategyPayload?.imdb_url,
  ]);
  const rottenTomatoesUrl = getFirstValidUrl([
    campaign?.rottenTomatoesUrl,
    campaign?.rotten_tomatoes_url,
    strategyPayload?.rottenTomatoesUrl,
    strategyPayload?.rotten_tomatoes_url,
  ]);
  const imdbRating = getFirstNumericValue([
    campaign?.imdbRating,
    campaign?.imdb_rating,
    strategyPayload?.imdbRating,
    strategyPayload?.imdb_rating,
    strategyPayload?.filmIntelligence?.imdbRating,
    strategyPayload?.film_intelligence?.imdb_rating,
  ]);
  const rottenTomatoesCriticScore = getFirstNumericValue([
    campaign?.rottenTomatoesCriticScore,
    campaign?.rotten_tomatoes_critic_score,
    strategyPayload?.rottenTomatoesCriticScore,
    strategyPayload?.rotten_tomatoes_critic_score,
    strategyPayload?.filmIntelligence?.rottenTomatoesCriticScore,
    strategyPayload?.film_intelligence?.rotten_tomatoes_critic_score,
  ]);
  const rottenTomatoesAudienceScore = getFirstNumericValue([
    campaign?.rottenTomatoesAudienceScore,
    campaign?.rotten_tomatoes_audience_score,
    strategyPayload?.rottenTomatoesAudienceScore,
    strategyPayload?.rotten_tomatoes_audience_score,
    strategyPayload?.filmIntelligence?.rottenTomatoesAudienceScore,
    strategyPayload?.film_intelligence?.rotten_tomatoes_audience_score,
  ]);

  const hasAnySource = Boolean(officialWebsiteUrl || imdbUrl || rottenTomatoesUrl);
  const hasAnySignal = Boolean(
    hasAnySource ||
      imdbRating != null ||
      rottenTomatoesCriticScore != null ||
      rottenTomatoesAudienceScore != null
  );

  return {
    title,
    officialWebsiteUrl,
    imdbUrl,
    rottenTomatoesUrl,
    imdbRating,
    rottenTomatoesCriticScore,
    rottenTomatoesAudienceScore,
    hasAnySource,
    hasAnySignal,
  };
}

function getFilmReferenceFallbackImage(cardKey) {
  if (cardKey === "imdb") return imdbHeroImage;
  if (cardKey === "rottenTomatoes") return rottenTomatoesHeroImage;
  return cinemaAnalyticsLogo;
}

function buildLocalFilmIntelligenceContext(filmReferenceInput) {
  if (!filmReferenceInput?.hasAnySignal) return null;

  const {
    title,
    officialWebsiteUrl,
    imdbUrl,
    rottenTomatoesUrl,
    imdbRating,
    rottenTomatoesCriticScore,
    rottenTomatoesAudienceScore,
  } = filmReferenceInput;

  const hasImdb = Boolean(imdbUrl || imdbRating != null);
  const hasRottenTomatoes = Boolean(
    rottenTomatoesUrl || rottenTomatoesCriticScore != null || rottenTomatoesAudienceScore != null
  );
  const hasOfficialWebsite = Boolean(officialWebsiteUrl);

  if (!hasOfficialWebsite && !hasImdb && !hasRottenTomatoes) return null;

  const cards = [];

  if (hasOfficialWebsite) {
    cards.push({
      key: "officialWebsite",
      title: "Official Website",
      image: cinemaAnalyticsLogo,
      url: officialWebsiteUrl,
      badgeTone: "neutral",
      badgeLabel: "Source Connected",
      value: "Official Site Connected",
      copy: `The official title website is connected for ${title}. Sherloc can use that owned positioning source as a safe narrative anchor while theater footprint and cinema delivery data are still being ingested.`,
    });
  }

  if (hasImdb) {
    const insight =
      imdbRating != null
        ? imdbRating >= 7.5
          ? `IMDb's current rating suggests ${title} is landing as a strong audience-facing title signal. That can support broader theatrical expectation-setting and more confident top-funnel messaging.`
          : imdbRating >= 6
          ? `IMDb's current rating points to a workable audience-facing profile for ${title}. Keep theatrical messaging focused on clear payoff, genre fit, and conversion-friendly hooks.`
          : `IMDb's current rating suggests ${title} may need sharper expectation-setting. Use theatrical creative to reduce ambiguity and reinforce the strongest audience-facing reasons to watch.`
        : `IMDb is connected as a film-identity source for ${title}. Use it to anchor audience-facing framing and title metadata, while this report treats score-specific sentiment as unavailable until structured ingestion is present.`;

    cards.push({
      key: "imdb",
      title: "IMDb",
      image: imdbHeroImage,
      url: imdbUrl,
      badgeTone: imdbRating != null ? "good" : "neutral",
      badgeLabel: imdbRating != null ? "Score Live" : "Source Connected",
      value: formatImdbRatingValue(imdbRating),
      copy: insight,
    });
  }

  if (hasRottenTomatoes) {
    let insight = `Rotten Tomatoes is connected as a critic/audience reception source for ${title}. Use it as a reputation check when calibrating theatrical messaging, but keep exact score claims out of circulation unless structured values are available.`;
    if (rottenTomatoesCriticScore != null && rottenTomatoesAudienceScore != null) {
      const divergence = Math.abs(rottenTomatoesCriticScore - rottenTomatoesAudienceScore);
      insight =
        divergence >= 15
          ? `Critic and audience reception are materially diverged for ${title}. That usually favors messaging that emphasizes audience payoff, intrigue, and practical watchability over prestige validation alone.`
          : `Critic and audience sentiment look broadly aligned for ${title}. That supports more unified theatrical messaging and cleaner expectation-setting across awareness and conversion creative.`;
    } else if (rottenTomatoesCriticScore != null) {
      insight =
        rottenTomatoesCriticScore >= 75
          ? `Rotten Tomatoes critic sentiment is supportive for ${title}. Lean into validation, review-driven creative cues, and premium framing where appropriate.`
          : `Rotten Tomatoes critic sentiment is currently mixed for ${title}. Theatrical creative should focus more on differentiated hooks and audience motivation than broad acclaim language.`;
    } else if (rottenTomatoesAudienceScore != null) {
      insight =
        rottenTomatoesAudienceScore >= 75
          ? `Rotten Tomatoes audience sentiment is supportive for ${title}. That can help justify stronger conversion-oriented theatrical messaging in interest-dense markets.`
          : `Rotten Tomatoes audience sentiment is currently mixed for ${title}. Messaging should reinforce payoff and reduce uncertainty before leaning too heavily into social-proof cues.`;
    }

    cards.push({
      key: "rottenTomatoes",
      title: "Rotten Tomatoes",
      image: rottenTomatoesHeroImage,
      url: rottenTomatoesUrl,
      badgeTone:
        rottenTomatoesCriticScore != null || rottenTomatoesAudienceScore != null
          ? "good"
          : "neutral",
      badgeLabel:
        rottenTomatoesCriticScore != null || rottenTomatoesAudienceScore != null
          ? "Scores Live"
          : "Source Connected",
      value: formatRottenTomatoesValue(
        rottenTomatoesCriticScore,
        rottenTomatoesAudienceScore
      ),
      copy: insight,
    });
  }

  let combinedTakeaway = `${title} has connected film-reference sources that can sharpen theatrical positioning, expectation-setting, and audience framing without relying on unsupported score claims or pretending cinema analytics are already live.`;
  if (imdbRating != null && rottenTomatoesCriticScore != null && rottenTomatoesAudienceScore != null) {
    const divergence = Math.abs(rottenTomatoesCriticScore - rottenTomatoesAudienceScore);
    combinedTakeaway =
      divergence >= 15
        ? `For ${title}, the current reception picture suggests critic and audience sentiment are not moving in lockstep. Use cinema forecasting and creative planning to favor audience payoff, intent signals, and practical conversion cues over prestige language alone.`
        : `For ${title}, the available IMDb and Rotten Tomatoes signals point to a relatively coherent reception profile. That supports more confident theatrical expectation-setting and cleaner market-by-market forecasting assumptions.`;
  } else if (imdbRating != null || rottenTomatoesCriticScore != null || rottenTomatoesAudienceScore != null) {
    combinedTakeaway = `Structured reception signals are partially available for ${title}. Use them as directional support for cinema demand expectations, but keep forecasting confidence calibrated until more complete audience and critic context is present.`;
  } else if (hasOfficialWebsite && (hasImdb || hasRottenTomatoes)) {
    combinedTakeaway = `For ${title}, Sherloc can already combine owned title positioning from the official site with source-connected reference endpoints like IMDb and Rotten Tomatoes. That keeps the page useful and auditable while cinema-specific footprint data is still pending.`;
  } else if (hasOfficialWebsite) {
    combinedTakeaway = `For ${title}, the official website already gives Sherloc a reliable owned-positioning source. That helps populate early title intelligence honestly while cinema footprint and theater analytics are still waiting on ingestion.`;
  } else if (hasImdb && hasRottenTomatoes) {
    combinedTakeaway = `IMDb and Rotten Tomatoes are both connected for ${title}, giving Sherloc a durable film-reference layer even though this report is not yet ingesting structured scores. Use those sources to shape audience-facing messaging, social-proof strategy, and theatrical expectation-setting carefully.`;
  }

  const sourceLabels = [];
  if (hasOfficialWebsite) sourceLabels.push("Official Website");
  if (hasImdb) sourceLabels.push("IMDb");
  if (hasRottenTomatoes) sourceLabels.push("Rotten Tomatoes");

  const fallbackAnalysis = {
    available: sourceLabels.length > 0,
    mode: "public_signal_analysis",
    label: "Public Signal Analysis",
    title,
    summary: combinedTakeaway,
    sourceSummary: sourceLabels.length
      ? `This limited cinema data view is grounded in ${sourceLabels.join(
          ", "
        )} and stays explicitly separate from true cinema footprint, exhibitor, geo, or box-office intelligence.`
      : "",
    sectionCards: [
      {
        key: "positioning",
        title: "Title Positioning",
        copy: hasOfficialWebsite
          ? `An official title website is already connected for ${title}, so Sherloc can ground early positioning in owned/public title framing instead of leaving the page blank.`
          : `No official title website is linked for ${title}, so positioning is currently inferred only from the connected public film-reference sources already in the system.`,
      },
      {
        key: "audience_fit",
        title: "Audience Fit Hypothesis",
        copy:
          imdbRating != null || rottenTomatoesCriticScore != null || rottenTomatoesAudienceScore != null
            ? `Public reception signals are available for ${title}, so Sherloc can form a directional audience-fit hypothesis while staying conservative about deeper cinema demand claims.`
            : `Audience-fit is currently directional for ${title} and based only on lighter public-title signals, not on ticket sales or exhibitor-level performance data.`,
      },
      {
        key: "release_context",
        title: "Release Context",
        copy:
          hasImdb || hasRottenTomatoes
            ? `IMDb and/or Rotten Tomatoes are connected for ${title}, which gives the page a durable title-reference layer while cinema-specific ingestion is still pending.`
            : `Release context for ${title} is still light, so Sherloc is holding this read at the title/public-signal layer only.`,
      },
      {
        key: "public_footprint",
        title: "Public Footprint Readiness",
        copy: hasOfficialWebsite && (hasImdb || hasRottenTomatoes)
          ? `${title} has both an owned title surface and at least one connected public reference surface, which is enough to support a useful limited cinema data analysis without pretending true cinema intelligence is live.`
          : `${title} has only a partial public-title footprint connected so far, so the limited cinema data analysis stays intentionally narrow and source-auditable.`,
      },
    ],
    strengths: [
      hasOfficialWebsite
        ? `An official title website is linked for ${title}.`
        : null,
      hasImdb && hasRottenTomatoes
        ? "Multiple public film-reference surfaces are connected."
        : hasImdb || hasRottenTomatoes
        ? "At least one public film-reference surface is connected."
        : null,
    ].filter(Boolean),
    gaps: [
      hasOfficialWebsite ? null : "No official title website is linked yet.",
      hasImdb ? null : "IMDb is not currently connected.",
      hasRottenTomatoes ? null : "Rotten Tomatoes is not currently connected.",
    ].filter(Boolean),
    limitations: [
      "This limited cinema data mode does not claim exhibitor intelligence, theater density, geographic coverage, box office, or ticket-sales performance.",
      "It only reflects the lighter title/public signals that are genuinely available to Sherloc right now.",
    ],
    sourcesUsed: sourceLabels.map((label) => ({
      key: label.toLowerCase().replace(/[^a-z0-9]+/g, "_"),
      title: label,
      status: "source_connected",
    })),
  };

  return {
    hero: {
      title,
      synopsis: hasOfficialWebsite
        ? `Sherloc has a connected official title URL for ${title}, plus any available film-reference sources. That keeps the page populated with owned-positioning and source-connected title context while cinema/location ingestion is still catching up.`
        : `Sherloc has connected film-reference sources for ${title}. These cards stay intentionally separate from theater footprint and box-office analytics until cinema data is actually available.`,
      image: hasOfficialWebsite
        ? cinemaAnalyticsLogo
        : hasImdb
        ? imdbHeroImage
        : rottenTomatoesHeroImage,
      sourceLabel: hasOfficialWebsite
        ? "Official Website Connected"
        : hasImdb && hasRottenTomatoes
        ? "IMDb + Rotten Tomatoes Connected"
        : hasImdb
        ? "IMDb Connected"
        : "Rotten Tomatoes Connected",
      releaseLabel: "",
      genreLabel: "",
    },
    cards,
    combinedTakeaway,
    fallbackAnalysis,
  };
}

function buildFilmIntelligenceContext(filmReferenceInput, enrichment = null) {
  const localContext = filmReferenceInput?.hasAnySignal
    ? buildLocalFilmIntelligenceContext(filmReferenceInput)
    : null;
  if (!filmReferenceInput?.hasAnySignal) return null;

  if (
    enrichment &&
    typeof enrichment === "object" &&
    (Array.isArray(enrichment.cards) && enrichment.cards.length)
  ) {
    const cards = enrichment.cards.map((card) => ({
      ...card,
      image: card?.image || getFilmReferenceFallbackImage(card?.key),
      value: String(card?.value || card?.badgeLabel || "Source Connected").trim(),
      copy: String(card?.copy || "").trim(),
    }));

    return {
      hero: {
        title:
          String(enrichment?.hero?.title || filmReferenceInput.title || "Film Reference Intelligence").trim() ||
          filmReferenceInput.title,
        synopsis:
          String(
            enrichment?.hero?.synopsis ||
              `Sherloc is using connected title URLs for ${filmReferenceInput.title} while cinema-specific data remains unavailable.`
          ).trim(),
        image:
          String(enrichment?.hero?.image || cards[0]?.image || cinemaAnalyticsLogo).trim() ||
          cinemaAnalyticsLogo,
        sourceLabel: String(enrichment?.hero?.sourceLabel || "Connected Film Sources").trim(),
        releaseLabel: String(enrichment?.hero?.releaseLabel || "").trim(),
        genreLabel: String(enrichment?.hero?.genreLabel || "").trim(),
      },
      insights: Array.isArray(enrichment?.insights)
        ? enrichment.insights.map((card) => ({
            ...card,
            title: String(card?.title || "").trim(),
            value: String(card?.value || "").trim(),
            copy: String(card?.copy || "").trim(),
          }))
        : [],
      cards,
      fallbackAnalysis:
        (enrichment?.fallbackAnalysis &&
          typeof enrichment.fallbackAnalysis === "object" &&
          enrichment.fallbackAnalysis.available)
          ? enrichment.fallbackAnalysis
          : localContext?.fallbackAnalysis || null,
      combinedTakeaway:
        String(enrichment?.combinedTakeaway || "").trim() ||
        localContext?.combinedTakeaway ||
        "",
    };
  }

  return localContext;
}

function normalizeFilmReferenceValue(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return normalized.toLowerCase() === "source connected" ? "" : normalized;
}

function buildFilmReferenceInsightCards(filmIntelligence) {
  if (!filmIntelligence || typeof filmIntelligence !== "object") return [];
  if (Array.isArray(filmIntelligence.insights) && filmIntelligence.insights.length) {
    return filmIntelligence.insights
      .map((card) => ({
        key: card?.key,
        title: String(card?.title || "").trim(),
        value: String(card?.value || "").trim(),
        copy: String(card?.copy || "").trim(),
      }))
      .filter((card) => card.key && (card.copy || card.value));
  }

  const cards = Array.isArray(filmIntelligence.cards) ? filmIntelligence.cards : [];
  const cardsByKey = cards.reduce((accumulator, card) => {
    if (card?.key) accumulator[card.key] = card;
    return accumulator;
  }, {});

  const imdbCard = cardsByKey.imdb || null;
  const rottenTomatoesCard = cardsByKey.rottenTomatoes || null;
  const fallbackSectionCards = Array.isArray(filmIntelligence?.fallbackAnalysis?.sectionCards)
    ? filmIntelligence.fallbackAnalysis.sectionCards
    : [];
  const audienceFitCard =
    fallbackSectionCards.find((card) => card?.key === "audience_fit") || null;

  const overviewCopy = String(
    filmIntelligence?.hero?.synopsis ||
      filmIntelligence?.combinedTakeaway ||
      imdbCard?.copy ||
      rottenTomatoesCard?.copy ||
      ""
  ).trim();
  const audienceScoreParts = [
    imdbCard ? `IMDb ${normalizeFilmReferenceValue(imdbCard.value) || imdbCard.badgeLabel}` : "",
    rottenTomatoesCard
      ? `Rotten Tomatoes ${
          normalizeFilmReferenceValue(rottenTomatoesCard.value) || rottenTomatoesCard.badgeLabel
        }`
      : "",
  ].filter(Boolean);
  const audienceScoreCopy = audienceScoreParts.length
    ? `${audienceScoreParts.join(" | ")}.`
    : "Structured audience or critic scores are not currently available from the connected film-reference sources.";
  const reviewAnalysisCopy = String(
    rottenTomatoesCard?.copy ||
      filmIntelligence?.combinedTakeaway ||
      overviewCopy ||
      ""
  ).trim();
  const audienceSentimentCopy = String(
    audienceFitCard?.copy ||
      imdbCard?.copy ||
      rottenTomatoesCard?.copy ||
      filmIntelligence?.combinedTakeaway ||
      ""
  ).trim();

  return [
    {
      key: "overview",
      title: "Overview",
      value: String(filmIntelligence?.hero?.sourceLabel || "Connected Film Sources").trim(),
      copy: overviewCopy,
    },
    {
      key: "audience_score",
      title: "Audience Score",
      value: audienceScoreParts.join(" | ") || "Source Connected",
      copy: audienceScoreCopy,
    },
    {
      key: "review_analysis",
      title: "Review Analysis",
      value: String(rottenTomatoesCard?.badgeLabel || "Reference Intelligence").trim(),
      copy: reviewAnalysisCopy,
    },
    {
      key: "audience_sentiment",
      title: "Audience Sentiment",
      value: String(
        imdbCard?.badgeLabel || rottenTomatoesCard?.badgeLabel || "Directional"
      ).trim(),
      copy: audienceSentimentCopy,
    },
  ].filter((card) => card.copy || card.value);
}

function createCinemaReportCandidate(label, value) {
  return { label, value };
}

function getAdvertiserCinemaReportCandidateEntries(advertiser) {
  if (!advertiser || typeof advertiser !== "object") return [];

  return [
    createCinemaReportCandidate("Cinema Report Link", advertiser.cinemaReportLink),
    createCinemaReportCandidate("Cinema Looker Link", advertiser.cinemaLookerLink),
    createCinemaReportCandidate("Cinema Analytics Link", advertiser.cinemaAnalyticsLink),
    createCinemaReportCandidate("Cinema Link", advertiser.cinema_link),
    createCinemaReportCandidate("Looker Studio Cinema Link", advertiser.lookerStudioCinemaLink),
    createCinemaReportCandidate("Cinema Report Link", advertiser.cinema_report_link),
    createCinemaReportCandidate("Cinema Looker Link", advertiser.cinema_looker_link),
    createCinemaReportCandidate("Cinema Analytics Link", advertiser.cinema_analytics_link),
    createCinemaReportCandidate("Looker Studio Cinema Link", advertiser.looker_studio_cinema_link),
    createCinemaReportCandidate("Cinema Report URL", advertiser.cinemaReportUrl),
    createCinemaReportCandidate("Cinema Looker URL", advertiser.cinemaLookerUrl),
    createCinemaReportCandidate("Cinema Analytics URL", advertiser.cinemaAnalyticsUrl),
    createCinemaReportCandidate("Cinema Looker Studio URL", advertiser.cinemaLookerStudioUrl),
    createCinemaReportCandidate("Cinema Report", advertiser.cinemaReport),
    createCinemaReportCandidate("Cinema Looker Report", advertiser.cinemaLookerReport),
    createCinemaReportCandidate("Cinema Analytics Report", advertiser.cinemaAnalyticsReport),
    createCinemaReportCandidate("Cinema Studio Link", advertiser.cinemaStudioLink),
    createCinemaReportCandidate("Cinema Studio Report Link", advertiser.cinemaStudioReportLink),
    createCinemaReportCandidate("Looker Studio Link", advertiser.lookerStudioLink),
    createCinemaReportCandidate("Looker Link", advertiser.looker_link),
    createCinemaReportCandidate("Looker URL", advertiser.looker_url),
    createCinemaReportCandidate("Cinema Link", advertiser.cinemaLink),
    createCinemaReportCandidate("Cinema", advertiser.cinema),
    createCinemaReportCandidate("Cinema CloudWorks Link", advertiser.cinemaCloudWorksLink),
    createCinemaReportCandidate("Cinema CloudWorks Link", advertiser.cinema_cloudworks_link),
    createCinemaReportCandidate("Report Links Cinema", advertiser.reportLinks?.cinema),
    createCinemaReportCandidate("Report Links Cinema", advertiser.reportLinks?.cinema_link),
    createCinemaReportCandidate("Report Links Cinema Analytics", advertiser.reportLinks?.cinemaAnalytics),
    createCinemaReportCandidate("Report Links Cinema Analytics", advertiser.reportLinks?.cinema_analytics),
    createCinemaReportCandidate("Report Links Cinema Looker", advertiser.reportLinks?.cinemaLooker),
    createCinemaReportCandidate("Report Links Cinema Looker", advertiser.reportLinks?.cinema_looker),
    createCinemaReportCandidate("Looker Links Cinema", advertiser.lookerLinks?.cinema),
    createCinemaReportCandidate("Looker Links Cinema", advertiser.lookerLinks?.cinema_link),
    createCinemaReportCandidate("Looker Links Cinema Analytics", advertiser.lookerLinks?.cinemaAnalytics),
    createCinemaReportCandidate("Looker Links Cinema Analytics", advertiser.lookerLinks?.cinema_analytics),
    createCinemaReportCandidate("Looker Links Cinema Looker", advertiser.lookerLinks?.cinemaLooker),
    createCinemaReportCandidate("Looker Links Cinema Looker", advertiser.lookerLinks?.cinema_looker),
    createCinemaReportCandidate("Report Links Cinema", advertiser.report_links?.cinema),
    createCinemaReportCandidate("Report Links Cinema", advertiser.report_links?.cinema_link),
    createCinemaReportCandidate("Report Links Cinema Analytics", advertiser.report_links?.cinemaAnalytics),
    createCinemaReportCandidate("Report Links Cinema Analytics", advertiser.report_links?.cinema_analytics),
    createCinemaReportCandidate("Report Links Cinema Looker", advertiser.report_links?.cinemaLooker),
    createCinemaReportCandidate("Report Links Cinema Looker", advertiser.report_links?.cinema_looker),
    createCinemaReportCandidate("Looker Links Cinema", advertiser.looker_links?.cinema),
    createCinemaReportCandidate("Looker Links Cinema", advertiser.looker_links?.cinema_link),
    createCinemaReportCandidate("Looker Links Cinema Analytics", advertiser.looker_links?.cinemaAnalytics),
    createCinemaReportCandidate("Looker Links Cinema Analytics", advertiser.looker_links?.cinema_analytics),
    createCinemaReportCandidate("Looker Links Cinema Looker", advertiser.looker_links?.cinemaLooker),
    createCinemaReportCandidate("Looker Links Cinema Looker", advertiser.looker_links?.cinema_looker),
  ];
}

function getAdvertiserCinemaReportCandidates(advertiser) {
  return getAdvertiserCinemaReportCandidateEntries(advertiser).map((entry) => entry.value);
}


const CINEMA_ANALYSIS_REPORT_STORAGE_VERSION = "v2";

function buildCinemaAnalysisReportStorageKey({
  audienceMode,
  advertiserId,
  aiProvider,
  datePreset,
  flightRange,
  campaignId,
  titleContextId,
  footprintVersion,
  title,
}) {
  const normalizedAdvertiserId = Number(advertiserId);
  if (!Number.isFinite(normalizedAdvertiserId) || normalizedAdvertiserId <= 0) return "";

  const normalizedPreset = String(datePreset || "").trim().toLowerCase();
  if (!normalizedPreset) return "";

  const normalizedProvider = normalizeAiProviderForUi(aiProvider || "auto");
  const windowKey =
    normalizedPreset === FLIGHT_DATE_PRESET && flightRange?.startDate && flightRange?.endDate
      ? `${FLIGHT_DATE_PRESET}:${flightRange.startDate}:${flightRange.endDate}`
      : normalizedPreset;

  return [
    "cinema-market-analysis-report",
    CINEMA_ANALYSIS_REPORT_STORAGE_VERSION,
    String(audienceMode || "client").trim().toLowerCase(),
    String(normalizedAdvertiserId),
    normalizedProvider,
    windowKey,
    `campaign_${normalizePositiveInteger(campaignId) || "none"}`,
    `context_${normalizePositiveInteger(titleContextId) || "none"}`,
    `footprint_${normalizePositiveInteger(footprintVersion) || "none"}`,
    normalizeCinemaTitleScope(title || "") || "advertiser_scope",
  ].join(":");
}

function readStoredCinemaAnalysisReport(storageKey) {
  if (!storageKey) return null;

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const payload = parsed?.payload ?? parsed;
    return payload && typeof payload === "object" ? payload : null;
  } catch (_err) {
    return null;
  }
}

function writeStoredCinemaAnalysisReport(storageKey, payload) {
  if (!storageKey || !payload || typeof payload !== "object") return;

  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        savedAt: new Date().toISOString(),
        payload,
      })
    );
  } catch (_err) {
    // Ignore storage quota / privacy mode failures.
  }
}

const CinemaAnalytics = () => {
  const navigate = useNavigate();

  // The cinema engine and analysis window are not client-selectable controls; the
  // page always runs Sherloc cinema intelligence over the selected campaign's
  // last approved flight lineage (which can remain visible during reapproval).
  const selectedAiProvider = "auto";
  const dateSelection = { advertiserKey: "", preset: null };
  const [analysis, setAnalysis] = useState(null);
  const [loadedAnalysisKey, setLoadedAnalysisKey] = useState("");
  const [analysisIsStale, setAnalysisIsStale] = useState(false);
  const [analysisLoadedFromCache, setAnalysisLoadedFromCache] = useState(false);
  const [locationsData, setLocationsData] = useState(EMPTY_LOCATION_DATA);
  const [locationClusters, setLocationClusters] = useState(EMPTY_LOCATION_CLUSTERS);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingLocationClusters, setLoadingLocationClusters] = useState(false);
  const [error, setError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [planCampaigns, setPlanCampaigns] = useState([]);
  const [planCampaignsAdvertiserKey, setPlanCampaignsAdvertiserKey] = useState("");
  const [loadingPlanCampaigns, setLoadingPlanCampaigns] = useState(false);
  const [filmReferenceEnrichment, setFilmReferenceEnrichment] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [locationSearchInput, setLocationSearchInput] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationStateFilter, setLocationStateFilter] = useState("");
  const [locationChainFilter, setLocationChainFilter] = useState("");
  const [locationSourceFilter, setLocationSourceFilter] = useState("");
  const [locationIncludeInactive, setLocationIncludeInactive] = useState(false);
  const [locationLimit, setLocationLimit] = useState(50);
  const [locationOffset, setLocationOffset] = useState(0);
  const [cinemaCsvImport, setCinemaCsvImport] = useState(null);
  const [cinemaCsvUploadBusy, setCinemaCsvUploadBusy] = useState(false);
  // Saved advertiser cinema dataset state from the backend (persisted imports),
  // plus the persistence result of the most recent manual upload.
  const [cinemaSourceState, setCinemaSourceState] = useState(null);
  const [cinemaImportSaveState, setCinemaImportSaveState] = useState(null);
  // Human-in-the-loop default geo-fence radius (miles) applied to prepared
  // coordinate-ready targets. Session-only; does not persist or export.
  const [cinemaDefaultRadiusMiles, setCinemaDefaultRadiusMiles] = useState(5);
  // Canonical campaign/title-context/version scope for footprint persistence.
  // The campaign is selected first and the backend rehydrates immutable IDs;
  // footprints are never read by mutable title, filename, or advertiser alone.
  const [cinemaFootprintScope, setCinemaFootprintScope] = useState({
    campaignId: "",
    titleContextId: "",
    footprintVersion: "",
    title: "",
  });
  // Parsed-but-unsaved upload retained so a campaign-scope correction can
  // retry ONLY the save — never forcing a re-upload/re-parse of the file.
  const [pendingCinemaImport, setPendingCinemaImport] = useState(null);
  // Canonical persisted footprint lifecycle state (survives reload): rows,
  // geocode statuses, governed geo-fence policy, resolved execution radii.
  const [cinemaFootprintState, setCinemaFootprintState] = useState(null);
  // The campaign whose canonical reads have answered. A newly selected
  // campaign has no known title context until they do, so the page cannot yet
  // tell a first-ever footprint (which needs the title control) from a
  // governed one (which owns its title). Rendering the control before the
  // answer flashed an empty text box between two resolved campaigns.
  const [cinemaCanonicalReadCampaignId, setCinemaCanonicalReadCampaignId] = useState("");
  // The governed film identity the backend resolved for this campaign — its own
  // bound title context, an adoptable one, or the tenant identity. It exists so
  // the first import does not have to ask an operator to retype identity the
  // platform already holds. Presentation and save-title only; canonical reads
  // still travel by campaign/context/version IDs.
  //
  // Stored WITH the campaign it was resolved for, the same way footprint
  // evidence is matched to its canonical scope: an identity that outlived its
  // campaign would name the wrong film, so it is filtered rather than cleared
  // by every transition that could strand it.
  const [cinemaTitleIdentity, setCinemaTitleIdentity] = useState(null);
  const [cinemaCanonicalRefreshNonce, setCinemaCanonicalRefreshNonce] = useState(0);
  const [cinemaPolicyDraft, setCinemaPolicyDraft] = useState(null);
  const [cinemaPolicySaveState, setCinemaPolicySaveState] = useState(null);
  // Reapproval is a two-step act: the operator opens the confirmation, reads
  // exactly what they are approving, and only then approves. Nothing here ever
  // fires on load or on a single incidental click.
  const [cinemaReapprovalConfirming, setCinemaReapprovalConfirming] = useState(false);
  const [cinemaReapprovalState, setCinemaReapprovalState] = useState(null);
  // Complete governed cinema list controls. View-layer only: search, filters
  // and sort feed filterAndSortCinemaVenues over the canonical model and can
  // never mutate the campaign's totals or percentages.
  const [cinemaRosterSearch, setCinemaRosterSearch] = useState("");
  const [cinemaRosterExhibitorFilter, setCinemaRosterExhibitorFilter] = useState("");
  const [cinemaRosterStateFilter, setCinemaRosterStateFilter] = useState("");
  const [cinemaRosterSort, setCinemaRosterSort] = useState({
    key: "canonical",
    direction: "asc",
  });
  const locationContextKeyRef = useRef("");
  const latestLocationsRequestKeyRef = useRef("");
  const latestLocationClustersRequestKeyRef = useRef("");
  const cinemaCsvInputRef = useRef(null);
  const advertiserScopeEpochRef = useRef(0);
  const scopedRequestControllersRef = useRef(new Set());

  const abortAdvertiserScopedRequests = useCallback(() => {
    scopedRequestControllersRef.current.forEach((controller) => controller.abort());
    scopedRequestControllersRef.current.clear();
  }, []);

  const clearAdvertiserScopedPresentationState = useCallback(() => {
    advertiserScopeEpochRef.current += 1;
    abortAdvertiserScopedRequests();
    locationContextKeyRef.current = "";
    latestLocationsRequestKeyRef.current = "";
    latestLocationClustersRequestKeyRef.current = "";
    setAnalysis(null);
    setLoadedAnalysisKey("");
    setAnalysisIsStale(false);
    setAnalysisLoadedFromCache(false);
    setLocationsData(EMPTY_LOCATION_DATA);
    setLocationClusters(EMPTY_LOCATION_CLUSTERS);
    setLoadingAnalysis(false);
    setLoadingLocations(false);
    setLoadingLocationClusters(false);
    setError("");
    setLocationError("");
    setPlanCampaigns([]);
    setPlanCampaignsAdvertiserKey("");
    setLoadingPlanCampaigns(false);
    setFilmReferenceEnrichment(null);
    setSelectedLocationId(null);
    setCinemaCsvImport(null);
    setCinemaCsvUploadBusy(false);
    setCinemaSourceState(null);
    setCinemaImportSaveState(null);
    setCinemaFootprintScope({
      campaignId: "",
      titleContextId: "",
      footprintVersion: "",
      title: "",
    });
    setPendingCinemaImport(null);
    setCinemaFootprintState(null);
    setCinemaTitleIdentity(null);
    setCinemaCanonicalRefreshNonce(0);
    setCinemaPolicyDraft(null);
    setCinemaPolicySaveState(null);
    setCinemaRosterSearch("");
    setCinemaRosterExhibitorFilter("");
    setCinemaRosterStateFilter("");
    setCinemaRosterSort({ key: "canonical", direction: "asc" });
  }, [abortAdvertiserScopedRequests]);

  const {
    selectedAdvertiserId,
    visibleAdvertisers,
    currentAdvertiser,
    loading: loadingAdvertisers,
    selectorDisabled,
    selectorEmpty,
    modalOpen: advertiserSelectorOpen,
    openSelector: openAdvertiserSelector,
    closeSelector: closeAdvertiserSelector,
    handleAdvertiserSelect,
  } = useGovernedAdvertiserCatalog({
    apiBase: API_BASE_URL,
    role: "client",
    onAdvertiserChange: clearAdvertiserScopedPresentationState,
  });

  const fetchJson = useCallback(async (path, options = {}) => {
    const requestScopeEpoch = advertiserScopeEpochRef.current;
    const controller = new AbortController();
    const upstreamSignal = options.signal;
    const abortFromUpstream = () => controller.abort();

    if (upstreamSignal?.aborted) controller.abort();
    else upstreamSignal?.addEventListener?.("abort", abortFromUpstream, { once: true });
    scopedRequestControllersRef.current.add(controller);

    try {
      const payload = await fetchJsonRequest(path, {
        ...options,
        signal: controller.signal,
      });
      if (
        controller.signal.aborted ||
        requestScopeEpoch !== advertiserScopeEpochRef.current
      ) {
        throw createAdvertiserScopeAbortError();
      }
      return payload;
    } finally {
      scopedRequestControllersRef.current.delete(controller);
      upstreamSignal?.removeEventListener?.("abort", abortFromUpstream);
    }
  }, []);

  useEffect(
    () => () => {
      advertiserScopeEpochRef.current += 1;
      abortAdvertiserScopedRequests();
    },
    [abortAdvertiserScopedRequests]
  );

  useEffect(() => {
    try {
      localStorage.setItem(CINEMA_AI_PROVIDER_KEY, normalizeAiProviderForUi(selectedAiProvider));
    } catch (_err) {
      // Ignore storage issues.
    }
  }, [selectedAiProvider]);

  const resolvedAdvertiser = currentAdvertiser?.raw || currentAdvertiser || null;

  const resolvedAdvertiserId =
    resolvedAdvertiser?.id ??
    resolvedAdvertiser?._id ??
    resolvedAdvertiser?.advertiserId ??
    resolvedAdvertiser?.advertiser_id ??
    resolvedAdvertiser?.raw?.id ??
    resolvedAdvertiser?.raw?._id ??
    resolvedAdvertiser?.raw?.advertiserId ??
    resolvedAdvertiser?.raw?.advertiser_id ??
    null;

  const resolvedAdvertiserName = String(
    resolvedAdvertiser?.advertiser ||
      resolvedAdvertiser?.name ||
      ""
  ).trim();

  const resolvedCinemaReportUrl = useMemo(
    () => getFirstValidUrl(getAdvertiserCinemaReportCandidates(resolvedAdvertiser)),
    [resolvedAdvertiser]
  );

  const advertiserKey = resolvedAdvertiserId ? String(resolvedAdvertiserId) : "";

  useEffect(() => {
    let cancelled = false;

    const fetchPlanCampaigns = async () => {
      if (!resolvedAdvertiserId) {
        setLoadingPlanCampaigns(false);
        setPlanCampaigns([]);
        setPlanCampaignsAdvertiserKey("");
        return;
      }

      setPlanCampaigns([]);
      setPlanCampaignsAdvertiserKey("");
      setLoadingPlanCampaigns(true);
      try {
        const payload = await fetchJson(
          `/api/media-campaigns?advertiserId=${encodeURIComponent(resolvedAdvertiserId)}`
        );
        if (!cancelled) {
          setPlanCampaigns(coerceCampaignArray(payload));
          setPlanCampaignsAdvertiserKey(String(resolvedAdvertiserId));
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("Failed to fetch cinema flight campaigns:", err);
        if (!cancelled) {
          setPlanCampaigns([]);
          setPlanCampaignsAdvertiserKey(String(resolvedAdvertiserId));
        }
      }
      finally {
        if (!cancelled) setLoadingPlanCampaigns(false);
      }
    };

    fetchPlanCampaigns();
    return () => {
      cancelled = true;
    };
  }, [fetchJson, resolvedAdvertiserId]);

  const advertiserFallbackPlan = useMemo(
    () => buildAdvertiserFallbackPlan(resolvedAdvertiser),
    [resolvedAdvertiser]
  );

  const approvedFlightCampaign = useMemo(
    () =>
      selectApprovedPlanCampaign({
        campaigns: planCampaigns,
        selectedCampaignName: null,
        allowAdvertiserBestFit: true,
        allowSingleApprovedFallback: true,
      }),
    [planCampaigns]
  );

  const cinemaContextCampaigns = useMemo(
    () =>
      planCampaigns.filter(
        (campaign) =>
          getCinemaCampaignId(campaign) && !isInternalReportingExcludedCampaign(campaign)
      ),
    [planCampaigns]
  );

  const defaultCinemaContextCampaign = useMemo(
    () =>
      selectCinemaContextCampaign({
        campaigns: cinemaContextCampaigns,
        approvedCampaign: approvedFlightCampaign,
        preferredCampaignId: cinemaFootprintScope.campaignId,
      }),
    [approvedFlightCampaign, cinemaContextCampaigns, cinemaFootprintScope.campaignId]
  );

  const selectedCinemaCampaign = useMemo(() => {
    const selectedId = normalizePositiveInteger(cinemaFootprintScope.campaignId);
    if (!selectedId) return defaultCinemaContextCampaign;
    return (
      cinemaContextCampaigns.find(
        (campaign) => getCinemaCampaignId(campaign) === selectedId
      ) || null
    );
  }, [cinemaContextCampaigns, cinemaFootprintScope.campaignId, defaultCinemaContextCampaign]);

  const cinemaCanonicalScope = useMemo(
    () => readCinemaCanonicalScope(null, cinemaFootprintScope),
    [cinemaFootprintScope]
  );

  const locationTitleQuery = useMemo(() => {
    // Title is presentation metadata. Canonical read identity is always the
    // campaign/context/version tuple below; campaign labels never masquerade
    // as film titles.
    const scopedTitle = normalizeCinemaTitleScope(cinemaFootprintScope.title)
      ? String(cinemaFootprintScope.title).trim()
      : "";
    if (scopedTitle) return scopedTitle;
    return getCinemaCampaignTitle(selectedCinemaCampaign);
  }, [cinemaFootprintScope.title, selectedCinemaCampaign]);

  const synchronizeCinemaCanonicalScope = useCallback((payload) => {
    setCinemaFootprintScope((previous) => mergeCinemaCanonicalScope(previous, payload));
  }, []);

  // Visible default: a campaign awaiting reapproval remains the relevant
  // Cinema context even though it is not deployable. Campaign ID is selected
  // first; the backend then rehydrates canonical title-context ID + version.
  useEffect(() => {
    if (!defaultCinemaContextCampaign) return;
    setCinemaFootprintScope((previous) => {
      if (previous.campaignId) return previous;
      return {
        campaignId: String(getCinemaCampaignId(defaultCinemaContextCampaign) || ""),
        titleContextId: "",
        footprintVersion: "",
        title: getCinemaCampaignTitle(defaultCinemaContextCampaign),
      };
    });
  }, [defaultCinemaContextCampaign]);

  const filmReferenceInput = useMemo(
    () =>
      extractCampaignFilmReferenceInputs(
        selectedCinemaCampaign,
        locationTitleQuery || resolvedAdvertiserName
      ),
    [locationTitleQuery, resolvedAdvertiserName, selectedCinemaCampaign]
  );

  useEffect(() => {
    let cancelled = false;

    if (!filmReferenceInput?.hasAnySource) {
      setFilmReferenceEnrichment(null);
      return () => {
        cancelled = true;
      };
    }

    const fetchFilmReferenceEnrichment = async () => {
      try {
        const payload = await fetchJson("/api/film-reference-intelligence/enrich", {
          method: "POST",
          body: JSON.stringify({
            advertiserId: resolvedAdvertiserId,
            campaignId: getCinemaCampaignId(selectedCinemaCampaign),
            ...filmReferenceInput,
          }),
        });
        if (!cancelled) {
          setFilmReferenceEnrichment(
            payload && typeof payload === "object" ? payload : null
          );
        }
      } catch (err) {
        if (isAbortError(err)) return;
        console.error("Failed to enrich film reference intelligence:", err);
        if (!cancelled) setFilmReferenceEnrichment(null);
      }
    };

    setFilmReferenceEnrichment(null);
    fetchFilmReferenceEnrichment();

    return () => {
      cancelled = true;
    };
  }, [fetchJson, filmReferenceInput, resolvedAdvertiserId, selectedCinemaCampaign]);

  const filmIntelligence = useMemo(
    () => buildFilmIntelligenceContext(filmReferenceInput, filmReferenceEnrichment),
    [filmReferenceEnrichment, filmReferenceInput]
  );

  const cinemaCanonicalIdentityPending =
    Boolean(cinemaCanonicalScope.campaignId) &&
    cinemaCanonicalReadCampaignId !== String(cinemaCanonicalScope.campaignId);
  // The film identity this campaign's footprint is (or would be) saved under.
  // A bound title context outranks everything; otherwise the backend's governed
  // resolution supplies it, and only a campaign with no resolvable identity at
  // all reaches the manual first-import control.
  const cinemaIdentityForCampaign =
    cinemaTitleIdentity &&
    cinemaTitleIdentity.campaignId === String(cinemaCanonicalScope.campaignId || "")
      ? cinemaTitleIdentity
      : null;
  const cinemaResolvedIdentityTitle = String(
    cinemaIdentityForCampaign?.identity?.title || ""
  ).trim();
  // The governed reads answered for this campaign, but with a failure. The page
  // knows nothing about this campaign's title context and must not imply it
  // knows there is none.
  const cinemaIdentityUnavailable = Boolean(cinemaIdentityForCampaign?.unavailable);
  const cinemaResolvedFilmTitle = String(
    cinemaCanonicalScope.title ||
      (cinemaCanonicalIdentityPending ? "" : cinemaResolvedIdentityTitle) ||
      locationTitleQuery ||
      ""
  ).trim();
  const currentCinemaFootprintState = doesCinemaEvidenceMatchCanonicalScope(
    cinemaFootprintState,
    cinemaCanonicalScope
  )
    ? cinemaFootprintState
    : null;
  const currentCinemaSourceState = doesCinemaEvidenceMatchCanonicalScope(
    cinemaSourceState,
    cinemaCanonicalScope
  )
    ? cinemaSourceState
    : null;
  const cinemaFlightLineage = resolveCurrentCinemaFlightLineage({
    footprintState: currentCinemaFootprintState,
    sourceState: currentCinemaSourceState,
  });
  const selectedCinemaCampaignStatus = getCinemaCampaignStatus(selectedCinemaCampaign);
  const cinemaRequiresReapproval = hasCinemaReapprovalSignal(
    cinemaFlightLineage,
    selectedCinemaCampaignStatus
  );
  const cinemaFlightWarning = cinemaRequiresReapproval
    ? String(
        cinemaFlightLineage?.warning ||
          "TARGETING REVISION REQUIRES REAPPROVAL — DEPLOYMENT IS BLOCKED"
      )
    : "";

  // The governed way out of NEEDS_REAPPROVAL, as the backend describes it for
  // THIS operator's credentials. The page never derives approval authority on
  // its own — no directive means no action is offered.
  const cinemaReapprovalDirective = useMemo(
    () => normalizeCinemaReapprovalDirective(currentCinemaFootprintState?.reapproval),
    [currentCinemaFootprintState]
  );
  // Cortex has no approval surface of its own: the canonical one is a Quantum
  // route this portal cannot open. Rather than link a client viewer somewhere
  // they cannot reach, the panel names the authority that holds the approval.
  const cinemaReapprovalCta = useMemo(
    () =>
      buildCinemaReapprovalCta({
        directive: cinemaReapprovalDirective,
        portalCanReachApprovalSurface: false,
        submitting: cinemaReapprovalState?.status === "saving",
        confirming: cinemaReapprovalConfirming,
      }),
    [cinemaReapprovalConfirming, cinemaReapprovalDirective, cinemaReapprovalState]
  );

  // A scope change invalidates an open review: the operator must re-read the
  // footprint that is actually in front of them before approving anything.
  useEffect(() => {
    setCinemaReapprovalConfirming(false);
    setCinemaReapprovalState(null);
  }, [
    cinemaCanonicalScope.campaignId,
    cinemaCanonicalScope.titleContextId,
    cinemaCanonicalScope.footprintVersion,
  ]);

  const lastApprovedFlightRange = useMemo(() => {
    return resolveLastApprovedFlightRange({
      flightLineage: cinemaFlightLineage,
      campaign: selectedCinemaCampaign,
      campaignStatus: selectedCinemaCampaignStatus,
    });
  }, [cinemaFlightLineage, selectedCinemaCampaign, selectedCinemaCampaignStatus]);

  const approvedFlightRange = useMemo(
    () => getCampaignFlightRange(approvedFlightCampaign),
    [approvedFlightCampaign]
  );

  const advertiserFlightRange = useMemo(() => {
    if (!advertiserFallbackPlan) return null;
    return buildRangeObjectFromDates(
      advertiserFallbackPlan.flightStart,
      advertiserFallbackPlan.flightEnd
    );
  }, [advertiserFallbackPlan]);

  const resolvedFlightRange = resolveCinemaFlightRange({
    lastApprovedFlightRange,
    selectedCampaign: selectedCinemaCampaign,
    canonicalCampaignId: cinemaCanonicalScope.campaignId,
    requiresReapproval: cinemaRequiresReapproval,
    approvedFlightRange,
    advertiserFlightRange,
  });
  const hasFlightDatePreset = Boolean(
    resolvedFlightRange?.startDate && resolvedFlightRange?.endDate
  );

  const analysisWindowOptions = useMemo(
    () => [
      {
        value: FLIGHT_DATE_PRESET,
        label:
          resolvedFlightRange?.startDate && resolvedFlightRange?.endDate
            ? formatFlightRange(resolvedFlightRange.startDate, resolvedFlightRange.endDate)
            : "Flight not available",
      },
    ],
    [resolvedFlightRange]
  );

  const effectiveDatePreset = useMemo(() => resolveCinemaAnalysisDatePreset(), []);

  // ---- Analytics-parity overview (top-of-page) ----------------------------
  // Footprint-level readiness snapshot for the hero band and overview strip.
  // Values come only from canonical loaded state (cluster totals, the saved
  // footprint lifecycle, the governed state machine). Absent evidence renders
  // as an explicit em dash + honest detail — never a healthy-looking zero.
  const cinemaOverviewIntelligence = useMemo(
    () =>
      resolveGovernedIntelligenceState({
        initialized: Boolean(selectedAdvertiserId),
        loading: loadingLocationClusters,
        error: locationError || null,
        evidenceSources: [
          {
            key: "theaters",
            label: "theaters",
            count: Number(locationClusters?.totals?.totalRows || 0),
            required: true,
          },
          {
            key: "exhibitors",
            label: "exhibitor groups",
            count: (locationClusters?.chainClusters || []).length,
            required: true,
          },
          {
            key: "markets",
            label: "state markets",
            count: (locationClusters?.stateClusters || []).length,
            required: true,
          },
        ],
      }),
    [
      selectedAdvertiserId,
      loadingLocationClusters,
      locationError,
      locationClusters,
    ]
  );

  const cinemaOverviewStatusLabel =
    CINEMA_OVERVIEW_STATE_LABELS[cinemaOverviewIntelligence.state] ||
    CINEMA_OVERVIEW_STATE_LABELS[GOVERNED_INTELLIGENCE_STATES.UNINITIALIZED];

  // ---- Canonical Cinema Analytics model -----------------------------------
  // ONE pure aggregation over the governed footprint feeds every KPI, chart,
  // rail card, and table total, so no module can disagree on a denominator.
  const cinemaFootprintAnalytics = useMemo(
    () => buildCinemaFootprintAnalyticsModel(currentCinemaFootprintState),
    [currentCinemaFootprintState]
  );

  // Exhibitor rail cards: the campaign's real exhibitor mix wearing the
  // approved chain artwork. An exhibitor without approved artwork falls back to
  // the shared cinema mark — never a fabricated logo, and never initials, which
  // read as a failed image rather than as a theater.
  const cinemaExhibitorRailCards = useMemo(
    () =>
      cinemaFootprintAnalytics.exhibitors.map((exhibitor) => {
        const heroButton = resolveCinemaChainHeroButton(
          exhibitor.key,
          exhibitor.label
        );
        return {
          key: exhibitor.key,
          label: exhibitor.label,
          image: exhibitor.isIndependent ? null : heroButton?.image || null,
          countLabel: `${formatInteger(exhibitor.cinemaCount)} ${
            exhibitor.cinemaCount === 1 ? "CINEMA" : "CINEMAS"
          }`,
          shareLabel: `${formatFootprintPercentage(
            exhibitor.percentage
          )} OF FOOTPRINT`,
        };
      }),
    [cinemaFootprintAnalytics]
  );

  // Donut geometry for the exhibitor distribution chart. It is fed the SAME
  // canonical `exhibitors` array the horizontal bars consume, enriched only
  // with the artwork the rail cards already resolve — so the donut, the legend
  // and the bars are three views of one aggregation, never three calculations.
  // An exhibitor without approved artwork carries the shared cinema mark, the
  // same fallback the rail adopted, never a fabricated logo or a letter tile.
  const cinemaExhibitorDistribution = useMemo(
    () =>
      buildCinemaExhibitorDistribution(
        cinemaFootprintAnalytics.exhibitors.map((exhibitor) => {
          const artwork = resolveCinemaExhibitorArtwork(exhibitor);
          return { ...exhibitor, image: artwork.src, imageIsFallback: artwork.isFallback };
        }),
        cinemaFootprintAnalytics.totalCinemas
      ),
    [cinemaFootprintAnalytics]
  );

  // Every governed state tile, carrying the exhibitor mix that makes up its
  // count. The breakdown is read from the canonical model — the same
  // aggregation the tile counts come from — and artwork is attached by the
  // page's ONE exhibitor resolver, so a chain cannot wear one logo in the
  // donut and another in a state popover.
  const cinemaGeographicTiles = useMemo(() => {
    const stateByKey = new Map(
      cinemaFootprintAnalytics.states.map((group) => [group.key, group])
    );
    return Object.entries(US_STATE_TILE_GRID).map(([stateKey, cell]) => {
      const group = stateByKey.get(stateKey) || null;
      return {
        stateKey,
        row: cell.row,
        col: cell.col,
        state: group
          ? {
              ...group,
              countLabel: formatInteger(group.cinemaCount),
              exhibitors: group.exhibitors.map((exhibitor) => {
                const artwork = resolveCinemaExhibitorArtwork(exhibitor);
                return {
                  ...exhibitor,
                  countLabel: formatInteger(exhibitor.cinemaCount),
                  image: artwork.src,
                  imageIsFallback: artwork.isFallback,
                };
              }),
            }
          : null,
      };
    });
  }, [cinemaFootprintAnalytics]);

  const cinemaRosterVenues = useMemo(
    () =>
      filterAndSortCinemaVenues(cinemaFootprintAnalytics.venues, {
        search: cinemaRosterSearch,
        exhibitorKey: cinemaRosterExhibitorFilter,
        stateKey: cinemaRosterStateFilter,
        sortKey: cinemaRosterSort.key,
        sortDirection: cinemaRosterSort.direction,
      }),
    [
      cinemaFootprintAnalytics,
      cinemaRosterSearch,
      cinemaRosterExhibitorFilter,
      cinemaRosterStateFilter,
      cinemaRosterSort,
    ]
  );

  const cinemaRosterShowsMarketColumn =
    cinemaFootprintAnalytics.marketCoverage.withMarket > 0;

  const handleCinemaRosterSort = useCallback((key) => {
    setCinemaRosterSort((previous) =>
      previous.key === key
        ? { key, direction: previous.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);

  const cinemaWorkspaceSubtitle = useMemo(() => {
    if (!cinemaFootprintAnalytics.hasFootprint) {
      return "Upload the campaign's governed theater footprint to activate cinema analytics.";
    }
    const parts = [];
    if (cinemaFootprintAnalytics.footprintTitle) {
      parts.push(cinemaFootprintAnalytics.footprintTitle);
    }
    if (cinemaFootprintAnalytics.footprintVersion) {
      parts.push(`Footprint v${cinemaFootprintAnalytics.footprintVersion}`);
    }
    parts.push(
      `${formatInteger(cinemaFootprintAnalytics.totalCinemas)} governed cinemas across ${formatInteger(
        cinemaFootprintAnalytics.distinctStateCount
      )} ${cinemaFootprintAnalytics.distinctStateCount === 1 ? "state" : "states"}`
    );
    return parts.join(" · ");
  }, [cinemaFootprintAnalytics]);

  const cinemaHeroContextChips = useMemo(() => {
    const chips = [];
    const campaignName = getCinemaCampaignName(selectedCinemaCampaign);
    const scopedTitle = String(cinemaResolvedFilmTitle || "").trim();
    if (campaignName) {
      chips.push({ key: "campaign", label: `Campaign · ${campaignName}` });
    }
    if (scopedTitle) chips.push({ key: "title", label: `Film · ${scopedTitle}` });
    // The footprint version and the readiness state each have a dedicated card
    // in the overview strip directly below this band. Repeating them here put
    // the same value in two adjacent blocks, so the chips carry only context
    // the strip does not: campaign, title, flight window and the reapproval
    // block. "No saved footprint" survives as a chip because there is no
    // version to show in that state and the absence must stay visible.
    const footprintVersion = cinemaCanonicalScope.footprintVersion;
    if (!footprintVersion) {
      chips.push({ key: "footprint", label: "No saved footprint" });
    }
    const flightLabel = analysisWindowOptions[0]?.label;
    if (flightLabel && flightLabel !== "Flight not available") {
      chips.push({
        key: "flight",
        label: `${cinemaRequiresReapproval ? "Last approved flight" : "Flight"} · ${flightLabel}`,
      });
    }
    if (cinemaRequiresReapproval) {
      chips.push({
        key: "reapproval",
        label: "Reapproval required · deployment blocked",
      });
    }
    return chips;
  }, [
    cinemaCanonicalScope.footprintVersion,
    cinemaRequiresReapproval,
    analysisWindowOptions,
    cinemaResolvedFilmTitle,
    selectedCinemaCampaign,
  ]);

  const cinemaOverviewCards = useMemo(() => {
    // Values derive from the canonical footprint model (one denominator for
    // every share) plus the governed lifecycle counts and evidence state the
    // page already maintains — locationClusters?.totals keeps driving the
    // governed status via cinemaOverviewIntelligence. Absent evidence renders
    // as an explicit em dash with honest detail, never a fabricated number.
    const model = cinemaFootprintAnalytics;
    const hasFootprint = model.hasFootprint;
    const counts = currentCinemaFootprintState?.counts || null;
    const footprintVersion = cinemaCanonicalScope.footprintVersion ?? null;
    const policyRangeLabel =
      Number.isFinite(model.radius.minimumRadius) &&
      Number.isFinite(model.radius.maximumRadius)
        ? `${model.radius.minimumRadius}–${model.radius.maximumRadius} mi`
        : "governed radius";
    return [
      {
        key: "total-cinemas",
        label: "Total Cinemas",
        value: hasFootprint ? formatInteger(model.totalCinemas) : "—",
        detail: hasFootprint
          ? "Governed footprint venues"
          : "No footprint imported",
      },
      {
        key: "exhibitors",
        label: "Exhibitors",
        value: hasFootprint ? formatInteger(model.exhibitors.length) : "—",
        detail: hasFootprint ? "Distinct exhibitor groups" : "Awaiting import",
      },
      {
        key: "states",
        label: "States",
        value: hasFootprint ? formatInteger(model.distinctStateCount) : "—",
        detail: hasFootprint ? "States with governed venues" : "Awaiting import",
      },
      {
        key: "top-exhibitor-share",
        label: "Top Exhibitor Share",
        value:
          hasFootprint && model.topExhibitor
            ? formatFootprintPercentage(model.topExhibitor.percentage)
            : "—",
        detail:
          hasFootprint && model.topExhibitor
            ? model.topExhibitor.label
            : "Awaiting import",
      },
      {
        key: "independent-share",
        label: "Independent / Other Share",
        value: hasFootprint
          ? formatFootprintPercentage(model.independentShare)
          : "—",
        detail: hasFootprint ? model.denominatorLabel : "Awaiting import",
      },
      {
        key: "geofence-ready",
        label: "Geo-Fence Ready",
        value: counts ? formatInteger(Number(counts.geoFenceReady || 0)) : "—",
        detail: counts
          ? `Coordinates + resolved ${policyRangeLabel} · ${formatInteger(
              Number(counts?.geocodePending || 0)
            )} awaiting geocoding`
          : "No saved footprint",
      },
      {
        key: "footprint-version",
        label: "Footprint Version",
        value: footprintVersion ? `v${footprintVersion}` : "—",
        detail: footprintVersion ? "Active saved version" : "Save a footprint to version it",
      },
      {
        key: "analysis-status",
        label: "Analysis Status",
        value: cinemaOverviewStatusLabel,
        detail: "Governed evidence state",
        isStatus: true,
      },
    ];
  }, [
    cinemaCanonicalScope.footprintVersion,
    cinemaFootprintAnalytics,
    currentCinemaFootprintState,
    cinemaOverviewStatusLabel,
  ]);

  const hasExplicitDatePresetSelection =
    Boolean(advertiserKey) &&
    dateSelection.advertiserKey === advertiserKey &&
    Boolean(dateSelection.preset);

  const waitingForDefaultDatePreset =
    Boolean(advertiserKey) && !hasExplicitDatePresetSelection && loadingPlanCampaigns;
  const locationContextKey = useMemo(() => {
    if (
      !resolvedAdvertiserId ||
      waitingForDefaultDatePreset ||
      loadingPlanCampaigns ||
      planCampaignsAdvertiserKey !== advertiserKey
    ) {
      return "";
    }
    if (!cinemaCanonicalScope.campaignId) return "";
    return [
      resolvedAdvertiserId,
      `campaign_${cinemaCanonicalScope.campaignId}`,
      `context_${cinemaCanonicalScope.titleContextId || "pending"}`,
      `footprint_${cinemaCanonicalScope.footprintVersion || "pending"}`,
      `refresh_${cinemaCanonicalRefreshNonce}`,
    ].join("::");
  }, [
    advertiserKey,
    cinemaCanonicalRefreshNonce,
    cinemaCanonicalScope.campaignId,
    cinemaCanonicalScope.footprintVersion,
    cinemaCanonicalScope.titleContextId,
    loadingPlanCampaigns,
    planCampaignsAdvertiserKey,
    resolvedAdvertiserId,
    waitingForDefaultDatePreset,
  ]);
  const hasResolvedLocationContext = Boolean(locationContextKey);
  const analysisWindowLabel =
    analysisWindowOptions.find((option) => option.value === effectiveDatePreset)?.label ||
    "Flight not available";
  const currentAnalysisStorageKey = useMemo(
    () =>
      buildCinemaAnalysisReportStorageKey({
        audienceMode: "client",
        advertiserId: resolvedAdvertiserId,
        aiProvider: selectedAiProvider,
        datePreset: effectiveDatePreset,
        flightRange: resolvedFlightRange,
        campaignId: cinemaCanonicalScope.campaignId,
        titleContextId: cinemaCanonicalScope.titleContextId,
        footprintVersion: cinemaCanonicalScope.footprintVersion,
        title: locationTitleQuery,
      }),
    [
      cinemaCanonicalScope.campaignId,
      cinemaCanonicalScope.footprintVersion,
      cinemaCanonicalScope.titleContextId,
      effectiveDatePreset,
      locationTitleQuery,
      resolvedAdvertiserId,
      resolvedFlightRange,
      selectedAiProvider,
    ]
  );
  const currentScopedAnalysis =
    hasFlightDatePreset &&
    currentAnalysisStorageKey &&
    loadedAnalysisKey === currentAnalysisStorageKey
      ? analysis
      : null;

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.debug("[CinemaAnalytics] loading state", {
      advertiserId: resolvedAdvertiserId,
      advertiserKey,
      loadingAdvertisers,
      loadingPlanCampaigns,
      waitingForDefaultDatePreset,
      loadingAnalysis,
      effectiveDatePreset,
    });
  }, [
    advertiserKey,
    effectiveDatePreset,
    loadingAdvertisers,
    loadingAnalysis,
    loadingPlanCampaigns,
    resolvedAdvertiserId,
    waitingForDefaultDatePreset,
  ]);

  useEffect(() => {
    if (!resolvedAdvertiserId) {
      setLoadingAnalysis(false);
      setAnalysis(null);
      setLoadedAnalysisKey("");
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      setError("");
      return;
    }

    if (waitingForDefaultDatePreset) {
      setLoadingAnalysis(false);
      setAnalysis(null);
      setLoadedAnalysisKey("");
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      if (process.env.NODE_ENV === "development") {
        console.debug("[CinemaAnalytics] waiting for default date window", {
          advertiserId: resolvedAdvertiserId,
          advertiserKey,
          selectedPreset: dateSelection.preset,
          hasFlightDatePreset,
          loadingPlanCampaigns,
        });
      }
      return;
    }

    if (!hasFlightDatePreset || !currentAnalysisStorageKey) {
      setLoadingAnalysis(false);
      setAnalysis(null);
      setLoadedAnalysisKey("");
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      setError("");
      return;
    }

    setError("");
    const storedAnalysis = readStoredCinemaAnalysisReport(currentAnalysisStorageKey);
    if (storedAnalysis) {
      setAnalysis(storedAnalysis);
      setLoadedAnalysisKey(currentAnalysisStorageKey);
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(true);
      return;
    }

    setAnalysisLoadedFromCache(false);
    // A cache miss for the current canonical key always clears any report
    // loaded under an earlier key. Cross-scope reports are never retained as
    // a visible "stale" presentation while the new scope resolves.
    setAnalysis(null);
    setLoadedAnalysisKey("");
    setAnalysisIsStale(false);
  }, [
    advertiserKey,
    currentAnalysisStorageKey,
    dateSelection.preset,
    hasFlightDatePreset,
    loadedAnalysisKey,
    loadingPlanCampaigns,
    resolvedAdvertiserId,
    waitingForDefaultDatePreset,
  ]);

  const runCinemaAnalysis = useCallback(async () => {
    if (!resolvedAdvertiserId || waitingForDefaultDatePreset || !hasFlightDatePreset) return;

    const requestScopeEpoch = advertiserScopeEpochRef.current;
    const requestContextKey = locationContextKey;
    setLoadingAnalysis(true);
    setError("");
    console.log("[CinemaAnalytics] analysis request", {
      advertiserId: resolvedAdvertiserId,
      selectedDatePreset: effectiveDatePreset,
      hasFlightDatePreset,
      resolvedFlightRange,
      trigger: "manual_run",
    });

    try {
      const params = new URLSearchParams({ mode: "client" });
      appendCinemaCanonicalScopeParams(params, cinemaCanonicalScope);
      if (effectiveDatePreset === FLIGHT_DATE_PRESET && hasFlightDatePreset) {
        params.set("datePreset", FLIGHT_DATE_PRESET);
        params.set("startDate", resolvedFlightRange.startDate);
        params.set("endDate", resolvedFlightRange.endDate);
      } else {
        params.set("datePreset", effectiveDatePreset);
      }

      const payload = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}?${params.toString()}`
      );
      if (locationContextKeyRef.current !== requestContextKey) return;
      synchronizeCinemaCanonicalScope(payload);
      console.log("[CinemaAnalytics] analysis response", {
        advertiserId: resolvedAdvertiserId,
        audienceMode: payload?.audienceMode || null,
        hasSnapshots: payload?.availability?.hasSnapshots ?? null,
        hasGaOverlay: payload?.availability?.hasGaOverlay ?? null,
        generatedAt: payload?.generatedAt || null,
      });
      setAnalysis(payload);
      setLoadedAnalysisKey(currentAnalysisStorageKey);
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      writeStoredCinemaAnalysisReport(currentAnalysisStorageKey, payload);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("[CinemaAnalytics] analysis failure", {
        advertiserId: resolvedAdvertiserId,
        selectedDatePreset: effectiveDatePreset,
        hasFlightDatePreset,
        resolvedFlightRange,
        message: String(err?.message || err),
        error: err,
      });
      setError(String(err?.message || "Failed to load cinema market analysis"));
    } finally {
      if (requestScopeEpoch === advertiserScopeEpochRef.current) {
        setLoadingAnalysis(false);
      }
    }
  }, [
    currentAnalysisStorageKey,
    cinemaCanonicalScope,
    effectiveDatePreset,
    fetchJson,
    hasFlightDatePreset,
    locationContextKey,
    resolvedAdvertiserId,
    resolvedFlightRange,
    synchronizeCinemaCanonicalScope,
    waitingForDefaultDatePreset,
  ]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setLocationOffset(0);
      setLocationSearch(locationSearchInput.trim());
    }, 300);

    return () => window.clearTimeout(handle);
  }, [locationSearchInput]);

  useEffect(() => {
    locationContextKeyRef.current = locationContextKey;
    latestLocationsRequestKeyRef.current = "";
    latestLocationClustersRequestKeyRef.current = "";
    setLocationError("");
    setLocationsData(EMPTY_LOCATION_DATA);
    setLocationClusters(EMPTY_LOCATION_CLUSTERS);
    setSelectedLocationId(null);
  }, [locationContextKey]);

  const loadLocations = useCallback(async () => {
    if (!resolvedAdvertiserId || !hasResolvedLocationContext) {
      latestLocationsRequestKeyRef.current = "";
      setLoadingLocations(false);
      setLocationsData(EMPTY_LOCATION_DATA);
      return;
    }

    const requestKey = [
      locationContextKey,
      locationLimit,
      locationOffset,
      locationSearch,
      locationStateFilter,
      locationChainFilter,
      locationSourceFilter,
      locationIncludeInactive ? "includeInactive" : "activeOnly",
    ].join("|");
    latestLocationsRequestKeyRef.current = requestKey;
    setLoadingLocations(true);
    setLocationError("");
    try {
      const params = new URLSearchParams({
        limit: String(locationLimit),
        offset: String(locationOffset),
      });
      if (resolvedAdvertiserId) params.set("advertiserId", String(resolvedAdvertiserId));
      appendCinemaCanonicalScopeParams(params, cinemaCanonicalScope);
      if (locationSearch) params.set("search", locationSearch);
      if (locationStateFilter) params.set("state", locationStateFilter);
      if (locationChainFilter) params.set("chainKey", locationChainFilter);
      if (locationSourceFilter) params.set("source", locationSourceFilter);
      if (locationIncludeInactive) params.set("includeInactive", "true");
      const payload = await fetchJson(
        `/api/cinema-location-intelligence/locations?${params.toString()}`
      );
      if (
        latestLocationsRequestKeyRef.current !== requestKey ||
        locationContextKeyRef.current !== locationContextKey
      ) {
        return;
      }
      synchronizeCinemaCanonicalScope(payload);
      setLocationsData(payload);
      const hasSelectedLocation = (payload.items || []).some(
        (item) => Number(item.id) === Number(selectedLocationId)
      );
      if (!hasSelectedLocation) {
        setSelectedLocationId(payload.items?.[0]?.id ?? null);
      }
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("Failed to load client cinema locations:", err);
      if (
        latestLocationsRequestKeyRef.current !== requestKey ||
        locationContextKeyRef.current !== locationContextKey
      ) {
        return;
      }
      setLocationError(String(err?.message || "Failed to load cinema footprint"));
      setLocationsData(EMPTY_LOCATION_DATA);
    } finally {
      if (latestLocationsRequestKeyRef.current === requestKey) {
        setLoadingLocations(false);
      }
    }
  }, [
    cinemaCanonicalScope,
    hasResolvedLocationContext,
    fetchJson,
    locationChainFilter,
    locationContextKey,
    locationIncludeInactive,
    locationLimit,
    locationOffset,
    locationSearch,
    locationSourceFilter,
    locationStateFilter,
    resolvedAdvertiserId,
    selectedLocationId,
    synchronizeCinemaCanonicalScope,
  ]);

  const loadLocationClusters = useCallback(async () => {
    if (!resolvedAdvertiserId || !hasResolvedLocationContext) {
      latestLocationClustersRequestKeyRef.current = "";
      setLoadingLocationClusters(false);
      setLocationClusters(EMPTY_LOCATION_CLUSTERS);
      return;
    }

    const requestKey = [
      locationContextKey,
      locationSearch,
      locationStateFilter,
      locationChainFilter,
      locationSourceFilter,
      locationIncludeInactive ? "includeInactive" : "activeOnly",
    ].join("|");
    latestLocationClustersRequestKeyRef.current = requestKey;
    setLoadingLocationClusters(true);
    setLocationError("");
    try {
      const params = new URLSearchParams();
      if (resolvedAdvertiserId) params.set("advertiserId", String(resolvedAdvertiserId));
      appendCinemaCanonicalScopeParams(params, cinemaCanonicalScope);
      if (locationSearch) params.set("search", locationSearch);
      if (locationStateFilter) params.set("state", locationStateFilter);
      if (locationChainFilter) params.set("chainKey", locationChainFilter);
      if (locationSourceFilter) params.set("source", locationSourceFilter);
      if (locationIncludeInactive) params.set("includeInactive", "true");
      const payload = await fetchJson(
        `/api/cinema-location-intelligence/locations/clusters?${params.toString()}`
      );
      if (
        latestLocationClustersRequestKeyRef.current !== requestKey ||
        locationContextKeyRef.current !== locationContextKey
      ) {
        return;
      }
      synchronizeCinemaCanonicalScope(payload);
      setLocationClusters(payload);
    } catch (err) {
      if (isAbortError(err)) return;
      console.error("Failed to load client cinema clusters:", err);
      if (
        latestLocationClustersRequestKeyRef.current !== requestKey ||
        locationContextKeyRef.current !== locationContextKey
      ) {
        return;
      }
      setLocationError(String(err?.message || "Failed to load cinema footprint clusters"));
      setLocationClusters(EMPTY_LOCATION_CLUSTERS);
    } finally {
      if (latestLocationClustersRequestKeyRef.current === requestKey) {
        setLoadingLocationClusters(false);
      }
    }
  }, [
    cinemaCanonicalScope,
    hasResolvedLocationContext,
    fetchJson,
    locationChainFilter,
    locationContextKey,
    locationIncludeInactive,
    locationSearch,
    locationSourceFilter,
    locationStateFilter,
    resolvedAdvertiserId,
    synchronizeCinemaCanonicalScope,
  ]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadLocationClusters();
  }, [loadLocationClusters]);

  const handleRunAnalysis = () => {
    void runCinemaAnalysis();
  };

  const fetchCinemaSourceState = useCallback(async () => {
    if (!resolvedAdvertiserId || !cinemaCanonicalScope.campaignId || !locationContextKey) {
      setCinemaSourceState(null);
      return;
    }
    const requestContextKey = locationContextKey;
    const requestCampaignId = cinemaCanonicalScope.campaignId;
    try {
      const params = new URLSearchParams();
      appendCinemaCanonicalScopeParams(params, cinemaCanonicalScope);
      const payload = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/cinema-source-state?${params.toString()}`
      );
      if (locationContextKeyRef.current !== requestContextKey) return;
      synchronizeCinemaCanonicalScope(payload);
      setCinemaSourceState(payload);
      setCinemaTitleIdentity({
        campaignId: String(requestCampaignId),
        identity: payload?.titleIdentity || null,
      });
      setCinemaCanonicalReadCampaignId(String(requestCampaignId));
    } catch (err) {
      if (isAbortError(err)) return;
      // Source-state is supplementary; never block the page on it.
      if (locationContextKeyRef.current === requestContextKey) {
        setCinemaSourceState(null);
      // A read that never answered is not evidence that this campaign owns no
      // title context. Record the failure against the campaign so the panel can
      // say identity is unavailable instead of presenting the control that
      // means "nothing exists here" — but never downgrade an identity another
      // governed read already resolved for this same campaign.
      setCinemaTitleIdentity((previous) =>
        previous &&
        previous.campaignId === String(requestCampaignId) &&
        previous.identity
          ? previous
          : { campaignId: String(requestCampaignId), identity: null, unavailable: true }
      );
        setCinemaCanonicalReadCampaignId(String(requestCampaignId));
      }
    }
  }, [
    cinemaCanonicalScope,
    fetchJson,
    locationContextKey,
    resolvedAdvertiserId,
    synchronizeCinemaCanonicalScope,
  ]);

  // Auto-load saved source state on canonical campaign/context/version change.
  useEffect(() => {
    void fetchCinemaSourceState();
  }, [fetchCinemaSourceState]);

  // Canonical persisted footprint lifecycle state — the saved rows, geocoding
  // counts, governed geo-fence policy, and resolved execution radii. This is
  // what survives reload; the parse preview is session-only.
  const fetchCinemaFootprintState = useCallback(async () => {
    if (!resolvedAdvertiserId || !cinemaCanonicalScope.campaignId || !locationContextKey) {
      setCinemaFootprintState(null);
      return;
    }
    const requestContextKey = locationContextKey;
    const requestCampaignId = cinemaCanonicalScope.campaignId;
    try {
      const params = new URLSearchParams();
      appendCinemaCanonicalScopeParams(params, cinemaCanonicalScope);
      const payload = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/cinema-footprint?${params.toString()}`
      );
      if (locationContextKeyRef.current !== requestContextKey) return;
      synchronizeCinemaCanonicalScope(payload);
      setCinemaFootprintState(payload?.exists ? payload : null);
      setCinemaTitleIdentity({
        campaignId: String(requestCampaignId),
        identity: payload?.titleIdentity || null,
      });
      setCinemaCanonicalReadCampaignId(String(requestCampaignId));
      if (payload?.exists && payload.geoFencePolicy) {
        setCinemaPolicyDraft({
          minimumRadius: String(payload.geoFencePolicy.minimumRadius),
          maximumRadius: String(payload.geoFencePolicy.maximumRadius),
          defaultRadius: String(payload.geoFencePolicy.defaultRadius),
        });
      } else {
        setCinemaPolicyDraft({
          minimumRadius: "3",
          maximumRadius: "5",
          defaultRadius: "5",
        });
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (locationContextKeyRef.current === requestContextKey) {
        setCinemaFootprintState(null);
      // A read that never answered is not evidence that this campaign owns no
      // title context. Record the failure against the campaign so the panel can
      // say identity is unavailable instead of presenting the control that
      // means "nothing exists here" — but never downgrade an identity another
      // governed read already resolved for this same campaign.
      setCinemaTitleIdentity((previous) =>
        previous &&
        previous.campaignId === String(requestCampaignId) &&
        previous.identity
          ? previous
          : { campaignId: String(requestCampaignId), identity: null, unavailable: true }
      );
        setCinemaCanonicalReadCampaignId(String(requestCampaignId));
        setCinemaPolicyDraft({
          minimumRadius: "3",
          maximumRadius: "5",
          defaultRadius: "5",
        });
      }
    }
  }, [
    cinemaCanonicalScope,
    fetchJson,
    locationContextKey,
    resolvedAdvertiserId,
    synchronizeCinemaCanonicalScope,
  ]);

  useEffect(() => {
    void fetchCinemaFootprintState();
  }, [fetchCinemaFootprintState]);

  // Map structured backend refusal codes to actionable copy. Campaign-scope,
  // persistence, and geocoding failures are never collapsed together.
  const buildCinemaImportErrorMessage = (err) => {
    const code = err?.code || err?.payload?.code || null;
    if (code === "TITLE_SCOPED_CAMPAIGN_REQUIRED") {
      return "The CSV was parsed successfully, but it has not been saved. Select the title-scoped campaign for this footprint and press Save Footprint again.";
    }
    if (code === "CAMPAIGN_ADVERTISER_MISMATCH" || code === "CAMPAIGN_NOT_TITLE_SCOPED") {
      return String(err?.message || "The selected campaign cannot scope this footprint.");
    }
    if (code === "CINEMA_SCOPE_MISMATCH" || code === "FOOTPRINT_VERSION_MISMATCH") {
      return "The saved Cinema context changed while this file was open. Reload the canonical campaign footprint, review the current version, and retry the save.";
    }
    if (code === "GEOCODING_QUEUE_FAILED") {
      return "The footprint was saved, but automatic geocoding could not be started. Retry geocoding without re-uploading the file.";
    }
    return String(err?.message || "Failed to save cinema footprint CSV.");
  };

  const persistCinemaCsvImport = async (parsed, fileName) => {
    if (!resolvedAdvertiserId) {
      setCinemaImportSaveState({
        status: "error",
        message: "Select an advertiser before saving a CinemaCloudWorks CSV.",
      });
      return;
    }
    const scopeCampaignId = Number(cinemaFootprintScope.campaignId);
    // Same identity the panel showed: a typed first-import title if there is
    // one, otherwise the governed resolution. The operator saves under the film
    // identity they were shown, never under a silently different one.
    const scopeTitle =
      String(cinemaFootprintScope.title || "").trim() || cinemaResolvedFilmTitle;
    if (!Number.isFinite(scopeCampaignId) || scopeCampaignId <= 0 || !scopeTitle) {
      // Campaign scope is intercepted BEFORE submission — the user never
      // reaches a generic failed-import state, and the parsed rows are
      // retained so only the save needs to be retried.
      setCinemaImportSaveState({
        status: "error",
        code: "TITLE_SCOPED_CAMPAIGN_REQUIRED",
        message:
          "Select a title-scoped campaign before saving this cinema footprint. The parsed file is retained — fix the selection and press Save Footprint.",
      });
      return;
    }

    setCinemaImportSaveState({ status: "saving", message: "Saving cinema footprint..." });
    try {
      const result = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/cinema-import`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...buildCinemaCanonicalScopeBody({
              ...cinemaCanonicalScope,
              campaignId: scopeCampaignId,
            }),
            title: scopeTitle,
            sourceFilename: fileName,
            records: parsed.rows,
          }),
        }
      );
      const sheet = result?.sheet || {};
      const sheetNote = sheet.synced
        ? "Master Google Sheet updated."
        : sheet.error
        ? "Google Sheet sync failed — backend saved data is still available."
        : sheet.reason === "drive_not_configured"
        ? "Google Sheet sync not configured."
        : "";
      const baseMessage = result?.duplicate
        ? result?.message || "This CinemaCloudWorks CSV was already saved for this advertiser."
        : result?.message || "CinemaCloudWorks CSV uploaded and saved.";
      const reapprovalNote = result?.lifecycleConsequence
        ? ` Campaign returned to governed review (${result.lifecycleConsequence.status}).`
        : "";
      setCinemaImportSaveState({
        status: result?.duplicate ? "duplicate" : "saved",
        message: `${baseMessage}${reapprovalNote}${sheetNote ? ` ${sheetNote}` : ""}`,
        rowsPersisted: result?.rowsPersisted ?? 0,
        rowsFailed: result?.rowsFailed ?? 0,
        geocodePendingCount: result?.geocodePendingCount ?? 0,
        footprintVersion: result?.duplicate
          ? cinemaCanonicalScope.footprintVersion || result?.footprintVersion || null
          : result?.footprintVersion ?? null,
      });

      setPendingCinemaImport(null);
      if (result?.duplicate) {
        // A duplicate upload is not a footprint mutation. Preserve the
        // currently loaded canonical lineage/report/presentation. A duplicate
        // response may describe the historical matching import rather than the
        // current footprint, so it is never allowed to advance or regress the
        // active tuple. Existing campaign-scoped reads remain authoritative.
        return;
      }

      advertiserScopeEpochRef.current += 1;
      abortAdvertiserScopedRequests();
      locationContextKeyRef.current = "";
      latestLocationsRequestKeyRef.current = "";
      latestLocationClustersRequestKeyRef.current = "";
      setAnalysis(null);
      setLoadedAnalysisKey("");
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      setLoadingAnalysis(false);
      setCinemaCsvUploadBusy(false);
      setError("");
      setLocationsData(EMPTY_LOCATION_DATA);
      setLocationClusters(EMPTY_LOCATION_CLUSTERS);
      setLoadingLocations(false);
      setLoadingLocationClusters(false);
      setLocationError("");
      setSelectedLocationId(null);
      setCinemaSourceState(null);
      setCinemaFootprintState(null);
      setCinemaPolicyDraft(null);
      setCinemaPolicySaveState(null);

      // A first-context import can legitimately persist footprint v1 while the
      // pre-import placeholder key also said v1. Force the canonical reads to
      // run even when the identity tuple's text is unchanged, and remove the
      // prior report under that same key before any cache effect can reuse it.
      if (currentAnalysisStorageKey) {
        try {
          window.localStorage.removeItem(currentAnalysisStorageKey);
        } catch (_err) {
          // Storage can be unavailable in privacy-restricted environments.
        }
      }
      setCinemaCanonicalRefreshNonce((value) => value + 1);

      synchronizeCinemaCanonicalScope(result);
      if (result?.lifecycleConsequence?.status && scopeCampaignId) {
        setPlanCampaigns((campaigns) =>
          campaigns.map((campaign) =>
            getCinemaCampaignId(campaign) === scopeCampaignId
              ? { ...campaign, status: result.lifecycleConsequence.status }
              : campaign
          )
        );
      }
      // synchronizeCinemaCanonicalScope advances the canonical context/version.
      // Its scope-driven effects rehydrate source and footprint state using the
      // exact returned identity. Analysis remains an explicit user action; this
      // import response must not run against the pre-import render closure.
    } catch (err) {
      if (isAbortError(err)) return;
      setCinemaImportSaveState({
        status: "error",
        code: err?.code || null,
        message: buildCinemaImportErrorMessage(err),
      });
    }
  };

  const handleSaveGeoFencePolicy = async () => {
    if (
      !resolvedAdvertiserId ||
      !cinemaCanonicalScope.campaignId ||
      !cinemaCanonicalScope.titleContextId ||
      !cinemaCanonicalScope.footprintVersion ||
      !cinemaPolicyDraft
    ) {
      return;
    }
    setCinemaPolicySaveState({ status: "saving", message: "Saving geo-fence policy..." });
    try {
      const payload = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/geofence-policy`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...buildCinemaCanonicalScopeBody(cinemaCanonicalScope),
            policy: {
              unit: "MILES",
              minimumRadius: Number(cinemaPolicyDraft.minimumRadius),
              maximumRadius: Number(cinemaPolicyDraft.maximumRadius),
              defaultRadius: Number(cinemaPolicyDraft.defaultRadius),
              allowLocationOverrides: true,
            },
          }),
        }
      );
      synchronizeCinemaCanonicalScope(payload);
      setCinemaPolicySaveState({
        status: "saved",
        message: `Geo-fence policy saved: ${payload.policy.minimumRadius}–${payload.policy.maximumRadius} miles, default ${payload.policy.defaultRadius}.`,
      });
      await fetchCinemaFootprintState();
    } catch (err) {
      if (isAbortError(err)) return;
      setCinemaPolicySaveState({
        status: "error",
        message: String(err?.message || "Failed to save geo-fence policy."),
      });
    }
  };

  const handleFootprintRadiusOverride = async (titleLocationId, rawValue) => {
    if (
      !resolvedAdvertiserId ||
      !cinemaCanonicalScope.campaignId ||
      !cinemaCanonicalScope.titleContextId ||
      !cinemaCanonicalScope.footprintVersion
    ) {
      return;
    }
    try {
      await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/footprint-locations/${titleLocationId}/radius-override`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...buildCinemaCanonicalScopeBody(cinemaCanonicalScope),
            radiusMiles: String(rawValue).trim() === "" ? null : Number(rawValue),
          }),
        }
      );
      setCinemaPolicySaveState(null);
      await fetchCinemaFootprintState();
    } catch (err) {
      if (isAbortError(err)) return;
      setCinemaPolicySaveState({
        status: "error",
        message: String(err?.message || "Failed to save radius override."),
      });
    }
  };

  // Refusals here are governance outcomes, not transport failures. Each one
  // tells the operator what is still true and what to do next; none of them
  // suggest retrying the same approval unchanged.
  const buildCinemaReapprovalErrorMessage = (err) => {
    const code = err?.code || err?.payload?.code || null;
    if (code === "FOOTPRINT_VERSION_MISMATCH") {
      return "A newer cinema footprint was saved while this review was open. Nothing was approved. Reload the canonical footprint and review the newer version before approving.";
    }
    if (code === "CINEMA_REAPPROVAL_AUTHORITY_REQUIRED" || code === "AUTHORIZATION_DENIED") {
      return "Reapproving a revised cinema footprint requires superadmin approval authority. Nothing was changed.";
    }
    if (code === "CINEMA_REAPPROVAL_NOT_APPLICABLE") {
      return String(
        err?.message ||
          "This campaign is not awaiting reapproval. Reapproval restores APPROVED from NEEDS_REAPPROVAL only."
      );
    }
    if (code === "CINEMA_REAPPROVAL_ACKNOWLEDGEMENT_REQUIRED") {
      return "The reviewed footprint version could not be confirmed, so nothing was approved. Reload the canonical footprint and try again.";
    }
    if (code === "CINEMA_REAPPROVAL_SCOPE_INCOMPLETE") {
      return "This cinema footprint is not bound to a campaign, so there is no campaign lifecycle to reapprove.";
    }
    return String(err?.message || "The cinema footprint could not be reapproved. Nothing was changed.");
  };

  // Governed reapproval. The acknowledgement body is the backend's own — the
  // page forwards the exact footprint version the operator was shown and never
  // substitutes whatever is current at click time. A version that moved
  // underneath the operator comes back as FOOTPRINT_VERSION_MISMATCH and the
  // review starts again against the newer footprint.
  const handleCinemaReapprove = async () => {
    const cta = cinemaReapprovalCta;
    if (
      !resolvedAdvertiserId ||
      cta.mode !== CINEMA_REAPPROVAL_CTA_MODES.ACTION ||
      !cta.actionPath ||
      !cta.requestBody ||
      cinemaReapprovalState?.status === "saving"
    ) {
      return;
    }
    setCinemaReapprovalState({ status: "saving", message: "Recording the governed reapproval…" });
    try {
      const payload = await fetchJson(cta.actionPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cta.requestBody),
      });
      setCinemaReapprovalConfirming(false);
      setCinemaReapprovalState({
        status: "saved",
        message: String(
          payload?.message || "Cinema footprint reapproved; deployment readiness recalculated."
        ),
      });
      // The restored lifecycle propagates through the same transition path the
      // import uses, so the status chips, flight lineage and deployment block
      // all move together off one backend-reported outcome.
      if (payload?.status && payload?.campaignId) {
        setPlanCampaigns((campaigns) =>
          campaigns.map((campaign) =>
            getCinemaCampaignId(campaign) === payload.campaignId
              ? { ...campaign, status: payload.status }
              : campaign
          )
        );
      }
      // Re-read the canonical state so lifecycle, lineage, and the deployment
      // block all come from the backend rather than an optimistic guess.
      await fetchCinemaFootprintState();
    } catch (err) {
      if (isAbortError(err)) return;
      setCinemaReapprovalState({
        status: "error",
        message: buildCinemaReapprovalErrorMessage(err),
      });
    }
  };

  const handleRefreshGeocoding = async () => {
    if (
      !resolvedAdvertiserId ||
      !cinemaCanonicalScope.campaignId ||
      !cinemaCanonicalScope.titleContextId ||
      !cinemaCanonicalScope.footprintVersion
    ) {
      return;
    }
    try {
      const payload = await fetchJson(
        `/api/cinema-market-analysis/${resolvedAdvertiserId}/geocoding/retry`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildCinemaCanonicalScopeBody(cinemaCanonicalScope)),
        }
      );
      synchronizeCinemaCanonicalScope(payload);
      setCinemaPolicySaveState({
        status: payload?.pending > 0 ? "info" : "saved",
        message: String(payload?.note || "Geocoding state refreshed."),
      });
      await fetchCinemaFootprintState();
    } catch (err) {
      if (isAbortError(err)) return;
      setCinemaPolicySaveState({
        status: "error",
        message: String(err?.message || "Failed to refresh geocoding state."),
      });
    }
  };

  const cinemaImportMutationBusy =
    cinemaCsvUploadBusy || cinemaImportSaveState?.status === "saving";

  const handleCinemaCsvUploadClick = () => {
    if (cinemaImportMutationBusy) return;
    cinemaCsvInputRef.current?.click();
  };

  const handleCinemaCsvFileChange = async (event) => {
    const file = event.target.files?.[0];
    const requestScopeEpoch = advertiserScopeEpochRef.current;
    event.target.value = "";
    if (!file || cinemaImportMutationBusy) return;

    if (!isCinemaCsvFile(file)) {
      setCinemaCsvImport({
        ok: false,
        fileName: file.name || "Selected file",
        statusMessage: "Only .csv files can be uploaded for CinemaCloudWorks footprint import.",
        readinessLabel: "Missing Required Location Fields",
        headers: [],
        previewRows: [],
        metrics: {
          totalRows: 0,
          totalColumns: 0,
          theaterCount: 0,
          circuitCount: 0,
          marketCount: 0,
          coordinateReadyCount: 0,
          geocodingRequiredCount: 0,
          marketOnlyCount: 0,
          missingLocationCount: 0,
          cleanupNeededCount: 0,
        },
        marketDistribution: [],
        warnings: ["Only .csv files can be uploaded for CinemaCloudWorks footprint import."],
        analysisContext: null,
      });
      return;
    }

    setCinemaCsvUploadBusy(true);
    try {
      const text = await file.text();
      if (requestScopeEpoch !== advertiserScopeEpochRef.current) return;
      const parsed = parseCinemaCloudWorksCsvPreview(text, { fileName: file.name });
      setCinemaCsvImport(parsed);
      // Persist the parsed footprint under the canonical campaign/title scope
      // so it survives the session. The parsed rows are retained locally so a
      // scope correction retries ONLY the save — never a re-upload.
      if (parsed?.ok && Array.isArray(parsed.rows) && parsed.rows.length) {
        setPendingCinemaImport({ parsed, fileName: file.name });
        await persistCinemaCsvImport(parsed, file.name);
      }
    } catch (err) {
      if (
        isAbortError(err) ||
        requestScopeEpoch !== advertiserScopeEpochRef.current
      ) {
        return;
      }
      setCinemaCsvImport({
        ok: false,
        fileName: file.name || "CinemaCloudWorks.csv",
        statusMessage: String(err?.message || "Unable to read CSV file."),
        readinessLabel: "Missing Required Location Fields",
        headers: [],
        previewRows: [],
        metrics: {
          totalRows: 0,
          totalColumns: 0,
          theaterCount: 0,
          circuitCount: 0,
          marketCount: 0,
          coordinateReadyCount: 0,
          geocodingRequiredCount: 0,
          marketOnlyCount: 0,
          missingLocationCount: 0,
          cleanupNeededCount: 0,
        },
        marketDistribution: [],
        warnings: [String(err?.message || "Unable to read CSV file.")],
        analysisContext: null,
      });
    } finally {
      if (requestScopeEpoch === advertiserScopeEpochRef.current) {
        setCinemaCsvUploadBusy(false);
      }
    }
  };

  const analysisStatusMessage = useMemo(() => {
    if (!resolvedAdvertiserId) {
      return "Choose an advertiser to load a saved cinema analysis or run a fresh one.";
    }

    if (waitingForDefaultDatePreset) {
      return "Resolving the selected campaign and its last approved flight lineage before loading saved analysis.";
    }

    if (!cinemaCanonicalScope.campaignId) {
      return "Select a campaign to resolve its canonical Cinema title context.";
    }

    if (!hasFlightDatePreset) {
      return "Last approved flight is not available for the selected campaign yet.";
    }

    if (loadingAnalysis) {
      return "Running a fresh cinema analysis for the current advertiser and analysis window.";
    }

    if (currentScopedAnalysis && analysisIsStale) {
      return "Displayed report is from a previous selection. Click Run Analysis to refresh this advertiser and analysis window.";
    }

    if (analysisLoadedFromCache && currentScopedAnalysis) {
      return `Loaded the last saved Cinema analysis for campaign ${cinemaCanonicalScope.campaignId}, ${analysisWindowLabel}.`;
    }

    if (currentScopedAnalysis?.generatedAt) {
      return `Current report generated ${formatDateTime(currentScopedAnalysis.generatedAt)}.`;
    }

    return "No saved Cinema analysis exists for this canonical campaign/context/version and analysis window yet. Click Run Analysis to generate one.";
  }, [
    analysisIsStale,
    analysisLoadedFromCache,
    hasFlightDatePreset,
    analysisWindowLabel,
    cinemaCanonicalScope.campaignId,
    loadingAnalysis,
    resolvedAdvertiserId,
    currentScopedAnalysis,
    waitingForDefaultDatePreset,
  ]);

  const scoreEntries = useMemo(() => {
    const scorecard = currentScopedAnalysis?.scorecard || {};
    return [
      ["marketPosition", scorecard.marketPosition],
      ["demandMomentum", scorecard.demandMomentum],
      ["marketingEfficiency", scorecard.marketingEfficiency],
      ["projectionConfidence", scorecard.projectionConfidence],
    ].filter(([, entry]) => Boolean(entry));
  }, [currentScopedAnalysis]);

  const historyMax = useMemo(() => {
    const rows = currentScopedAnalysis?.salesDemandSignals?.historicalWindows || [];
    return rows.reduce((max, row) => Math.max(max, safeNum(row.ticketSalesUsd) || 0), 0) || 1;
  }, [currentScopedAnalysis]);

  const gaTopCities = currentScopedAnalysis?.marketingImpact?.gaOverlay?.overview?.topCities || [];
  const gaTopSources = currentScopedAnalysis?.marketingImpact?.gaOverlay?.overview?.topSources || [];
  const gaIntentEvents = currentScopedAnalysis?.marketingImpact?.gaOverlay?.ticketIntent?.observedEvents || [];
  // Static brand chrome for the cinema engine (no client-facing model picker).
  const cinemaEnginePresentation = useMemo(
    () => getAiProviderPresentation("chatgpt"),
    []
  );
  const selectedLocation = useMemo(() => {
    const id = Number(selectedLocationId);
    return locationsData.items.find((item) => Number(item.id) === id) || null;
  }, [locationsData.items, selectedLocationId]);
  const latestVisibleLocationSyncAt = useMemo(() => {
    let latest = 0;
    locationsData.items.forEach((location) => {
      const candidate = location?.updated_at || location?.created_at;
      const parsed = candidate ? new Date(candidate).getTime() : NaN;
      if (Number.isFinite(parsed) && parsed > latest) latest = parsed;
    });
    return latest ? new Date(latest).toISOString() : "";
  }, [locationsData.items]);
  const liveCinemas = useMemo(() => {
    const total = Number(locationClusters?.totals?.filteredRows || 0);
    return buildLiveCinemaCards(locationClusters.chainClusters, total);
  }, [locationClusters]);
  const cinemaCsvMetrics = cinemaCsvImport?.metrics || null;
  const cinemaCsvWarnings = cinemaCsvImport?.warnings || [];
  const cinemaCsvPreviewRows = cinemaCsvImport?.previewRows || [];
  const cinemaCsvAutomation = cinemaCsvImport?.automation || null;
  const cinemaCsvAutomationSummary = cinemaCsvAutomation?.summary || null;
  const cinemaCircuitChart = cinemaCsvAutomation ? getTopCircuitChartData(cinemaCsvAutomation, 8) : null;
  const cinemaMarketChart = cinemaCsvAutomation ? getTopMarketChartData(cinemaCsvAutomation, 8) : null;
  const cinemaReadinessChart = cinemaCsvAutomation ? getReadinessChartData(cinemaCsvAutomation) : null;
  const cinemaNextStep = cinemaCsvAutomation ? getRecommendedNextStep(cinemaCsvAutomation) : null;
  const cinemaFootprintHeadline = cinemaCsvAutomation ? getCinemaFootprintHeadline(cinemaCsvAutomation) : "";
  // Radius authority: once a canonical geo-fence policy is persisted for the
  // saved footprint, its default radius governs every prepared-targeting
  // figure. The session radius control only applies before a policy exists —
  // a local default must never override the saved footprint policy.
  const cinemaPersistedDefaultRadius = Number(
    currentCinemaFootprintState?.geoFencePolicy?.defaultRadius
  );
  const cinemaRadiusPolicyPersisted =
    Number.isFinite(cinemaPersistedDefaultRadius) && cinemaPersistedDefaultRadius > 0;
  const cinemaAuthoritativeRadiusMiles = cinemaRadiusPolicyPersisted
    ? cinemaPersistedDefaultRadius
    : cinemaDefaultRadiusMiles;
  const cinemaTargetingPrep = cinemaCsvAutomation
    ? buildCinemaTargetingPrep(cinemaCsvAutomation, {
        defaultRadiusMiles: cinemaAuthoritativeRadiusMiles,
      })
    : null;

  const handleCinemaCampaignScopeChange = useCallback(
    (campaignIdValue) => {
      advertiserScopeEpochRef.current += 1;
      abortAdvertiserScopedRequests();
      locationContextKeyRef.current = "";
      latestLocationsRequestKeyRef.current = "";
      latestLocationClustersRequestKeyRef.current = "";

      const campaignId = normalizePositiveInteger(campaignIdValue);
      const selected = cinemaContextCampaigns.find(
        (campaign) => getCinemaCampaignId(campaign) === campaignId
      );
      setCinemaFootprintScope({
        campaignId: campaignId ? String(campaignId) : "",
        titleContextId: "",
        footprintVersion: "",
        title: getCinemaCampaignTitle(selected),
      });

      // Campaign transitions clear every presentation/cache surface before
      // the existing scoped effects rehydrate the selected canonical tuple.
      setAnalysis(null);
      setLoadedAnalysisKey("");
      setAnalysisIsStale(false);
      setAnalysisLoadedFromCache(false);
      setLoadingAnalysis(false);
      setLocationsData(EMPTY_LOCATION_DATA);
      setLocationClusters(EMPTY_LOCATION_CLUSTERS);
      setLoadingLocations(false);
      setLoadingLocationClusters(false);
      setSelectedLocationId(null);
      const preserveScopeCorrectionImport = Boolean(
        pendingCinemaImport &&
          CINEMA_RETRYABLE_SCOPE_IMPORT_CODES.has(cinemaImportSaveState?.code)
      );
      if (!preserveScopeCorrectionImport) {
        setCinemaCsvImport(null);
        setPendingCinemaImport(null);
        setCinemaImportSaveState(null);
      }
      setCinemaSourceState(null);
      setCinemaFootprintState(null);
      setCinemaPolicyDraft({
        minimumRadius: "3",
        maximumRadius: "5",
        defaultRadius: "5",
      });
      setCinemaPolicySaveState(null);
      setError("");
      setLocationError("");
    },
    [
      abortAdvertiserScopedRequests,
      cinemaContextCampaigns,
      cinemaImportSaveState?.code,
      pendingCinemaImport,
    ]
  );

  // ---- Cinema Analytics workspace modules ---------------------------------
  // Every module below renders exclusively from cinemaFootprintAnalytics (the
  // canonical governed-footprint model) plus the existing lifecycle state.
  // Absent data renders the governed empty treatment — no placeholder venues,
  // no fabricated geography, no decorative percentages.

  const roundedFootprintPercent = (percentage) =>
    percentage === null || percentage === undefined
      ? undefined
      : Math.round(percentage * 10) / 10;

  const renderCinemaWorkspaceSectionHeader = (kicker, title, subtitle) => (
    <div className="cinema-analysis-workspace-section-header">
      <div className="cinema-analysis-section-kicker">{kicker}</div>
      <h3 className="cinema-analysis-section-title">{title}</h3>
      {subtitle ? (
        <p className="cinema-analysis-workspace-section-subtitle">{subtitle}</p>
      ) : null}
    </div>
  );

  const renderCinemaWorkspaceEmptyState = (message) => (
    <div className="cinema-chart-empty">{message}</div>
  );

  const renderCinemaGeographicFootprintSection = () => {
    const model = cinemaFootprintAnalytics;
    const gridStates = model.states.filter((group) => group.tile);
    const offGridStates = model.states.filter(
      (group) => !group.tile && !group.isUnspecified
    );
    const unspecifiedState = model.states.find((group) => group.isUnspecified);
    const maxStateCount = gridStates.reduce(
      (max, group) => Math.max(max, group.cinemaCount),
      0
    );
    return (
      <section
        className="cinema-analysis-section cinema-analysis-workspace-section cinema-geo-footprint-section"
        aria-label="Geographic footprint"
      >
        {renderCinemaWorkspaceSectionHeader(
          "Where the campaign plays",
          "Geographic Footprint",
          model.hasFootprint
            ? `${formatInteger(model.totalCinemas)} governed cinemas across ${formatInteger(
                model.distinctStateCount
              )} ${model.distinctStateCount === 1 ? "state" : "states"}`
            : null
        )}
        {model.hasFootprint ? (
          <>
            <CinemaGeographicTileGrid
              tiles={cinemaGeographicTiles}
              columns={US_STATE_TILE_GRID_COLS}
              rows={US_STATE_TILE_GRID_ROWS}
              maxCinemaCount={maxStateCount}
              ariaLabel={`Cinema count by U.S. state for the governed footprint: ${formatInteger(
                model.totalCinemas
              )} cinemas across ${formatInteger(
                model.distinctStateCount
              )} states. Each governed state opens its exhibitor breakdown.`}
            />
            {offGridStates.length ? (
              <div className="cinema-geo-offgrid">
                <span className="cinema-geo-offgrid-label">
                  Outside the U.S. state grid
                </span>
                {offGridStates.map((group) => (
                  <span key={group.key} className="cinema-analysis-hero-band-chip">
                    {group.label} · {formatInteger(group.cinemaCount)}
                  </span>
                ))}
              </div>
            ) : null}
            {unspecifiedState ? (
              <p className="cinema-geo-note">
                {formatInteger(unspecifiedState.cinemaCount)} governed{" "}
                {unspecifiedState.cinemaCount === 1 ? "venue carries" : "venues carry"}{" "}
                no recorded state; they stay in every total and are labeled
                Unspecified in the breakdown below.
              </p>
            ) : null}
            <p className="cinema-geo-note">
              {model.geocode.withCoordinates === 0
                ? "No governed venue coordinates are recorded for this footprint yet, so geography is presented at state level — nothing is plotted from inferred locations. Recorded coordinates activate venue-level mapping automatically."
                : `${formatInteger(model.geocode.withCoordinates)} of ${formatInteger(
                    model.totalCinemas
                  )} governed venues carry recorded coordinates.`}
            </p>
          </>
        ) : (
          renderCinemaWorkspaceEmptyState(
            "No governed cinema footprint is saved for this campaign scope yet, so no geography is shown. Upload the campaign's theater footprint to populate the geographic view."
          )
        )}
      </section>
    );
  };

  const renderCinemaGeographicBreakdownSection = () => {
    const model = cinemaFootprintAnalytics;
    return (
      <section
        className="cinema-analysis-section cinema-analysis-workspace-section cinema-geo-breakdown-section"
        aria-label="Geographic breakdown"
      >
        {renderCinemaWorkspaceSectionHeader(
          "Footprint share by geography",
          "Geographic Breakdown",
          model.hasFootprint ? model.denominatorLabel : null
        )}
        {/* Two geographies side by side: the state chart answers "how wide
            is this footprint", the city chart "where does it actually
            concentrate". Splitting them also halves the state chart's
            left-to-right scan, which at full width ran the eye across the
            whole panel for a two-letter label and a two-digit count. */}
        {model.hasFootprint ? (
          <div className="cinema-geo-breakdown-grid">
            <CinemaInsightChartCard
              title="Cinemas by State"
              description={model.denominatorLabel}
            >
              <CinemaHorizontalBarChart
                data={{
                  items: model.states.map((group) => ({
                    label: group.label,
                    value: group.cinemaCount,
                    percent: roundedFootprintPercent(group.percentage),
                    isOther: group.isUnspecified,
                  })),
                }}
              />
            </CinemaInsightChartCard>
            <CinemaInsightChartCard
              title="Top Cities"
              description={model.denominatorLabel}
            >
              {/* City names are long where state abbreviations are two
                  characters. The shared bar label ellipsises by default, which
                  is right for "TX" and wrong for "Oklahoma City, OK" in a
                  halved column — so city rows wrap instead of truncating. */}
              <div className="cinema-geo-city-chart">
                <CinemaHorizontalBarChart
                  data={{
                    items: model.topCities.map((group) => ({
                      label: group.label,
                      value: group.cinemaCount,
                      percent: roundedFootprintPercent(group.percentage),
                    })),
                  }}
                  emptyMessage="City concentration populates once a governed footprint carries city data."
                />
              </div>
              {model.cityCoverage.hiddenCities > 0 ? (
                <p className="cinema-geo-remainder">
                  {`+${formatInteger(model.cityCoverage.hiddenCities)} more ${
                    model.cityCoverage.hiddenCities === 1 ? "city" : "cities"
                  } · ${formatInteger(model.cityCoverage.hiddenCinemas)} ${
                    model.cityCoverage.hiddenCinemas === 1 ? "cinema" : "cinemas"
                  }`}
                </p>
              ) : null}
            </CinemaInsightChartCard>
          </div>
        ) : (
          renderCinemaWorkspaceEmptyState(
            "State-level shares populate once a governed footprint is saved for this campaign scope."
          )
        )}
      </section>
    );
  };

  const renderCinemaExhibitorConcentrationSection = () => {
    const model = cinemaFootprintAnalytics;
    return (
      <section
        className="cinema-analysis-section cinema-analysis-workspace-section cinema-exhibitor-concentration-section"
        aria-label="Exhibitor concentration"
      >
        {renderCinemaWorkspaceSectionHeader(
          "Who controls the footprint",
          "Exhibitor Concentration",
          model.hasFootprint ? model.denominatorLabel : null
        )}
        {model.hasFootprint ? (
          <>
            <div className="cinema-exhibitor-insight-strip">
              {model.topExhibitor ? (
                <div className="cinema-exhibitor-insight">
                  <span className="cinema-exhibitor-insight-label">
                    Largest Exhibitor
                  </span>
                  <span className="cinema-exhibitor-insight-value cinema-analysis-numeric">
                    {model.topExhibitor.label} ·{" "}
                    {formatFootprintPercentage(model.topExhibitor.percentage)}
                  </span>
                </div>
              ) : null}
              {model.topThreeShare !== null ? (
                <div className="cinema-exhibitor-insight">
                  <span className="cinema-exhibitor-insight-label">
                    Top 3 Exhibitors
                  </span>
                  <span className="cinema-exhibitor-insight-value cinema-analysis-numeric">
                    {formatFootprintPercentage(model.topThreeShare)} of campaign
                    footprint
                  </span>
                </div>
              ) : null}
            </div>
            {/* Two readings of one aggregation: the donut answers "how is the
                footprint shaped", the bars answer "how do exhibitors rank".
                They stack rather than compress on narrow viewports. */}
            <div className="cinema-exhibitor-distribution-grid">
              <CinemaInsightChartCard
                title="Exhibitor Distribution"
                description={model.denominatorLabel}
              >
                <div className="cinema-exhibitor-distribution-body">
                  <CinemaExhibitorDonutChart
                    distribution={cinemaExhibitorDistribution}
                  />
                  <CinemaExhibitorLegend
                    distribution={cinemaExhibitorDistribution}
                  />
                </div>
              </CinemaInsightChartCard>
              <CinemaInsightChartCard
                title="Share of Footprint by Exhibitor"
                description={model.denominatorLabel}
              >
                <CinemaHorizontalBarChart
                  data={{
                    // Same aggregation, same artwork resolver as the legend
                    // beside it. Counts and percentages are untouched — only
                    // the image fields are added.
                    items: model.exhibitors.map((group) => {
                      const artwork = resolveCinemaExhibitorArtwork(group);
                      return {
                        label: group.label,
                        value: group.cinemaCount,
                        percent: roundedFootprintPercent(group.percentage),
                        isOther: group.isIndependent,
                        image: artwork.src,
                        imageIsFallback: artwork.isFallback,
                      };
                    }),
                  }}
                />
              </CinemaInsightChartCard>
            </div>
          </>
        ) : (
          renderCinemaWorkspaceEmptyState(
            "Exhibitor concentration populates once a governed footprint is saved for this campaign scope."
          )
        )}
      </section>
    );
  };

  const renderCinemaMarketBreakdownSection = () => {
    const model = cinemaFootprintAnalytics;
    // Market identity is optional source data: the module renders only when
    // the governed footprint actually carries market/DMA values — a footprint
    // without them gets no fabricated market view.
    if (!model.hasFootprint || !model.marketCoverage.withMarket) return null;
    return (
      <section
        className="cinema-analysis-section cinema-analysis-workspace-section cinema-market-breakdown-section"
        aria-label="Market breakdown"
      >
        {renderCinemaWorkspaceSectionHeader(
          "Recorded market identity",
          "Market Breakdown",
          `${formatInteger(model.marketCoverage.withMarket)} of ${formatInteger(
            model.totalCinemas
          )} governed cinemas carry a recorded market · percentages keep the full-footprint denominator`
        )}
        <CinemaInsightChartCard
          title="Cinemas by Market"
          description={model.denominatorLabel}
        >
          <CinemaHorizontalBarChart
            data={{
              items: model.markets.map((group) => ({
                label: group.label,
                value: group.cinemaCount,
                percent: roundedFootprintPercent(group.percentage),
              })),
            }}
          />
        </CinemaInsightChartCard>
      </section>
    );
  };

  const renderCinemaRosterSortHeader = (key, label, cellClass) => (
    <th
      scope="col"
      className={cellClass}
      aria-sort={
        cinemaRosterSort.key === key
          ? cinemaRosterSort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
    >
      <button
        type="button"
        className="cinema-roster-sort-button"
        onClick={() => handleCinemaRosterSort(key)}
      >
        {label}
        <span className="cinema-roster-sort-indicator" aria-hidden="true">
          {cinemaRosterSort.key === key
            ? cinemaRosterSort.direction === "asc"
              ? "▲"
              : "▼"
            : "↕"}
        </span>
      </button>
    </th>
  );

  const renderCinemaRosterSection = () => {
    const model = cinemaFootprintAnalytics;
    const hasActiveRosterFilters = Boolean(
      cinemaRosterSearch || cinemaRosterExhibitorFilter || cinemaRosterStateFilter
    );
    return (
      <section
        className="cinema-analysis-section cinema-analysis-workspace-section cinema-roster-section"
        aria-label="Cinemas in this campaign"
      >
        {renderCinemaWorkspaceSectionHeader(
          "Complete governed list",
          "Cinemas in this Campaign",
          model.hasFootprint
            ? `${formatInteger(model.totalCinemas)} governed cinemas · search, filters, and sorting never change the campaign totals`
            : null
        )}
        {model.hasFootprint ? (
          <>
            <div className="cinema-roster-controls" role="group" aria-label="Cinema list filters">
              <input
                type="search"
                className="cinema-roster-search"
                aria-label="Search cinemas by name, exhibitor, address, city, state, or ZIP"
                placeholder="Search cinemas…"
                value={cinemaRosterSearch}
                onChange={(event) => setCinemaRosterSearch(event.target.value)}
              />
              <select
                className="cinema-roster-filter"
                aria-label="Filter by exhibitor"
                value={cinemaRosterExhibitorFilter}
                onChange={(event) => setCinemaRosterExhibitorFilter(event.target.value)}
              >
                <option value="">All exhibitors</option>
                {model.exhibitors.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.label} ({formatInteger(group.cinemaCount)})
                  </option>
                ))}
              </select>
              <select
                className="cinema-roster-filter"
                aria-label="Filter by state"
                value={cinemaRosterStateFilter}
                onChange={(event) => setCinemaRosterStateFilter(event.target.value)}
              >
                <option value="">All states</option>
                {model.states.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.label} ({formatInteger(group.cinemaCount)})
                  </option>
                ))}
              </select>
              {hasActiveRosterFilters ? (
                <button
                  type="button"
                  className="cinema-roster-reset"
                  onClick={() => {
                    setCinemaRosterSearch("");
                    setCinemaRosterExhibitorFilter("");
                    setCinemaRosterStateFilter("");
                  }}
                >
                  Reset
                </button>
              ) : null}
            </div>
            <p className="cinema-roster-count" role="status">
              Showing {formatInteger(cinemaRosterVenues.length)} of{" "}
              {formatInteger(model.totalCinemas)} governed cinemas
            </p>
            <div className="cinema-location-table-scroll cinema-footprint-locations-scroll">
              <table
                className={`cinema-footprint-locations-table${
                  cinemaRosterShowsMarketColumn
                    ? " cinema-footprint-locations-table--with-market"
                    : ""
                }`}
              >
                <thead>
                  <tr>
                    {renderCinemaRosterSortHeader("cinema", "Cinema", "cinema-cell-text")}
                    {renderCinemaRosterSortHeader("exhibitor", "Exhibitor", "cinema-cell-tag")}
                    <th scope="col" className="cinema-cell-text">Address</th>
                    {renderCinemaRosterSortHeader("city", "City", "cinema-cell-text")}
                    {renderCinemaRosterSortHeader("state", "State", "cinema-cell-tag")}
                    {renderCinemaRosterSortHeader("zip", "ZIP", "cinema-cell-tag")}
                    {cinemaRosterShowsMarketColumn
                      ? renderCinemaRosterSortHeader("market", "Market", "cinema-cell-tag")
                      : null}
                    {renderCinemaRosterSortHeader("status", "Status", "cinema-cell-tag")}
                    {renderCinemaRosterSortHeader("radius", "Radius (mi)", "cinema-cell-number")}
                  </tr>
                </thead>
                <tbody>
                  {cinemaRosterVenues.map((venue) => (
                    <tr key={venue.identityKey}>
                      <th scope="row" className="cinema-cell-text" data-label="Cinema">
                        {venue.cinemaName}
                      </th>
                      <td className="cinema-cell-tag" data-label="Exhibitor">
                        {venue.exhibitorLabel}
                      </td>
                      <td className="cinema-cell-text" data-label="Address">
                        {venue.addressLine1 || "—"}
                      </td>
                      <td className="cinema-cell-text" data-label="City">
                        {venue.city || "—"}
                      </td>
                      <td className="cinema-cell-tag" data-label="State">
                        {venue.stateLabel}
                      </td>
                      <td className="cinema-cell-tag cinema-analysis-numeric" data-label="ZIP">
                        {venue.postalCode || "—"}
                      </td>
                      {cinemaRosterShowsMarketColumn ? (
                        <td className="cinema-cell-tag" data-label="Market">
                          {venue.marketLabel || "—"}
                        </td>
                      ) : null}
                      <td className="cinema-cell-tag" data-label="Status">
                        {venue.geoFenceReady
                          ? "GEO-FENCE READY"
                          : venue.geocodeStatus === "pending"
                          ? "GEOCODING PENDING"
                          : venue.geocodeStatus.toUpperCase()}
                      </td>
                      <td className="cinema-cell-number" data-label="Radius (mi)">
                        <input
                          type="number"
                          min={currentCinemaFootprintState.geoFencePolicy.minimumRadius}
                          max={currentCinemaFootprintState.geoFencePolicy.maximumRadius}
                          step="0.5"
                          className="cinema-footprint-override-input"
                          aria-label={`Execution radius override for ${venue.cinemaName}`}
                          placeholder={
                            venue.executionRadiusMiles === null
                              ? ""
                              : String(venue.executionRadiusMiles)
                          }
                          defaultValue={
                            venue.radiusOverrideMiles === null
                              ? ""
                              : String(venue.radiusOverrideMiles)
                          }
                          onBlur={(event) => {
                            const next = event.target.value;
                            const current =
                              venue.radiusOverrideMiles === null
                                ? ""
                                : String(venue.radiusOverrideMiles);
                            if (next !== current) {
                              void handleFootprintRadiusOverride(venue.id, next);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                  {!cinemaRosterVenues.length ? (
                    <tr>
                      <td
                        colSpan={cinemaRosterShowsMarketColumn ? 9 : 8}
                        className="cinema-cell-text cinema-roster-empty-row"
                        data-label="Result"
                      >
                        No cinemas match the current filters. The governed footprint
                        still holds {formatInteger(model.totalCinemas)} cinemas.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          renderCinemaWorkspaceEmptyState(
            "The complete cinema list appears here once a governed footprint is saved for this campaign scope."
          )
        )}
      </section>
    );
  };

  // The governance panel that sits directly under the "Reapproval required ·
  // deployment blocked" chip. It states the block, names the authority, and
  // offers exactly one intentional way forward — never an automatic approval.
  const renderCinemaReapprovalPanel = () => {
    const cta = cinemaReapprovalCta;
    if (!cta.visible) return null;
    const busy = cta.submitting;
    return (
      <div
        className="cinema-reapproval-panel"
        role="group"
        aria-label="Cinema footprint reapproval"
      >
        <div className="cinema-reapproval-panel-copy">
          <div className="cinema-reapproval-panel-headline">{cta.headline}</div>
          <p className="cinema-reapproval-panel-body">{cta.body}</p>
          {cta.authorityNote ? (
            <p className="cinema-reapproval-panel-authority">{cta.authorityNote}</p>
          ) : null}
        </div>
        {cta.mode === CINEMA_REAPPROVAL_CTA_MODES.ACTION ? (
          cta.confirming ? (
            <div className="cinema-reapproval-panel-confirm" role="group">
              <p className="cinema-reapproval-panel-confirm-prompt">{cta.confirmPrompt}</p>
              <div className="cinema-reapproval-panel-actions">
                <button
                  type="button"
                  className="cinema-reapproval-panel-button cinema-reapproval-panel-button--approve"
                  onClick={handleCinemaReapprove}
                  disabled={busy}
                >
                  {busy ? "Approving…" : cta.confirmLabel}
                </button>
                <button
                  type="button"
                  className="cinema-reapproval-panel-button cinema-reapproval-panel-button--cancel"
                  onClick={() => setCinemaReapprovalConfirming(false)}
                  disabled={busy}
                >
                  {cta.cancelLabel}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="cinema-reapproval-panel-button cinema-reapproval-panel-button--review"
              onClick={() => {
                setCinemaReapprovalState(null);
                setCinemaReapprovalConfirming(true);
              }}
              disabled={busy}
            >
              {cta.ctaLabel}
            </button>
          )
        ) : cta.mode === CINEMA_REAPPROVAL_CTA_MODES.DEEP_LINK ? (
          <button
            type="button"
            className="cinema-reapproval-panel-button cinema-reapproval-panel-button--review"
            onClick={() => navigate(cta.deepLinkPath)}
          >
            {cta.ctaLabel}
          </button>
        ) : null}
        {cinemaReapprovalState ? (
          <p
            className={`cinema-reapproval-panel-status cinema-reapproval-panel-status--${cinemaReapprovalState.status}`}
            role="status"
          >
            {cinemaReapprovalState.message}
          </p>
        ) : null}
      </div>
    );
  };

  const renderCinemaCloudWorksUploadPanel = () => (
    // One structural shell for every footprint state. The panel used to
    // switch composition once a footprint, a saved row count or a parse
    // preview existed, so selecting another campaign rebuilt the workspace
    // — controls moved between columns and rows on the way through the
    // cleared intermediate state. Campaign evidence populates this shell; it
    // never reshapes it.
    <section className="cinema-location-upload-panel" aria-label="Footprint upload">
      {/* The workspace header directly above already asks for the governed
          footprint, and the readiness cards below already state what geocoding
          resolves. Two more sentences here said the same things a third time,
          so the heading carries the section and the labelled controls carry
          the instruction. */}
      <div className="cinema-location-upload-copy">
        <div className="cinema-analysis-section-kicker">Location Intelligence Input</div>
        <h2 className="cinema-analysis-section-title cinema-footprint-group-title">
          Footprint Source
        </h2>
      </div>
      <div className="cinema-location-upload-actions">
        <div className="cinema-footprint-scope">
          <label className="cinema-footprint-scope-label" htmlFor="cinema-footprint-campaign-select">
            Campaign
          </label>
          <select
            id="cinema-footprint-campaign-select"
            className="cinema-footprint-scope-select"
            value={cinemaFootprintScope.campaignId}
            onChange={(event) => handleCinemaCampaignScopeChange(event.target.value)}
            disabled={loadingPlanCampaigns || cinemaImportMutationBusy}
          >
            <option value="">Select a title-scoped campaign…</option>
            {cinemaContextCampaigns.map((campaign) => (
              <option
                key={getCinemaCampaignId(campaign)}
                value={String(getCinemaCampaignId(campaign))}
              >
                {(getCinemaCampaignName(campaign) ||
                  `Campaign ${getCinemaCampaignId(campaign)}`) +
                  (campaign.status ? ` — ${campaign.status}` : "")}
              </option>
            ))}
          </select>
          {/* Film identity is READ here, not asked for. A bound title context
              owns it; failing that the backend resolves it from the governed
              tenant identity (or an adoptable unbound context, which is the row
              a save would reuse — so nothing here can create a duplicate). The
              manual control survives for the one case none of that covers: a
              genuinely new campaign whose film identity is unknown. Reads still
              bind to IDs either way — this is presentation and save-title only. */}
          {cinemaCanonicalScope.titleContextId ? (
            <p className="cinema-footprint-scope-resolved">
              {cinemaResolvedFilmTitle || "Untitled footprint"}
              {" · Footprint v"}
              {cinemaCanonicalScope.footprintVersion || "pending"}
            </p>
          ) : cinemaCanonicalIdentityPending ? (
            /* The reads have not answered for this campaign yet, so the page
               does not know whether it owns a canonical title. It says so
               rather than presenting the first-import control, which would
               claim there is no context before anything has looked. */
            <p
              className="cinema-footprint-scope-resolved cinema-footprint-scope-resolved--pending"
              role="status"
            >
              Resolving footprint identity…
            </p>
          ) : cinemaResolvedFilmTitle ? (
            <p className="cinema-footprint-scope-resolved">
              {"Film · "}
              {cinemaResolvedFilmTitle}
            </p>
          ) : cinemaIdentityUnavailable ? (
            /* The reads answered with a failure, so nothing here is known. The
               first-import control would assert that this campaign owns no
               title context, which is exactly what was never established. */
            <p
              className="cinema-footprint-scope-resolved cinema-footprint-scope-resolved--pending"
              role="status"
            >
              Footprint identity unavailable — reload to retry
            </p>
          ) : (
            <input
              className="cinema-footprint-scope-title"
              aria-label="Film title for this footprint"
              placeholder="Film title"
              value={cinemaFootprintScope.title}
              disabled={cinemaImportMutationBusy}
              onChange={(event) =>
                setCinemaFootprintScope((previous) => ({ ...previous, title: event.target.value }))
              }
            />
          )}
        </div>
        <input
          ref={cinemaCsvInputRef}
          type="file"
          accept=".csv,text/csv"
          className="cinema-location-upload-input"
          onChange={handleCinemaCsvFileChange}
          disabled={cinemaImportMutationBusy}
        />
        {/* The control keeps its own labelled column so the three unconditional
            controls (scope, upload, session file) present one label rhythm
            instead of a bare button floating between two labelled fields. The
            product term stays on the accessible name — the visible button says
            what it does. */}
        <div className="cinema-footprint-upload-control">
          <div className="cinema-footprint-field-label">Footprint File</div>
          <button
            type="button"
            className="cinema-analysis-primary-button cinema-location-upload-button"
            onClick={handleCinemaCsvUploadClick}
            disabled={cinemaImportMutationBusy}
            aria-label="Upload footprint CSV"
          >
            {cinemaCsvUploadBusy ? "LOADING CSV..." : "UPLOAD CSV"}
          </button>
        </div>
        <div className="cinema-location-upload-status" role="status">
          <span className="cinema-footprint-field-label">Session File</span>
          <span className="cinema-footprint-status-value">
            {cinemaCsvImport?.statusMessage || "No file loaded"}
          </span>
        </div>
        {cinemaImportSaveState ? (
          <div
            className={`cinema-location-upload-save-status${
              cinemaImportSaveState.status === "error"
                ? " cinema-location-upload-save-status--error"
                : ""
            }`}
          >
            {cinemaImportSaveState.message}
          </div>
        ) : null}
        {pendingCinemaImport && cinemaImportSaveState?.status === "error" ? (
          <button
            type="button"
            className="cinema-analysis-primary-button cinema-location-upload-button"
            onClick={() =>
              void persistCinemaCsvImport(pendingCinemaImport.parsed, pendingCinemaImport.fileName)
            }
          >
            SAVE FOOTPRINT
          </button>
        ) : null}
        {currentCinemaSourceState ? (
          <div className="cinema-location-upload-saved-state">
            <div className="cinema-footprint-group-head">
              <div className="cinema-analysis-section-kicker">Footprint Dataset</div>
              <p className="cinema-footprint-group-note">{currentCinemaSourceState.message}</p>
            </div>
            {currentCinemaSourceState.hasCinemaData ? (
              <div className="cinema-footprint-meta-grid" aria-label="Saved dataset provenance">
                <div className="cinema-footprint-meta-item">
                  <span className="cinema-footprint-meta-label">Rows loaded</span>
                  <span className="cinema-footprint-meta-value cinema-analysis-numeric">
                    {formatInteger(currentCinemaSourceState.rowsLoaded)}
                  </span>
                </div>
                {currentCinemaSourceState.lastImportAt ? (
                  <div className="cinema-footprint-meta-item">
                    <span className="cinema-footprint-meta-label">Last import</span>
                    <span className="cinema-footprint-meta-value">
                      {formatDateTime(currentCinemaSourceState.lastImportAt)}
                    </span>
                  </div>
                ) : null}
                {currentCinemaSourceState.sourceFilename ? (
                  <div className="cinema-footprint-meta-item cinema-footprint-meta-item--wide">
                    <span className="cinema-footprint-meta-label">Source</span>
                    <span className="cinema-footprint-meta-value">
                      {currentCinemaSourceState.sourceFilename}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="cinema-footprint-group-note">
                No cinema footprint data is saved for this campaign context yet.
              </p>
            )}
          </div>
        ) : null}
        {currentCinemaFootprintState?.exists ? (
          <div className="cinema-footprint-lifecycle" aria-label="Saved cinema footprint lifecycle">
            <div className="cinema-footprint-group-head">
              <div className="cinema-analysis-section-kicker">Saved Footprint</div>
              <h3 className="cinema-footprint-group-title">Governed Footprint</h3>
              <p className="cinema-footprint-group-value">
                {currentCinemaFootprintState.titleContext.title}
                {" · Footprint v"}
                {currentCinemaFootprintState.titleContext.footprintVersion}
              </p>
            </div>
            {/* The three counts are the numbers an operator scans first, so they
                wear the canonical overview-card treatment the rest of this page
                already uses rather than reading as three more sentences. Each
                keeps a written detail line: the value never depends on colour
                alone to say what state it describes. */}
            <div className="cinema-footprint-stat-grid" aria-label="Governed footprint readiness">
              <div className="cinema-analysis-overview-card">
                <span className="cinema-analysis-overview-label">Theaters</span>
                <span className="cinema-analysis-overview-value cinema-analysis-numeric">
                  {formatInteger(currentCinemaFootprintState.counts.active)}
                </span>
                <span className="cinema-analysis-overview-detail">Saved to this context</span>
              </div>
              <div className="cinema-analysis-overview-card">
                <span className="cinema-analysis-overview-label">Geocoding Pending</span>
                <span className="cinema-analysis-overview-value cinema-analysis-numeric">
                  {formatInteger(currentCinemaFootprintState.counts.geocodePending)}
                </span>
                <span className="cinema-analysis-overview-detail">Awaiting coordinates</span>
              </div>
              <div className="cinema-analysis-overview-card">
                <span className="cinema-analysis-overview-label">Geo-Fence Ready</span>
                <span className="cinema-analysis-overview-value cinema-analysis-numeric">
                  {formatInteger(currentCinemaFootprintState.counts.geoFenceReady)}
                </span>
                <span className="cinema-analysis-overview-detail">Deployable to a connector</span>
              </div>
            </div>
            <div className="cinema-footprint-policy" aria-label="Geo-fence radius policy">
              <div className="cinema-footprint-group-head">
                <div className="cinema-analysis-section-kicker">Geo-Fence Policy</div>
                <h3 className="cinema-footprint-group-title">Location Readiness</h3>
                <p className="cinema-footprint-group-value">
                  Radius in {currentCinemaFootprintState.geoFencePolicy.unit.toLowerCase()}
                </p>
              </div>
              <div className="cinema-footprint-policy-inputs">
                <label>
                  Min
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    aria-label="Minimum geo-fence radius in miles"
                    value={cinemaPolicyDraft?.minimumRadius ?? ""}
                    onChange={(event) =>
                      setCinemaPolicyDraft((previous) => ({
                        ...(previous || {}),
                        minimumRadius: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Max
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    aria-label="Maximum geo-fence radius in miles"
                    value={cinemaPolicyDraft?.maximumRadius ?? ""}
                    onChange={(event) =>
                      setCinemaPolicyDraft((previous) => ({
                        ...(previous || {}),
                        maximumRadius: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Default
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    aria-label="Default geo-fence execution radius in miles"
                    value={cinemaPolicyDraft?.defaultRadius ?? ""}
                    onChange={(event) =>
                      setCinemaPolicyDraft((previous) => ({
                        ...(previous || {}),
                        defaultRadius: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  type="button"
                  className="cinema-analysis-primary-button cinema-footprint-policy-save"
                  onClick={() => void handleSaveGeoFencePolicy()}
                >
                  SAVE POLICY
                </button>
                <button
                  type="button"
                  className="cinema-analysis-primary-button cinema-footprint-policy-save"
                  onClick={() => void handleRefreshGeocoding()}
                >
                  REFRESH GEOCODING
                </button>
              </div>
              <p className="cinema-footprint-scope-hint">
                Approved range applies to deterministic theater targeting. Per-theater overrides
                must remain within the approved bounds.
              </p>
              <p className="cinema-footprint-scope-hint">
                Unresolved locations are never sent to a connector.
              </p>
              {cinemaPolicySaveState ? (
                <div
                  className={`cinema-location-upload-save-status${
                    cinemaPolicySaveState.status === "error"
                      ? " cinema-location-upload-save-status--error"
                      : ""
                  }`}
                >
                  {cinemaPolicySaveState.message}
                </div>
              ) : null}
            </div>
            <p className="cinema-footprint-scope-hint">
              Per-theater radius overrides live in the Cinemas in this Campaign module below.
            </p>
          </div>
        ) : null}
      </div>

      {cinemaCsvImport ? (
        <div className="cinema-location-upload-results">
          {/* 1. Automated readiness summary — the one-line verdict */}
          <div className="cinema-location-readiness-summary">
            <div className="cinema-location-upload-readiness">
              <Badge tone={cinemaCsvImport.ok ? (cinemaCsvImport.readinessLabel === "Location Intelligence Ready" ? "good" : cinemaCsvImport.readinessLabel === "Missing Required Location Fields" ? "muted" : "warning") : "muted"}>
                {cinemaCsvImport.readinessLabel}
              </Badge>
              <span>{cinemaCsvImport.fileName}</span>
            </div>
            {cinemaFootprintHeadline ? (
              <p className="cinema-analysis-score-copy">{cinemaFootprintHeadline}</p>
            ) : null}
          </div>

          {/* 2. Key metrics row — six high-value counts only */}
          <div className="cinema-location-upload-summary-grid">
            <MetricCard label="Theaters" value={formatInteger(cinemaCsvMetrics?.theaterCount)} hint={`${formatInteger(cinemaCsvMetrics?.totalRows)} imported rows`} />
            <MetricCard label="Circuits" value={formatInteger(cinemaCsvMetrics?.circuitCount)} hint="Detected exhibitor groups" />
            <MetricCard label="Markets" value={formatInteger(cinemaCsvMetrics?.marketCount)} hint="Market, DMA, state, or city signals" />
            <MetricCard
              label="Geo-Fence Ready"
              value={formatInteger(cinemaCsvAutomationSummary?.geofenceReadyCount)}
              hint={`Coordinates + resolved ${cinemaPolicyDraft?.minimumRadius || 3}–${
                cinemaPolicyDraft?.maximumRadius || 5
              } mi radius`}
            />
            <MetricCard label="Geocoding Needed" value={formatInteger(cinemaCsvAutomationSummary?.geocodingRequiredCount)} hint="Address rows awaiting coordinates" />
            <MetricCard label="Cleanup Needed" value={formatInteger(cinemaCsvAutomationSummary?.cleanupRequiredCount)} hint="Rows missing required identifiers" />
          </div>

          {/* 3. Interactive chart analytics — high-value insights first */}
          {cinemaCsvAutomationSummary ? (
            <div className="cinema-chart-grid">
              <CinemaInsightChartCard
                title="Location Readiness"
                description="Readiness of the uploaded footprint for geo-targeting preparation."
                badge={
                  <Badge tone={cinemaCsvAutomation.datasetReadiness === "Location Intelligence Ready" ? "good" : cinemaCsvAutomation.datasetReadiness === "Cleanup Required" || cinemaCsvAutomation.datasetReadiness === "Missing Required Location Fields" ? "muted" : "warning"}>
                    {cinemaCsvAutomation.datasetReadiness}
                  </Badge>
                }
              >
                <CinemaReadinessChart data={cinemaReadinessChart} />
              </CinemaInsightChartCard>

              <CinemaInsightChartCard
                title="Circuit Footprint"
                description="Top circuits by uploaded theater count."
              >
                <CinemaHorizontalBarChart
                  data={cinemaCircuitChart}
                  emptyMessage="Circuit data appears when circuit names are present."
                />
              </CinemaInsightChartCard>

              <CinemaInsightChartCard
                title="Market Concentration"
                description="Largest markets or regions identified from the cinema footprint."
              >
                <CinemaHorizontalBarChart
                  data={cinemaMarketChart}
                  emptyMessage="Market data appears when market, DMA, state, or city fields are present."
                />
              </CinemaInsightChartCard>

              <CinemaInsightChartCard title="Recommended Next Step">
                <div className="cinema-next-step">
                  <div className="cinema-next-step-headline">{cinemaNextStep?.step}</div>
                  <p className="cinema-analysis-score-copy">{cinemaNextStep?.detail}</p>
                </div>
                <div className="cinema-analysis-bullet-list">
                  <div className="cinema-analysis-bullet-item">Top circuit: {cinemaCircuitChart?.items?.[0]?.label || "--"}</div>
                  <div className="cinema-analysis-bullet-item">Largest market: {cinemaMarketChart?.items?.[0]?.label || "--"}</div>
                  <div className="cinema-analysis-bullet-item">Geo-fence ready: {cinemaReadinessChart?.readyPercent || 0}% of footprint</div>
                </div>
              </CinemaInsightChartCard>
            </div>
          ) : null}

          {/* 4. AI Analysis Context — preserved, condensed */}
          <div className="cinema-location-ai-context-card">
            <div className="cinema-analysis-section-kicker">AI Analysis Context</div>
            <div className="cinema-location-ai-summary-title">
              AI Analysis uses cinema footprint and location readiness data to identify market priorities, circuit concentration, geo-fence readiness, and activation considerations.
            </div>
            <div className="cinema-analysis-bullet-list">
              <div className="cinema-analysis-bullet-item">
                Footprint: {formatInteger(cinemaCsvMetrics?.theaterCount)} theaters · {formatInteger(cinemaCsvMetrics?.circuitCount)} circuits · {formatInteger(cinemaCsvMetrics?.marketCount)} markets.
              </div>
              <div className="cinema-analysis-bullet-item">
                Geo-fence readiness: {cinemaReadinessChart?.readyPercent || 0}% of footprint.
              </div>
              <div className="cinema-analysis-bullet-item">
                Recommended next step: {cinemaNextStep?.step || "--"}.
              </div>
              {cinemaTargetingPrep ? (
                <div className="cinema-analysis-bullet-item">
                  Prepared targeting: {formatInteger(cinemaTargetingPrep.geofenceTargets.length)} geo-fence · {formatInteger(cinemaTargetingPrep.cityTargets.length)} city · {formatInteger(cinemaTargetingPrep.marketTargets.length)} market targets at {formatInteger(cinemaTargetingPrep.defaultRadiusMiles)} mi.
                </div>
              ) : null}
            </div>
          </div>

          {/* 4b. Geo-targeting preparation — automated prep + human radius control */}
          {cinemaTargetingPrep ? (
            <div className="cinema-location-targeting-prep">
              <div className="cinema-location-targeting-prep-header">
                <div className="cinema-location-targeting-prep-heading">
                  <div className="cinema-analysis-section-kicker">Geo-Targeting Preparation</div>
                  <div className="cinema-analysis-score-copy">
                    Cphere prepared geo-targeting parameters from the uploaded footprint.
                  </div>
                </div>
                <label className="cinema-location-radius-control">
                  <span className="cinema-analysis-section-kicker">Default Radius</span>
                  <span className="cinema-location-radius-input-wrap">
                    <input
                      type="number"
                      min={cinemaPolicyDraft?.minimumRadius || 3}
                      max={cinemaPolicyDraft?.maximumRadius || 5}
                      step="0.5"
                      className="cinema-analysis-text-input cinema-location-radius-input"
                      value={cinemaAuthoritativeRadiusMiles}
                      onChange={(event) => setCinemaDefaultRadiusMiles(event.target.value)}
                      onBlur={(event) =>
                        setCinemaDefaultRadiusMiles(normalizeTargetingRadiusMiles(event.target.value))
                      }
                      disabled={cinemaRadiusPolicyPersisted}
                      aria-label="Default geo-fence radius in miles"
                    />
                    <span className="cinema-location-radius-unit">miles</span>
                  </span>
                  <span className="cinema-analysis-score-copy">
                    {cinemaRadiusPolicyPersisted
                      ? "Governed by the saved geo-fence policy. Adjust it in the radius policy editor."
                      : "Applied to geo-fence-ready locations."}
                  </span>
                </label>
              </div>

              <div className="cinema-location-upload-summary-grid">
                <MetricCard label="Geo-Fence Targets" value={formatInteger(cinemaTargetingPrep.geofenceTargets.length)} hint={`Prepared · ${formatInteger(cinemaTargetingPrep.defaultRadiusMiles)} mi radius`} />
                <MetricCard label="City Targets" value={formatInteger(cinemaTargetingPrep.cityTargets.length)} hint="Prepared" />
                <MetricCard label="State Targets" value={formatInteger(cinemaTargetingPrep.stateTargets.length)} hint="Prepared" />
                <MetricCard label="ZIP Targets" value={formatInteger(cinemaTargetingPrep.zipTargets.length)} hint="Prepared" />
                <MetricCard label="Market Targets" value={formatInteger(cinemaTargetingPrep.marketTargets.length)} hint="Prepared" />
                <MetricCard label="Geocoding Queue" value={formatInteger(cinemaTargetingPrep.geocodingQueue.length)} hint="Requires geocoding" />
                <MetricCard label="Cleanup Queue" value={formatInteger(cinemaTargetingPrep.cleanupQueue.length)} hint="Requires cleanup" />
                <MetricCard label="Default Radius" value={`${formatInteger(cinemaTargetingPrep.defaultRadiusMiles)} mi`} hint="Ready for review" />
              </div>
            </div>
          ) : null}

          {/* 5. Expandable details — collapsed by default to reduce density */}
          {cinemaCsvWarnings.length ? (
            <details className="cinema-location-details">
              <summary>View Data Notes ({cinemaCsvWarnings.length})</summary>
              <div className="cinema-analysis-bullet-list">
                {cinemaCsvWarnings.map((warning) => (
                  <div key={warning} className="cinema-analysis-bullet-item">{warning}</div>
                ))}
              </div>
            </details>
          ) : null}

          {cinemaCsvAutomation?.circuitBreakdown?.length ? (
            <details className="cinema-location-details">
              <summary>View All Circuits</summary>
              <div className="cinema-analysis-bullet-list">
                {cinemaCsvAutomation.circuitBreakdown.map((circuit) => (
                  <div key={circuit.label} className="cinema-analysis-bullet-item">
                    {circuit.label}: {formatInteger(circuit.count)} theaters
                  </div>
                ))}
              </div>
            </details>
          ) : null}

          {cinemaTargetingPrep ? (
            <>
              <details className="cinema-location-details">
                <summary>View Geo-Fence Targets ({formatInteger(cinemaTargetingPrep.geofenceTargets.length)})</summary>
                <div className="cinema-analysis-bullet-list">
                  {cinemaTargetingPrep.geofenceTargets.length === 0 ? (
                    <div className="cinema-analysis-bullet-item">No geo-fence-ready targets prepared yet.</div>
                  ) : null}
                  {cinemaTargetingPrep.geofenceTargets.slice(0, 5).map((target, index) => (
                    <div key={`geofence-${index}`} className="cinema-analysis-bullet-item">
                      {target.theaterName || "--"}
                      {target.city ? ` · ${target.city}` : ""}
                      {target.state ? `, ${target.state}` : ""} · {formatInteger(target.radius)} {target.radiusUnit}
                    </div>
                  ))}
                  {cinemaTargetingPrep.geofenceTargets.length > 5 ? (
                    <div className="cinema-analysis-bullet-item">+ {formatInteger(cinemaTargetingPrep.geofenceTargets.length - 5)} more</div>
                  ) : null}
                </div>
              </details>

              <details className="cinema-location-details">
                <summary>View City/State/ZIP Targets</summary>
                <div className="cinema-location-geo-preview">
                  <div className="cinema-location-geo-preview-group">
                    <div className="cinema-analysis-subsection-title">City Targets ({formatInteger(cinemaTargetingPrep.cityTargets.length)})</div>
                    <div className="cinema-analysis-bullet-list">
                      {cinemaTargetingPrep.cityTargets.length === 0 ? (
                        <div className="cinema-analysis-bullet-item">None prepared.</div>
                      ) : null}
                      {cinemaTargetingPrep.cityTargets.slice(0, 5).map((target, index) => (
                        <div key={`city-${index}`} className="cinema-analysis-bullet-item">
                          {target.city}{target.state ? `, ${target.state}` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="cinema-location-geo-preview-group">
                    <div className="cinema-analysis-subsection-title">State Targets ({formatInteger(cinemaTargetingPrep.stateTargets.length)})</div>
                    <div className="cinema-analysis-bullet-list">
                      {cinemaTargetingPrep.stateTargets.length === 0 ? (
                        <div className="cinema-analysis-bullet-item">None prepared.</div>
                      ) : null}
                      {cinemaTargetingPrep.stateTargets.slice(0, 5).map((target, index) => (
                        <div key={`state-${index}`} className="cinema-analysis-bullet-item">{target.state}</div>
                      ))}
                    </div>
                  </div>
                  <div className="cinema-location-geo-preview-group">
                    <div className="cinema-analysis-subsection-title">ZIP Targets ({formatInteger(cinemaTargetingPrep.zipTargets.length)})</div>
                    <div className="cinema-analysis-bullet-list">
                      {cinemaTargetingPrep.zipTargets.length === 0 ? (
                        <div className="cinema-analysis-bullet-item">None prepared.</div>
                      ) : null}
                      {cinemaTargetingPrep.zipTargets.slice(0, 5).map((target, index) => (
                        <div key={`zip-${index}`} className="cinema-analysis-bullet-item">
                          {target.zip}{target.city ? ` · ${target.city}` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </details>

              <details className="cinema-location-details">
                <summary>View Market Targets ({formatInteger(cinemaTargetingPrep.marketTargets.length)})</summary>
                <div className="cinema-analysis-bullet-list">
                  {cinemaTargetingPrep.marketTargets.length === 0 ? (
                    <div className="cinema-analysis-bullet-item">None prepared.</div>
                  ) : null}
                  {cinemaTargetingPrep.marketTargets.slice(0, 5).map((target, index) => (
                    <div key={`market-${index}`} className="cinema-analysis-bullet-item">{target.market}</div>
                  ))}
                  {cinemaTargetingPrep.marketTargets.length > 5 ? (
                    <div className="cinema-analysis-bullet-item">+ {formatInteger(cinemaTargetingPrep.marketTargets.length - 5)} more</div>
                  ) : null}
                </div>
              </details>

              <details className="cinema-location-details">
                <summary>View Geocoding Queue ({formatInteger(cinemaTargetingPrep.geocodingQueue.length)})</summary>
                <div className="cinema-analysis-bullet-list">
                  {cinemaTargetingPrep.geocodingQueue.length === 0 ? (
                    <div className="cinema-analysis-bullet-item">No rows require geocoding.</div>
                  ) : null}
                  {cinemaTargetingPrep.geocodingQueue.slice(0, 5).map((target, index) => (
                    <div key={`geocode-${index}`} className="cinema-analysis-bullet-item">
                      {target.theaterName || "--"}{target.suggestedGeocodingInput ? ` · ${target.suggestedGeocodingInput}` : ""}
                    </div>
                  ))}
                  {cinemaTargetingPrep.geocodingQueue.length > 5 ? (
                    <div className="cinema-analysis-bullet-item">+ {formatInteger(cinemaTargetingPrep.geocodingQueue.length - 5)} more</div>
                  ) : null}
                </div>
              </details>

              <details className="cinema-location-details">
                <summary>View Cleanup Queue ({formatInteger(cinemaTargetingPrep.cleanupQueue.length)})</summary>
                <div className="cinema-analysis-bullet-list">
                  {cinemaTargetingPrep.cleanupQueue.length === 0 ? (
                    <div className="cinema-analysis-bullet-item">No rows require cleanup.</div>
                  ) : null}
                  {cinemaTargetingPrep.cleanupQueue.slice(0, 5).map((target, index) => (
                    <div key={`cleanup-${index}`} className="cinema-analysis-bullet-item">
                      {target.theaterName || "--"} · requires cleanup
                    </div>
                  ))}
                  {cinemaTargetingPrep.cleanupQueue.length > 5 ? (
                    <div className="cinema-analysis-bullet-item">+ {formatInteger(cinemaTargetingPrep.cleanupQueue.length - 5)} more</div>
                  ) : null}
                </div>
              </details>
            </>
          ) : null}

          {cinemaCsvPreviewRows.length ? (
            <details className="cinema-location-details">
              <summary>View Theater Records</summary>
              <div className="cinema-location-table-shell cinema-location-table-shell-premium cinema-location-upload-preview">
                <div className="cinema-location-selection-summary cinema-location-selection-summary-premium">
                  <div>
                    <div className="cinema-analysis-subsection-title">Cinema Footprint Preview</div>
                    <div className="cinema-analysis-score-copy">
                      Detected {formatInteger(cinemaCsvImport.headers?.length)} columns. Preview is local to this session.
                    </div>
                  </div>
                </div>
                <div className="cinema-location-table-scroll">
                  <table className="cinema-location-table">
                    <thead>
                      <tr>
                        <th>Theater</th>
                        <th>Circuit</th>
                        <th>Market / Region</th>
                        <th>Location</th>
                        <th>Readiness</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cinemaCsvPreviewRows.map((row, index) => (
                        <tr key={`${row.theaterName || "row"}-${index}`}>
                          <td>{row.theaterName || "--"}</td>
                          <td>{row.circuitName || "--"}</td>
                          <td>{row.market || row.state || row.city || "--"}</td>
                          <td>{[row.address, row.city, row.state, row.postalCode].filter(Boolean).join(", ") || (row.hasCoordinates ? `${row.latitude}, ${row.longitude}` : "--")}</td>
                          <td>{row.rowReadiness}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
    </section>
  );

  useLegacyPlatformPageAwareness({
    routeKey: "/cinema-analytics",
    pageTitle: "Cinema Analytics",
    platformKey: CINEMA_PLATFORM_KEY,
    platformLabel: CINEMA_PLATFORM_LABEL,
    advertiserId: resolvedAdvertiserId,
    advertiserName: resolvedAdvertiserName,
    selectedLink: resolvedCinemaReportUrl,
    linkResolved: true,
    loading: false,
    showReport: false,
    loadError: error,
    pageCapabilities: [
      "theatrical report access",
      "cinema performance review",
      "report availability checks",
    ],
  });

  const renderMarketAnalysisSurface = () => {
    const analysis = currentScopedAnalysis;
    if (!resolvedAdvertiserId) {
      return (
        <div className="cinema-analysis-state-card">
          <div className="cinema-analysis-state-title">Advertiser Required</div>
          <div className="cinema-analysis-state-copy">
            Choose an advertiser to load the live Cinema Market Analysis contract.
          </div>
        </div>
      );
    }

    if (loadingAdvertisers || waitingForDefaultDatePreset || loadingAnalysis) {
      return (
        <div className="cinema-analysis-state-card">
          <div className="cinema-analysis-loader-orb" />
          <div className="cinema-analysis-state-title">Loading Cinema Market Analysis</div>
          <div className="cinema-analysis-state-copy">
            {waitingForDefaultDatePreset
              ? "Resolving the selected campaign and its last approved flight lineage before loading saved analysis."
              : "Running a fresh Cinema analysis for the selected canonical campaign context and analysis window."}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="cinema-analysis-state-card cinema-analysis-state-card-error">
          <div className="cinema-analysis-state-title">Analysis Unavailable</div>
          <div className="cinema-analysis-state-copy">{error}</div>
        </div>
      );
    }

    if (!hasFlightDatePreset) {
      return (
        <div className="cinema-analysis-state-card">
          <div className="cinema-analysis-state-title cinema-analysis-top-control-title">
            Last-Approved Flight Evidence Required
          </div>
          <div className="cinema-analysis-state-copy">
            No immutable last-approved flight is available for this campaign. Cinema analysis
            remains unavailable and deployment remains blocked until governed flight evidence is
            restored.
          </div>
        </div>
      );
    }

    if (!analysis) {
      return (
        <div className="cinema-analysis-state-card">
          <div className="cinema-analysis-state-title cinema-analysis-top-control-title cinema-analysis-output-title">
            No Saved Cinema Analysis
          </div>
          <div className="cinema-analysis-state-copy">
            Run Analysis to generate the first saved report for this advertiser and analysis window.
          </div>
        </div>
      );
    }

    const renderFilmIntelligenceSection = () => {
      if (!filmIntelligence?.cards?.length) return null;
      const insightCards = buildFilmReferenceInsightCards(filmIntelligence);
      return (
        <AnalysisSection title="Film Intelligence" kicker="IMDb + Rotten Tomatoes">
          {insightCards.length ? (
            <div className="cinema-location-ai-summary-grid">
              {insightCards.map((card) => (
                <div key={card.key} className="cinema-location-ai-summary-card">
                  <div className="cinema-analysis-subsection-title">{card.title}</div>
                  <div className="cinema-analysis-film-value">{card.value}</div>
                  <p className="cinema-analysis-score-copy">{card.copy}</p>
                </div>
              ))}
            </div>
          ) : null}
          <div className="cinema-analysis-film-grid">
            {filmIntelligence.cards.map((card) => (
              <div key={card.key} className="cinema-analysis-film-card">
                <div className="cinema-analysis-film-topline">
                  <div className="cinema-analysis-film-hero-frame" aria-hidden="true">
                    <div className="cinema-analysis-film-hero-frame-inner">
                      <div className="cinema-analysis-film-hero-media-well">
                        <img
                          src={card.image}
                          alt={`${card.title} hero`}
                          className="cinema-analysis-film-hero-image"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="cinema-analysis-film-topline-copy">
                    <div className="cinema-analysis-subsection-title">{card.title}</div>
                    <div className="cinema-analysis-film-value">{card.value}</div>
                    <Badge tone={card.badgeTone}>{card.badgeLabel}</Badge>
                  </div>
                </div>
                <p className="cinema-analysis-score-copy">{card.copy}</p>
                {card.url ? (
                  <a
                    className="cinema-analysis-film-link"
                    href={card.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open {card.title}
                  </a>
                ) : null}
              </div>
            ))}
          </div>
          <div className="cinema-analysis-interpretation-card cinema-analysis-film-takeaway-card">
            <div className="cinema-analysis-subsection-title">Reception Takeaway</div>
            <p>{filmIntelligence.combinedTakeaway}</p>
          </div>
        </AnalysisSection>
      );
    };

    const locationTotals = locationClusters.totals || {};
    const scopedTotalRows = Number(locationTotals.totalRows || 0);
    const scopedActiveRows = Number(locationTotals.activeRows || 0);
    const filteredRows = Number(locationTotals.filteredRows || 0);
    const filteredActiveRows = Number(locationTotals.filteredActiveRows || 0);
    const stateCoverageCount = (locationClusters.availableStates || []).length;
    const chainCoverageCount = (locationClusters.availableChains || []).length;
    const topStateCluster = locationClusters.stateClusters?.[0] || null;
    const topChainCluster = locationClusters.chainClusters?.[0] || null;
    const topStateShare = filteredRows > 0 && topStateCluster ? topStateCluster.count / filteredRows : 0;
    const topChainShare = filteredRows > 0 && topChainCluster ? topChainCluster.count / filteredRows : 0;
    const readinessScore =
      scopedTotalRows > 0
        ? clampNumber(
            Math.round(
              34 +
                (scopedActiveRows / Math.max(scopedTotalRows, 1)) * 28 +
                (Math.min(stateCoverageCount, 12) / 12) * 18 +
                (Math.min(chainCoverageCount, 8) / 8) * 12 +
                (locationTitleQuery ? 8 : 0)
            ),
            0,
            100
          )
        : 0;
    const readinessLabel =
      scopedTotalRows === 0
        ? "Unavailable"
        : readinessScore >= 85
        ? "Release Ready"
        : readinessScore >= 70
        ? "Operationally Strong"
        : readinessScore >= 55
        ? "Watch Coverage"
        : "Needs Expansion";
    const siteIntentTotal = gaIntentEvents.reduce(
      (sum, item) => sum + Number(item?.eventCount || 0),
      0
    );
    const projectionSignal =
      analysis?.projectionOutlook?.projection?.confidence ||
      analysis?.scorecard?.projectionConfidence?.label ||
      "not_available";
    const titleFootprint = analysis?.titleFootprint || null;
    const titleScope = analysis?.titleScope || null;
    const titleFootprintSummary = titleFootprint?.summary || {};
    const titleFootprintSource = titleFootprint?.sourceProvenance || null;
    const titleFootprintSourceType = titleFootprintSource?.type || "";
    const titleFootprintSourceLabel = formatCinemaSourceLabel(titleFootprintSourceType);
    const hasScopedCinemaData = Boolean(titleScope?.hasScopedCinemaData);
    const hasRenderableClientCinemaData = Boolean(hasScopedCinemaData || scopedTotalRows > 0);
    // Governed intelligence state: analytical conclusions, badges, risks, and
    // recommendations may render ONLY when real imported cinema evidence
    // supports them (from the scoped roster or a title-linked footprint).
    const cinemaEvidenceTheaters =
      scopedTotalRows ||
      Number(titleFootprintSummary.activeTheaterCount || titleFootprintSummary.theaterCount || 0);
    const cinemaEvidenceExhibitors =
      chainCoverageCount || Number(titleFootprintSummary.circuitCount || 0);
    const cinemaEvidenceMarkets =
      stateCoverageCount || Number(titleFootprintSummary.stateCount || 0);
    const cinemaIntelligence = resolveGovernedIntelligenceState({
      initialized: hasResolvedLocationContext,
      loading: loadingLocationClusters || loadingLocations,
      error: locationError || null,
      evidenceSources: [
        { key: "theaters", label: "theaters", count: cinemaEvidenceTheaters, required: true },
        { key: "exhibitors", label: "exhibitor groups", count: cinemaEvidenceExhibitors, required: true },
        { key: "markets", label: "state markets", count: cinemaEvidenceMarkets, required: true },
      ],
    });
    const cinemaConclusionsAllowed = cinemaIntelligence.canRenderConclusions;
    const cinemaUnavailableCopy = governedInsightUnavailableCopy(
      cinemaIntelligence,
      "cinema data"
    );
    const cinemaEvidenceProvenance = governedEvidenceProvenance(cinemaIntelligence);
    const scopedNoDataMessage =
      titleScope?.noDataMessage ||
      titleFootprint?.availability?.reason ||
      titleFootprint?.emptyState?.title ||
      (locationTitleQuery ? "No cinema data uploaded for this title yet." : "");
    const titleFootprintSourceCopy =
      titleFootprintSourceType === "workbook_sitelist"
        ? "This title footprint is currently derived from uploaded workbook footprint data."
        : titleFootprintSourceType === "manual_assignment"
        ? "This title footprint is currently derived from manually assigned reusable cinema locations."
        : scopedNoDataMessage || "No cinema data uploaded for this title yet.";
    const fallbackPublicAnalysis =
      filmIntelligence?.fallbackAnalysis &&
      typeof filmIntelligence.fallbackAnalysis === "object" &&
      filmIntelligence.fallbackAnalysis.available
        ? filmIntelligence.fallbackAnalysis
        : null;
    const clientFootprintCounts = {
      theaters: titleFootprintSummary.activeTheaterCount ?? analysis.marketSnapshot?.metrics?.bookedTheaters,
      circuits: titleFootprintSummary.circuitCount ?? analysis.marketSnapshot?.metrics?.circuits,
      cities: titleFootprintSummary.cityCount ?? analysis.marketSnapshot?.metrics?.cities,
      states: titleFootprintSummary.stateCount ?? analysis.marketSnapshot?.metrics?.states,
    };
    // Five hero metrics that answer the executive questions at a glance:
    // ready? how large? where strongest? how concentrated/risky?
    const heroMetrics = [
      {
        label: "Release Readiness",
        value: scopedTotalRows > 0 ? readinessLabel : "--",
        hint: scopedTotalRows > 0 ? `${formatInteger(readinessScore)}/100 readiness` : "Awaiting footprint",
        accent: readinessScore >= 70 ? "green" : "amber",
      },
      {
        label: "Booked Theaters",
        value: formatInteger(filteredRows || analysis.marketSnapshot?.metrics?.bookedTheaters),
        hint: `${formatInteger(filteredActiveRows || scopedActiveRows)} active`,
        accent: "blue",
      },
      {
        label: "Market Coverage",
        value: formatInteger(stateCoverageCount || analysis.marketSnapshot?.metrics?.states),
        hint: `${formatInteger(chainCoverageCount)} exhibitor chains`,
        accent: "violet",
      },
      {
        label: "Lead Exhibitor",
        value: topChainCluster ? topChainCluster.label : "--",
        hint: topChainCluster
          ? `${formatShareLabel(topChainCluster.count, filteredRows, 0)} of footprint`
          : "No lead exhibitor yet",
        accent: "amber",
      },
      {
        label: "Concentration Risk",
        value: !cinemaConclusionsAllowed
          ? "Not Available"
          : topChainShare >= 0.45
          ? "High"
          : topStateShare >= 0.35
          ? "Moderate"
          : "Balanced",
        hint: !cinemaConclusionsAllowed
          ? "Not available until cinema data is imported."
          : topChainShare >= 0.45 && topChainCluster
          ? `${topChainCluster.label} holds ${formatShareLabel(topChainCluster.count, filteredRows, 0)}`
          : "Exhibitor & state mix balanced",
        accent: !cinemaConclusionsAllowed ? "slate" : topChainShare >= 0.45 ? "amber" : "green",
      },
    ];
    const summarySignals = [
      hasScopedCinemaData
        ? `${titleFootprintSourceLabel} is powering this title-scoped cinema view.`
        : scopedNoDataMessage || "No cinema data uploaded for this title yet.",
      scopedTotalRows > 0
        ? `${formatInteger(scopedActiveRows)} active theaters are currently linked to ${locationTitleQuery || resolvedAdvertiserName}.`
        : hasScopedCinemaData
        ? "No theaters are visible in the current filtered view."
        : "No title-linked theater footprint is available in the reusable location store yet.",
      topStateCluster
        ? `${topStateCluster.label} leads visible coverage with ${formatInteger(topStateCluster.count)} theaters.`
        : "State leadership will populate once scoped footprint rows are available.",
      topChainCluster
        ? `${topChainCluster.label} is the dominant exhibitor block at ${formatInteger(topChainCluster.count)} theaters.`
        : "No dominant exhibitor block is available yet.",
    ];
    const deploymentRisks = !cinemaConclusionsAllowed
      ? [cinemaUnavailableCopy]
      : [
      topChainShare >= 0.45
        ? `${topChainCluster?.label || "The top chain"} controls ${formatShareLabel(
            topChainCluster?.count,
            filteredRows,
            0
          )} of the visible roster, creating exhibitor concentration risk.`
        : "Chain concentration looks manageable in the current scope.",
      topStateShare >= 0.35
        ? `${topStateCluster?.label || "The lead state"} is carrying an outsized share of the current footprint.`
        : "State concentration looks balanced enough for normal theatrical planning.",
      siteIntentTotal <= 0
        ? "Site-intent support is currently light, so opening-strength reads should stay directional."
        : "Site-intent signals are present and can be used as directional validation for footprint depth.",
    ];
    const recommendedActions = !cinemaConclusionsAllowed
      ? [cinemaUnavailableCopy]
      : [
      topChainShare >= 0.45
        ? "Diversify exhibitor mix where adjacent chains can expand the footprint."
        : "Lean into the strongest exhibitor mix while pressure-testing secondary chain depth.",
      stateCoverageCount < 8
        ? "Expand territory coverage before treating this as a broad theatrical footprint."
        : "Use state drilldowns to prioritize regional deployment sequencing and support weighting.",
      siteIntentTotal > 0
        ? "Cross-check top footprint states against top site-intent cities before finalizing rollout emphasis."
        : "Use paid media and footprint strength as the primary directional read until site-intent deepens.",
    ];
    const projectionCards = [
      {
        label: "Projected Ticket Sales",
        value: formatCurrency(analysis?.projectionOutlook?.projection?.projectedTicketSalesUsd),
        hint: "Opening-strength proxy from the current projection layer.",
      },
      {
        label: "Estimated Attendance",
        value: formatInteger(analysis?.projectionOutlook?.projection?.projectedAttendance),
        hint: "Revenue-based attendance estimate.",
      },
      {
        label: "Top Projected Market",
        value: topStateCluster?.label || gaTopCities?.[0]?.city || "--",
        hint: topStateCluster ? `${formatInteger(topStateCluster.count)} visible theaters lead` : "Strengthens as footprint grows",
      },
      {
        label: "Projection Confidence",
        value: titleCase(projectionSignal),
        hint: "Strengthens as paid media and title history accumulate.",
      },
    ];

    if (!hasRenderableClientCinemaData) {
      if (fallbackPublicAnalysis) {
        return (
          <>
            <AnalysisSection
              title="Limited Cinema Intelligence"
              kicker={fallbackPublicAnalysis.label || "Public Signal Analysis"}
              aside={<div className="cinema-analysis-section-aside">Limited Data Mode</div>}
            >
              <div className="cinema-location-ai-summary-grid">
                <div className="cinema-location-ai-summary-card cinema-location-ai-summary-card-primary">
                  <div className="cinema-analysis-section-kicker">Title Footprint Analysis</div>
                  <div className="cinema-location-ai-summary-title">
                    {fallbackPublicAnalysis.summary ||
                      `Sherloc is using lighter title/public signals for ${
                        locationTitleQuery || resolvedAdvertiserName || "this title"
                      } while deeper cinema ingestion is still pending.`}
                  </div>
                  <p className="cinema-analysis-score-copy">
                    Sherloc found public title and release metadata, but verified cinema footprint, exhibitor, geo, delivery, and box-office signals are not available yet. This analysis is safe to use for early positioning, but should not be treated as confirmed cinema performance intelligence.
                  </p>
                  <p className="cinema-analysis-score-copy">
                    {fallbackPublicAnalysis.sourceSummary ||
                      "This is a limited cinema data mode based only on genuinely available public/title sources."}
                  </p>
                </div>

                {(fallbackPublicAnalysis.sectionCards || []).map((card) => (
                  <div key={card.key} className="cinema-location-ai-summary-card">
                    <div className="cinema-analysis-subsection-title">{card.title}</div>
                    <p className="cinema-analysis-score-copy">{card.copy}</p>
                  </div>
                ))}

                <div className="cinema-location-ai-summary-card">
                  <div className="cinema-analysis-subsection-title">Connected Sources</div>
                  <div className="cinema-analysis-bullet-list">
                    {(fallbackPublicAnalysis.sourcesUsed || []).map((source) => (
                      <div key={source.key} className="cinema-analysis-bullet-item">
                        <strong>{source.title}</strong>
                        {source.status ? ` • ${titleCase(String(source.status).replace(/[_-]+/g, " "))}` : ""}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="cinema-location-ai-summary-card">
                  <div className="cinema-analysis-subsection-title">Strengths</div>
                  <div className="cinema-analysis-bullet-list">
                    {(fallbackPublicAnalysis.strengths || []).length ? (
                      fallbackPublicAnalysis.strengths.map((item) => (
                        <div key={item} className="cinema-analysis-bullet-item">{item}</div>
                      ))
                    ) : (
                      <div className="cinema-analysis-bullet-item">
                        Connected public-title sources are present, but the current signal set is still light.
                      </div>
                    )}
                  </div>
                </div>

                <div className="cinema-location-ai-summary-card">
                  <div className="cinema-analysis-subsection-title">Gaps</div>
                  <div className="cinema-analysis-bullet-list">
                    {(fallbackPublicAnalysis.gaps || []).length ? (
                      fallbackPublicAnalysis.gaps.map((item) => (
                        <div key={item} className="cinema-analysis-bullet-item">{item}</div>
                      ))
                    ) : (
                      <div className="cinema-analysis-bullet-item">
                        No major public-title signal gaps are currently blocking limited cinema data analysis.
                      </div>
                    )}
                  </div>
                </div>

                <div className="cinema-location-ai-summary-card">
                  <div className="cinema-analysis-subsection-title">Limitations</div>
                  <div className="cinema-analysis-bullet-list">
                    {(fallbackPublicAnalysis.limitations || []).map((item) => (
                      <div key={item} className="cinema-analysis-bullet-item">{item}</div>
                    ))}
                  </div>
                </div>
              </div>
            </AnalysisSection>

            {renderFilmIntelligenceSection()}
          </>
        );
      }

      return (
        <AnalysisSection title="Cinema Intelligence" kicker="Awaiting Cinema Data">
          <div className="cinema-analysis-state-card" data-testid="cinema-governed-state">
            <div className="cinema-analysis-state-title">Awaiting Cinema Data</div>
            <div className="cinema-analysis-state-copy">
              {scopedNoDataMessage ||
                "No cinema intelligence has been uploaded for this campaign. Upload theater inventory or cinema planning data to begin analysis."}
            </div>
          </div>
        </AnalysisSection>
      );
    }

    const demandConfidenceNote =
      analysis?.salesDemandSignals?.insight || "No demand narrative returned.";
    const mediaConfidenceNote =
      analysis?.marketingImpact?.insight || "No marketing-impact narrative returned.";

    return (
      <>
        {cinemaIntelligence.state === GOVERNED_INTELLIGENCE_STATES.INSUFFICIENT_DATA ? (
          <div className="cinema-analysis-state-card" data-testid="cinema-governed-state">
            <div className="cinema-analysis-section-kicker">Data Confidence</div>
            <div className="cinema-analysis-state-title cinema-analysis-output-title">Limited</div>
            <div className="cinema-analysis-state-copy">
              Additional cinema intelligence is required before every insight can be generated.
            </div>
          </div>
        ) : cinemaEvidenceProvenance ? (
          <div className="cinema-analysis-state-card" data-testid="cinema-governed-state">
            <div className="cinema-analysis-section-kicker">Evidence</div>
            <div className="cinema-analysis-state-copy">{cinemaEvidenceProvenance}</div>
          </div>
        ) : null}
        {/* A. Executive Summary — the five-second read */}
        <AnalysisSection title="Executive Summary" kicker="Cinema Intelligence">
          <div className="cinema-location-performance-grid">
            {heroMetrics.map((card) => (
              <KpiCard
                key={card.label}
                label={card.label}
                value={card.value}
                hint={card.hint}
                accent={card.accent}
              />
            ))}
          </div>
          <div className="cinema-location-ai-summary-grid">
            <div className="cinema-location-ai-summary-card cinema-location-ai-summary-card-primary">
              <div className="cinema-analysis-section-kicker">Planning Verdict</div>
              <div className="cinema-location-ai-summary-title">
                {hasScopedCinemaData
                  ? `${locationTitleQuery || resolvedAdvertiserName} is powered by ${titleFootprintSourceLabel.toLowerCase()} with ${formatInteger(
                      clientFootprintCounts.theaters
                    )} theaters, ${formatInteger(clientFootprintCounts.circuits)} circuits, ${formatInteger(
                      clientFootprintCounts.cities
                    )} cities, and ${formatInteger(clientFootprintCounts.states)} states in scope.`
                  : locationTitleQuery
                  ? "No cinema data uploaded for this title yet."
                  : scopedTotalRows > 0
                  ? `${locationTitleQuery || resolvedAdvertiserName} shows a ${readinessLabel.toLowerCase()} footprint with ${formatInteger(
                      scopedActiveRows
                    )} active theaters across ${formatInteger(stateCoverageCount)} states.`
                  : "Cinema intelligence is ready, but this title still needs a linked theatrical footprint before coverage analysis can be trusted."}
              </div>
              <p className="cinema-analysis-score-copy">
                {hasScopedCinemaData
                  ? titleFootprintSourceCopy
                  : locationTitleQuery
                  ? scopedNoDataMessage || "No cinema data uploaded for this title yet."
                  : scopedTotalRows > 0
                  ? `${topStateCluster?.label || "The lead state"} and ${
                      topChainCluster?.label || "the leading exhibitor"
                    } carry the heaviest share of the visible roster. The sections below show where the release is strongest, where it is over-concentrated, and where more theatrical depth is needed.`
                  : "Once title-linked locations land in the reusable store, this page summarizes territorial concentration, exhibitor mix, deployment risks, and next actions automatically."}
              </p>
            </div>
            <div className="cinema-location-ai-summary-card">
              <div className="cinema-analysis-subsection-title">Market Signals</div>
              <div className="cinema-analysis-bullet-list">
                {summarySignals.map((signal) => (
                  <div key={signal} className="cinema-analysis-bullet-item">{signal}</div>
                ))}
              </div>
            </div>
            <div className="cinema-location-ai-summary-card">
              <div className="cinema-analysis-subsection-title">Deployment Risks</div>
              <div className="cinema-analysis-bullet-list">
                {deploymentRisks.map((risk) => (
                  <div key={risk} className="cinema-analysis-bullet-item">{risk}</div>
                ))}
              </div>
            </div>
          </div>
        </AnalysisSection>

        {/* B. Footprint & Coverage — exhibitor mix, coverage, and the theater roster drill-down */}
        <AnalysisSection title="Footprint & Coverage" kicker="Where the release is strongest">
          <div className="cinema-analysis-subsection-title">Exhibitor Mix</div>
          <div className="cinema-location-live-cinemas-grid">
            {(!hasResolvedLocationContext || loadingLocationClusters) ? (
              <div className="cinema-analysis-empty-mini">
                Resolving the current advertiser and title footprint before loading live cinemas.
              </div>
            ) : liveCinemas.length ? (
              liveCinemas.map((cinema) => {
                const isActive = locationChainFilter === cinema.key;
                const heroButton = resolveCinemaChainHeroButton(
                  cinema.key,
                  cinema.label,
                  ...(cinema.labelLines || [])
                );
                const heroImage = heroButton?.image || cinema.image || null;
                const heroLabel = heroButton?.label || cinema.label;
                return (
                  <button
                    key={cinema.key}
                    type="button"
                    className={`cinema-location-live-cinema-card ${
                      isActive ? "cinema-location-live-cinema-card-active" : ""
                    }`}
                    onClick={() => {
                      setLocationOffset(0);
                      setLocationChainFilter(isActive ? "" : cinema.key);
                    }}
                  >
                    <div className="cinema-location-live-cinema-topline">
                      <div className="cinema-location-live-cinema-brand cinema-location-live-cinema-brand-framed">
                        {heroImage ? (
                          <div className="cinema-location-live-cinema-logo-shell">
                            <div className="cinema-location-live-cinema-logo-well">
                              <img src={heroImage} alt={`${heroLabel} logo`} className="cinema-location-live-cinema-logo" />
                            </div>
                          </div>
                        ) : (
                          <div className="cinema-location-live-cinema-logo-shell">
                            <div className="cinema-location-live-cinema-logo-well">
                              <span className="cinema-location-live-cinema-fallback">{heroLabel}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <Badge tone={isActive ? "good" : "neutral"}>
                        {isActive ? "Selected" : "Live"}
                      </Badge>
                    </div>
                    <div className="cinema-location-live-cinema-value">{formatInteger(cinema.count)}</div>
                    <div className="cinema-analysis-score-copy">
                      {formatShareLabel(cinema.count, filteredRows, 0)} of the visible theatrical footprint.
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="cinema-analysis-empty-mini">
                Live cinema cards will populate once title-linked footprint rows are available.
              </div>
            )}
          </div>

          <div className="cinema-analysis-subsection-title">Coverage</div>
          <div className="cinema-location-cluster-summary-grid cinema-location-cluster-summary-grid-expanded">
            <MetricCard label="Circuits" value={formatInteger(analysis.marketSnapshot?.metrics?.circuits)} hint="Distinct exhibitor circuits in scope" />
            <MetricCard label="Cities" value={formatInteger(analysis.marketSnapshot?.metrics?.cities)} hint="Cities with booked theaters" />
            <MetricCard label="States Covered" value={formatInteger(stateCoverageCount)} hint={topStateCluster ? `${topStateCluster.label} leads visible coverage` : "No dominant state yet"} />
            <MetricCard label="Chains Covered" value={formatInteger(chainCoverageCount)} hint={topChainCluster ? `${topChainCluster.label} is the lead exhibitor` : "No dominant exhibitor yet"} />
            <MetricCard label="Top State Share" value={topStateCluster ? formatShareLabel(topStateCluster.count, filteredRows, 0) : "--"} hint={topStateCluster?.label || "Visible state share"} />
            <MetricCard label="Market Coverage" value={formatPercent(analysis.marketSnapshot?.metrics?.estimatedKnownMarketTheaterSharePct)} hint="Share of the known reference market" />
          </div>
          <div className="cinema-analysis-inline-note-grid">
            <div className="cinema-analysis-inline-note">
              <span>Reference set usable</span>
              <strong>{analysis.marketSnapshot?.benchmark?.coverageDatasetUsable ? "Yes" : "No"}</strong>
            </div>
            <div className="cinema-analysis-inline-note">
              <span>City coverage</span>
              <strong>{formatPercent(analysis.marketSnapshot?.benchmark?.estimatedCityShareOfKnownMarketPct)}</strong>
            </div>
            <div className="cinema-analysis-inline-note">
              <span>State coverage</span>
              <strong>{formatPercent(analysis.marketSnapshot?.benchmark?.estimatedStateShareOfKnownMarketPct)}</strong>
            </div>
            <div className="cinema-analysis-inline-note">
              <span>Known active cinemas</span>
              <strong>{formatInteger(analysis.marketSnapshot?.benchmark?.activeCinemaCount)}</strong>
            </div>
          </div>
          <div className="cinema-analysis-interpretation-card">
            <div className="cinema-analysis-subsection-title">Confidence &amp; Method</div>
            <p>{analysis.marketSnapshot?.insight || "No market snapshot narrative returned."}</p>
          </div>

          <div className="cinema-analysis-subsection-title">Theater Roster</div>
          {locationError ? (
            <div className="cinema-analysis-state-card cinema-analysis-state-card-error">
              <div className="cinema-analysis-state-title">Location Intelligence Unavailable</div>
              <div className="cinema-analysis-state-copy">{locationError}</div>
            </div>
          ) : (
            <div className="cinema-location-filter-grid">
              <div className="cinema-location-filter-context-card">
                <div className="cinema-analysis-subsection-title">Footprint Scope</div>
                <div className="cinema-location-filter-context-meta">
                  <div><span>Advertiser</span><strong>{resolvedAdvertiserName || "Waiting for selection"}</strong></div>
                  <div><span>Title</span><strong>{locationTitleQuery || "Awaiting campaign context"}</strong></div>
                  <div><span>Footprint Source</span><strong>{locationTitleQuery ? titleFootprintSourceLabel : "Advertiser Scope"}</strong></div>
                  <div><span>Last Synced</span><strong>{latestVisibleLocationSyncAt ? formatDateTime(latestVisibleLocationSyncAt) : "Live source"}</strong></div>
                  <div><span>Readiness</span><strong>{readinessLabel}</strong></div>
                </div>
                {locationTitleQuery ? (
                  <div className="cinema-analysis-score-copy">
                    {hasScopedCinemaData
                      ? titleFootprintSourceCopy
                      : scopedNoDataMessage || "No cinema data uploaded for this title yet."}
                  </div>
                ) : null}
              </div>
              <div className="cinema-location-list-controls cinema-location-list-controls-premium">
                <div className="cinema-analysis-control-group">
                  <label className="cinema-analysis-control-label">Search Footprint</label>
                  <input
                    className="cinema-analysis-text-input"
                    type="text"
                    value={locationSearchInput}
                    onChange={(event) => setLocationSearchInput(event.target.value)}
                    placeholder="Search theater, chain, city, postal, DMA..."
                  />
                </div>
                <div className="cinema-analysis-control-group cinema-analysis-control-group-small">
                  <label className="cinema-analysis-control-label">State</label>
                  <select
                    className="cinema-analysis-select"
                    value={locationStateFilter}
                    onChange={(event) => {
                      setLocationOffset(0);
                      setLocationStateFilter(event.target.value);
                    }}
                  >
                    <option value="">All States</option>
                    {(locationClusters.availableStates || []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="cinema-analysis-control-group">
                  <label className="cinema-analysis-control-label">Chain</label>
                  <select
                    className="cinema-analysis-select"
                    value={locationChainFilter}
                    onChange={(event) => {
                      setLocationOffset(0);
                      setLocationChainFilter(event.target.value);
                    }}
                  >
                    <option value="">All Chains</option>
                    {(locationClusters.availableChains || []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="cinema-analysis-control-group">
                  <label className="cinema-analysis-control-label">Source</label>
                  <select
                    className="cinema-analysis-select"
                    value={locationSourceFilter}
                    onChange={(event) => {
                      setLocationOffset(0);
                      setLocationSourceFilter(event.target.value);
                    }}
                  >
                    <option value="">All Sources</option>
                    {(locationClusters.availableSources || []).map((option) => (
                      <option key={option.key} value={option.key}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="cinema-analysis-control-group cinema-analysis-control-group-small">
                  <label className="cinema-analysis-control-label">Status</label>
                  <select
                    className="cinema-analysis-select"
                    value={locationIncludeInactive ? "all" : "active"}
                    onChange={(event) => {
                      setLocationOffset(0);
                      setLocationIncludeInactive(event.target.value === "all");
                    }}
                  >
                    <option value="active">Active Only</option>
                    <option value="all">Include Inactive</option>
                  </select>
                </div>
                <div className="cinema-analysis-control-group cinema-analysis-control-group-small">
                  <label className="cinema-analysis-control-label">Limit</label>
                  <select
                    className="cinema-analysis-select"
                    value={locationLimit}
                    onChange={(event) => {
                      setLocationOffset(0);
                      setLocationLimit(Number(event.target.value));
                    }}
                  >
                    {LOCATION_LIMIT_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
          {!locationError ? (
            <div className="cinema-location-admin-grid">
              <div className="cinema-location-table-shell cinema-location-table-shell-premium">
                <div className="cinema-location-selection-summary cinema-location-selection-summary-premium">
                  <div>
                    <div className="cinema-analysis-subsection-title">Visible Roster</div>
                    <div className="cinema-analysis-score-copy">
                      {formatInteger(filteredRows)} visible theaters after current client-safe filters.
                    </div>
                  </div>
                </div>
                <div className="cinema-location-table-scroll">
                  <table className="cinema-location-table">
                    <thead>
                      <tr>
                        <th>Theater</th>
                        <th>Chain</th>
                        <th>City / State</th>
                        <th>Postal</th>
                        <th>DMA / Market</th>
                        <th>Source</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingLocations ? (
                        <tr><td colSpan="7" className="cinema-location-table-empty">Loading theaters...</td></tr>
                      ) : locationsData.items.length ? (
                        locationsData.items.map((location) => (
                          <tr key={location.id} onClick={() => setSelectedLocationId(location.id)}>
                            <td>
                              <div className="cinema-location-primary-cell">
                                <strong>{location.theater_name || "--"}</strong>
                                <span>{location.address_line_1 || "--"}</span>
                              </div>
                            </td>
                            <td>{location.normalized_chain_label || location.chain || location.exhibitor_name || "--"}</td>
                            <td>{[location.city, location.state].filter(Boolean).join(", ") || "--"}</td>
                            <td>{location.postal_code || "--"}</td>
                            <td>{location.dma || location.market_name || "--"}</td>
                            <td>{titleCase(location.source || "manual")}</td>
                            <td>
                              <Badge tone={location.is_active ? "good" : "muted"}>
                                {location.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="7" className="cinema-location-table-empty">No theaters returned for the current filter.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="cinema-location-detail-panel cinema-location-detail-panel-premium">
                <div className="cinema-analysis-subsection-title">Theater Detail</div>
                {selectedLocation ? (
                  <>
                    <div className="cinema-location-detail-title">{selectedLocation.theater_name}</div>
                    <div className="cinema-location-detail-meta">
                      {[
                        selectedLocation.normalized_chain_label ||
                          selectedLocation.chain ||
                          selectedLocation.exhibitor_name,
                        selectedLocation.city,
                        selectedLocation.state,
                      ].filter(Boolean).join(" • ")}
                    </div>
                    <div className="cinema-location-detail-grid">
                      <div><span>Address</span><strong>{selectedLocation.address_line_1 || "--"}</strong></div>
                      <div><span>Postal Code</span><strong>{selectedLocation.postal_code || "--"}</strong></div>
                      <div><span>DMA</span><strong>{selectedLocation.dma || "--"}</strong></div>
                      <div><span>Market</span><strong>{selectedLocation.market_name || "--"}</strong></div>
                      <div><span>Source</span><strong>{titleCase(selectedLocation.source || "manual")}</strong></div>
                      <div><span>Status</span><strong>{selectedLocation.is_active ? "Active" : "Inactive"}</strong></div>
                    </div>
                  </>
                ) : (
                  <div className="cinema-analysis-bullet-item">Select a theater to review its normalized client-facing footprint details.</div>
                )}
              </div>
            </div>
          ) : null}
        </AnalysisSection>

        <AnalysisSection title="Demand & Media" kicker="Audience demand and media support">
          <div className="cinema-analysis-metric-grid cinema-analysis-metric-grid-four">
            <MetricCard label="Ticket Sales Revenue" value={formatCurrency(analysis.salesDemandSignals?.metrics?.ticketSalesUsd)} />
            <MetricCard label="Estimated Attendance" value={formatInteger(analysis.salesDemandSignals?.metrics?.estimatedAttendance)} hint="Revenue-based estimate" />
            <MetricCard label="Sales Per Booked Theater" value={formatCurrencyPrecise(analysis.salesDemandSignals?.metrics?.ticketSalesPerBookedTheater)} />
            <MetricCard label="Daily Ticket Sales" value={formatCurrencyPrecise(analysis.salesDemandSignals?.metrics?.dailyTicketSalesUsd)} />
          </div>

          <div className="cinema-analysis-trend-shell">
            <div className="cinema-analysis-trend-summary">
              <div className="cinema-analysis-trend-pill">
                {titleCase(analysis.salesDemandSignals?.trend?.direction || "insufficient history")}
              </div>
              <div className="cinema-analysis-trend-delta">
                Delta: {safeNum(analysis.salesDemandSignals?.trend?.deltaPct) == null ? "--" : formatPercent(analysis.salesDemandSignals?.trend?.deltaPct)}
              </div>
            </div>

            <div className="cinema-analysis-history-bars">
              {(analysis.salesDemandSignals?.historicalWindows || []).map((row) => {
                const value = safeNum(row.ticketSalesUsd) || 0;
                const width = `${Math.max(8, Math.round((value / historyMax) * 100))}%`;
                return (
                  <div key={`${row.startDate}-${row.endDate}`} className="cinema-analysis-history-row">
                    <div className="cinema-analysis-history-label">
                      {formatDateOnly(row.startDate)} to {formatDateOnly(row.endDate)}
                    </div>
                    <div className="cinema-analysis-history-track">
                      <div className="cinema-analysis-history-fill" style={{ width }} />
                    </div>
                    <div className="cinema-analysis-history-value">{formatCurrency(value)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="cinema-analysis-subsection-title">Media Support</div>
          <div className="cinema-analysis-metric-grid cinema-analysis-metric-grid-four">
            <MetricCard label="Digital Media Spend" value={formatCurrency(analysis.marketingImpact?.metrics?.digitalMediaSpendUsd)} />
            <MetricCard label="Total Snapshot Spend" value={formatCurrency(analysis.marketingImpact?.metrics?.totalSnapshotSpendUsd)} />
            <MetricCard label="Media Efficiency" value={formatMultiple(analysis.marketingImpact?.metrics?.mediaToTicketSalesMultiple)} hint="Revenue returned per media dollar" />
            <MetricCard label="Cost per Ticket Revenue $" value={formatCurrencyPrecise(analysis.marketingImpact?.metrics?.costPerTicketRevenueDollar)} />
          </div>

          <div className="cinema-analysis-ga-grid">
            <div className="cinema-analysis-ga-card">
              <div className="cinema-analysis-subsection-title">Site Activity</div>
              {analysis.marketingImpact?.gaOverlay?.available && analysis.marketingImpact?.gaOverlay?.overview ? (
                <div className="cinema-analysis-mini-metrics">
                  <div><span>Sessions</span><strong>{formatInteger(analysis.marketingImpact.gaOverlay.overview.sessions)}</strong></div>
                  <div><span>Total Users</span><strong>{formatInteger(analysis.marketingImpact.gaOverlay.overview.totalUsers)}</strong></div>
                  <div><span>Engagement Rate</span><strong>{formatPercentFromDecimal(analysis.marketingImpact.gaOverlay.overview.engagementRate)}</strong></div>
                  <div><span>Avg Session Duration</span><strong>{analysis.marketingImpact.gaOverlay.overview.averageSessionDuration || "--"}</strong></div>
                </div>
              ) : (
                <div className="cinema-analysis-empty-mini">{analysis.marketingImpact?.gaOverlay?.reason || "No GA overlay available."}</div>
              )}
            </div>

            <div className="cinema-analysis-ga-card">
              <div className="cinema-analysis-subsection-title">Top Cities</div>
              {gaTopCities.length ? (
                <div className="cinema-analysis-list">
                  {gaTopCities.map((item, index) => (
                    <div key={`${item.city || "city"}-${index}`} className="cinema-analysis-list-row">
                      <span>{item.city || "Unknown City"}</span>
                      <strong>{formatInteger(item.sessions || item.totalUsers)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cinema-analysis-empty-mini">No city overlay returned for this window.</div>
              )}
            </div>

            <div className="cinema-analysis-ga-card">
              <div className="cinema-analysis-subsection-title">Top Sources</div>
              {gaTopSources.length ? (
                <div className="cinema-analysis-list">
                  {gaTopSources.map((item, index) => (
                    <div key={`${item.source || "source"}-${index}`} className="cinema-analysis-list-row">
                      <span>{item.source || "Unknown Source"}</span>
                      <strong>{formatInteger(item.sessions || item.totalUsers)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cinema-analysis-empty-mini">No source overlay returned for this window.</div>
              )}
            </div>

            <div className="cinema-analysis-ga-card">
              <div className="cinema-analysis-subsection-title">Ticket Intent Events</div>
              {gaIntentEvents.length ? (
                <div className="cinema-analysis-list">
                  {gaIntentEvents.map((item, index) => (
                    <div key={`${item.eventName || "event"}-${index}`} className="cinema-analysis-list-row">
                      <span>{titleCase(item.eventName)}</span>
                      <strong>{formatInteger(item.eventCount)}</strong>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="cinema-analysis-empty-mini">No ticket-intent events were returned in the current response.</div>
              )}
            </div>
          </div>

          <div className="cinema-analysis-interpretation-card">
            <div className="cinema-analysis-subsection-title">Confidence &amp; Method</div>
            <p>{demandConfidenceNote}</p>
            <p>{mediaConfidenceNote}</p>
          </div>
        </AnalysisSection>

        {/* D. Projections — single forward-looking section */}
        <AnalysisSection title="Projections" kicker="Directional forward read">
          <div className="cinema-location-projection-grid">
            {projectionCards.map((card) => (
              <MetricCard key={card.label} label={card.label} value={card.value} hint={card.hint} />
            ))}
          </div>

          <div className="cinema-analysis-confidence-grid">
            <div className="cinema-analysis-notes-card">
              <div className="cinema-analysis-subsection-title">Confidence Notes</div>
              <div className="cinema-analysis-bullet-list">
                {(analysis.projectionOutlook?.projection?.confidenceNotes || []).map((note, index) => (
                  <div key={`${note}-${index}`} className="cinema-analysis-bullet-item">{note}</div>
                ))}
              </div>
            </div>
            <div className="cinema-analysis-interpretation-card">
              <div className="cinema-analysis-subsection-title">Outlook Interpretation</div>
              <p>{analysis.projectionOutlook?.insight || "No outlook narrative returned."}</p>
            </div>
          </div>

          <div className="cinema-analysis-subsection-title">Signal Scorecard</div>
          <div className="cinema-analysis-score-grid">
            {scoreEntries.map(([key, entry]) => (
              <div key={key} className={`cinema-analysis-score-card cinema-analysis-score-card-${key}`}>
                <div className="cinema-analysis-score-topline">
                  <div>
                    <div className="cinema-analysis-score-label">{titleCase(key)}</div>
                    <div className="cinema-analysis-score-state">{titleCase(entry.label)}</div>
                  </div>
                  <div className="cinema-analysis-score-value">{formatInteger(entry.score)}</div>
                </div>
                <div className="cinema-analysis-score-bar-track">
                  <div
                    className="cinema-analysis-score-bar-fill"
                    style={{ width: `${Math.max(0, Math.min(100, safeNum(entry.score) || 0))}%` }}
                  />
                </div>
                <p className="cinema-analysis-score-copy">{scoreDescription(key, entry)}</p>
                <div className="cinema-analysis-score-inputs">
                  {Object.entries(entry.inputs || {}).map(([inputKey, inputValue]) => (
                    <div key={inputKey} className="cinema-analysis-score-input-chip">
                      <span>{titleCase(inputKey)}</span>
                      <strong>
                        {typeof inputValue === "boolean"
                          ? inputValue
                            ? "Yes"
                            : "No"
                          : typeof inputValue === "number"
                          ? Number.isInteger(inputValue)
                            ? formatInteger(inputValue)
                            : inputValue.toFixed(2)
                          : String(inputValue ?? "--")}
                      </strong>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AnalysisSection>

        {/* E. Film Intelligence — supporting context, below planning intelligence */}
        {renderFilmIntelligenceSection()}

        {/* F. Recommendations — what to do next */}
        <AnalysisSection title="Recommendations" kicker="What to do next">
          <div className="cinema-analysis-recommendation-grid">
            {(analysis.recommendations?.items || []).map((item) => (
              <div key={item.id} className={`cinema-analysis-recommendation-card cinema-analysis-severity-${String(item.severity || "note").toLowerCase()}`}>
                <div className="cinema-analysis-recommendation-topline">
                  <span className="cinema-analysis-recommendation-severity">{severityLabel(item.severity)}</span>
                  <span className="cinema-analysis-recommendation-id">{item.id}</span>
                </div>
                <div className="cinema-analysis-recommendation-title">{item.title}</div>
                <p className="cinema-analysis-recommendation-copy">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="cinema-analysis-interpretation-card">
            <div className="cinema-analysis-subsection-title">Planning Moves</div>
            <div className="cinema-analysis-bullet-list">
              {recommendedActions.map((action) => (
                <div key={action} className="cinema-analysis-bullet-item">{action}</div>
              ))}
            </div>
          </div>

          {Array.isArray(analysis.recommendations?.summary) && analysis.recommendations.summary.length ? (
            <div className="cinema-analysis-interpretation-card">
              <div className="cinema-analysis-subsection-title">Strategic Summary</div>
              <div className="cinema-analysis-bullet-list">
                {analysis.recommendations.summary.map((note, index) => (
                  <div key={`${note}-${index}`} className="cinema-analysis-bullet-item">{note}</div>
                ))}
              </div>
            </div>
          ) : null}
        </AnalysisSection>

      </>
    );
  };

  const handleDashboardBack = (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate("/dashboard");
  };

  return (
    <div className="cinema-analysis-page reports-dashboard">
      <div className="reports-content cinema-analysis-content">
        <button
          type="button"
          className="cinema-analysis-poweredby-logo-button"
          onClick={handleDashboardBack}
          aria-label="Back to dashboard"
        >
          <PoweredByLogo
            alt="Powered by Logo"
            className="cinema-analysis-poweredby-logo poweredby-logo"
            draggable={false}
          />
        </button>

        <div className="cphere-governed-hero-selector-stack cinema-analysis-governed-hero-stack">
          <div className="cinema-analysis-hero-container">
            <div
              className="cinema-analysis-hero-pill"
              role="heading"
              aria-level="1"
              aria-label="Cinema Intelligence"
            >
              <span className="cinema-analysis-hero-pill-label cphere-hero-pill-wordmark">Cinema</span>
              <span className="cinema-analysis-hero-pill-tag cphere-hero-pill-tag">Intelligence</span>
            </div>
            <div className="cinema-analysis-hero-shimmer-bar" aria-hidden="true" />
          </div>

          <div className="cphere-governed-advertiser-selector-stage cinema-analysis-governed-advertiser-selector-stage">
            <AdvertiserIdentityTrigger
              advertiser={currentAdvertiser}
              advertiserName={currentAdvertiser?.name || ""}
              apiBase={API_BASE_URL}
              disabled={selectorDisabled}
              loading={loadingAdvertisers}
              empty={selectorEmpty}
              modalOpen={advertiserSelectorOpen}
              onClick={openAdvertiserSelector}
            />
          </div>
        </div>

        {advertiserSelectorOpen && visibleAdvertisers.length ? (
          <GovernedAdvertiserSelectorModal
            advertisers={visibleAdvertisers}
            selectedAdvertiserId={selectedAdvertiserId}
            selectedAdvertiserName={currentAdvertiser?.name || ""}
            apiBase={API_BASE_URL}
            onSelect={handleAdvertiserSelect}
            onClose={closeAdvertiserSelector}
          />
        ) : null}

        {/* Cinematic exhibitor rail: with a governed footprint, the campaign's
            real exhibitor mix wears the approved chain artwork — counts and
            shares from the canonical model. Without one, the ungated identity
            artwork band remains: imagery as identity, never fabricated data.
            Context chips carry honest loaded state in both modes. */}
        <section className="cinema-analysis-hero-band" aria-label="Theatrical footprint context">
          {cinemaFootprintAnalytics.hasFootprint ? (
            <div
              className="cinema-analysis-exhibitor-rail"
              role="list"
              aria-label="Campaign exhibitor footprint"
            >
              {cinemaExhibitorRailCards.map((card) => (
                <div
                  key={card.key}
                  role="listitem"
                  className="cinema-analysis-exhibitor-card"
                >
                  <div className="cinema-analysis-exhibitor-card-media" aria-hidden="true">
                    {/* A real exhibitor's own artwork always wins. Only chains
                        with no approved logo — Malco, UltraStar, the
                        Independent / Other grouping — fall back to the shared
                        cinema mark, which reads as a theater rather than as a
                        missing asset. */}
                    <img
                      src={card.image || cinemaFallbackMark}
                      alt=""
                      className={
                        card.image
                          ? "cinema-analysis-hero-band-image"
                          : "cinema-analysis-exhibitor-fallback-image"
                      }
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                  <div className="cinema-analysis-exhibitor-card-name">{card.label}</div>
                  <div className="cinema-analysis-exhibitor-card-count cinema-analysis-numeric">
                    {card.countLabel}
                  </div>
                  <div className="cinema-analysis-exhibitor-card-share cinema-analysis-numeric">
                    {card.shareLabel}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="cinema-analysis-hero-band-imagery"
              role="list"
              aria-label="Cinema exhibitor circuits"
            >
              {CINEMA_CHAIN_HERO_BUTTONS.map((button) => (
                <div key={button.key} role="listitem" className="cinema-analysis-hero-band-frame">
                  <img
                    src={button.image}
                    alt={`${button.label} cinema exhibitor artwork`}
                    className="cinema-analysis-hero-band-image"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="cinema-analysis-hero-band-chips">
            {cinemaHeroContextChips.map((chip) => (
              <span key={chip.key} className="cinema-analysis-hero-band-chip">
                {chip.label}
              </span>
            ))}
          </div>
          {renderCinemaReapprovalPanel()}
        </section>

        {/* Cinema Analytics workspace: the principal analytics surface. It
            adopts the canonical Platform Analytics perimeter (the shared
            --cphere-dashboard-tile-* tokens and 1500px desktop band) and every
            number inside derives from the one canonical footprint model. */}
        <section className="cinema-analytics-shell" aria-label="Cinema analytics workspace">
          <div className="cinema-analytics-shell-header">
            <div className="cinema-analysis-section-kicker">Governed Footprint Analytics</div>
            <h2 className="cinema-analytics-shell-title">Cinema Analytics Workspace</h2>
            <p className="cinema-analytics-shell-subtitle">{cinemaWorkspaceSubtitle}</p>
          </div>
          {renderCinemaCloudWorksUploadPanel()}
          <section
            className="cinema-analysis-overview-strip"
            role="group"
            aria-label="Cinema footprint readiness overview"
          >
            {cinemaOverviewCards.map((card) => (
              <div
                key={card.key}
                className={`cinema-analysis-overview-card${
                  card.isStatus ? " cinema-analysis-overview-card--status" : ""
                }`}
              >
                <span className="cinema-analysis-overview-label">{card.label}</span>
                <span className="cinema-analysis-overview-value cinema-analysis-numeric">
                  {card.value}
                </span>
                <span className="cinema-analysis-overview-detail">{card.detail}</span>
              </div>
            ))}
          </section>
          {renderCinemaGeographicFootprintSection()}
          {renderCinemaGeographicBreakdownSection()}
          {renderCinemaExhibitorConcentrationSection()}
          {renderCinemaMarketBreakdownSection()}
          {renderCinemaRosterSection()}
        </section>

        <div className="cinema-analysis-top-control-panel cinema-analysis-top-control-panel-single">
            <div className="cinema-analysis-top-control-card cinema-analysis-top-control-card-engine">
              <div className="cinema-analysis-top-control-header">
                <div className="cinema-analysis-top-control-title">Cinema Intelligence</div>
                <div className="cinema-analysis-top-control-pill cinema-analysis-top-control-pill-engine">
                  {cinemaEnginePresentation.logoSrc ? (
                    <div
                      className="cinema-analysis-engine-hero-frame cinema-analysis-engine-hero-frame-compact cinema-analysis-top-control-pill-logo-frame"
                      aria-hidden="true"
                    >
                      <div className="cinema-analysis-engine-hero-frame-inner">
                        <div className="cinema-analysis-engine-hero-media-well">
                          <img
                            src={cinemaEnginePresentation.logoSrc}
                            alt={cinemaEnginePresentation.logoAlt}
                            className="cinema-analysis-engine-hero-image cinema-analysis-engine-hero-image-compact"
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <span className="cinema-analysis-top-control-pill-engine-label">
                    {cinemaEnginePresentation.label}
                  </span>
                </div>
              </div>
              <div className="cinema-analysis-select-shell">
                <div className="cinema-analysis-top-control-pill cinema-analysis-top-control-pill-advertiser">
                  {cinemaRequiresReapproval ? "Last Approved Flight" : "Flight Window"} ·{" "}
                  {analysisWindowLabel}
                </div>
              </div>
              {cinemaRequiresReapproval ? (
                <div className="cinema-analysis-top-control-status" role="status">
                  {cinemaFlightWarning.replace(/_/g, " ")}.{" "}
                  {hasFlightDatePreset
                    ? "This immutable last-approved flight window is retained for Cinema analysis only and does not authorize deployment."
                    : "No immutable last-approved flight evidence is available; Cinema analysis remains unavailable and deployment remains blocked."}
                </div>
              ) : null}
              <button
                type="button"
                className="cinema-analysis-run-analysis-button"
                onClick={handleRunAnalysis}
                disabled={
                  !resolvedAdvertiserId ||
                  loadingAnalysis ||
                  waitingForDefaultDatePreset ||
                  !hasFlightDatePreset
                }
              >
                {loadingAnalysis ? "Running Analysis..." : "Run Analysis"}
              </button>
              {/* The external Looker report CTA is retired: this page's
                  native cinema intelligence is the reporting surface. The
                  advertiser's configured cinema report link is still resolved
                  (resolvedCinemaReportUrl) for governed page awareness, and
                  the mapping remains available to internal tooling. */}
              <div className="cinema-analysis-top-control-status">
                {analysisStatusMessage}
              </div>
            </div>

          </div>

        {/* The Market Analysis intelligence deck renders directly: the former
            Location Intelligence tab's entire capability (footprint upload,
            policy, radius governance, and the cinema list) now lives in the
            always-visible Cinema Analytics workspace above. */}
        <div className="cinema-analysis-surface-panel cinema-analysis-surface-panel-market">
          {renderMarketAnalysisSurface()}
        </div>
      </div>


      <div className="one-back-button" onClick={() => navigate(-1)}>
        <img src={reverseArrowImage} alt="Back" />
      </div>
    </div>
  );
};

export default CinemaAnalytics;
